(() => {

  // Prevent the same Add to cart payload being sent twice by overlapping
  // product-form submit handlers. Identical /cart/add requests made within a
  // short window share one network request, while each caller receives its own
  // cloned Response object.
  if (!window.__INTAPRINT_CART_ADD_DEDUPE__) {
    window.__INTAPRINT_CART_ADD_DEDUPE__ = true;

    const originalFetch = window.fetch.bind(window);
    const inFlightCartAdds = new Map();

    const cartAddSignature = (url, options = {}) => {
      const method = String(options.method || "GET").toUpperCase();
      const body = options.body;

      if (body instanceof FormData) {
        const entries = [];
        for (const [key, value] of body.entries()) {
          entries.push([
            key,
            value instanceof File
              ? `file:${value.name}:${value.size}:${value.lastModified}`
              : String(value)
          ]);
        }
        entries.sort((a, b) => {
          const keyCompare = a[0].localeCompare(b[0]);
          return keyCompare || a[1].localeCompare(b[1]);
        });
        return JSON.stringify([method, url, entries]);
      }

      if (typeof body === "string") {
        return JSON.stringify([method, url, body]);
      }

      return JSON.stringify([method, url, null]);
    };

    window.fetch = function intaprintDedupedFetch(resource, options = {}) {
      const url = String(resource?.url || resource || "");

      if (!/\/cart\/add(?:\.js)?(?:\?|$)/.test(url)) {
        return originalFetch(resource, options);
      }

      const signature = cartAddSignature(url, options);
      const existing = inFlightCartAdds.get(signature);

      if (existing) {
        console.warn("[Intaprint] Duplicate cart add blocked.");
        return existing.then((response) => response.clone());
      }

      const request = originalFetch(resource, options)
        .then((response) => response)
        .finally(() => {
          setTimeout(() => inFlightCartAdds.delete(signature), 1500);
        });

      inFlightCartAdds.set(signature, request);
      return request.then((response) => response.clone());
    };
  }

  function $(sel, root = document) { return root.querySelector(sel); }
  function $all(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function norm(s) { return (s || "").toString().trim().toLowerCase(); }

  function displayText(s) {
    return (s || "").toString();
  }

  function getSelectedPrintSidesValue(state) {
    const values = state?.values || {};
    const keys = Object.keys(values);

    const preferredKey = keys.find((key) => {
      const k = norm(key);
      return k === "print_sides" || k === "underside_print" || k.includes("print_side") || k.includes("printed_sides");
    });

    if (preferredKey) return values[preferredKey] || "";

    return Object.values(values).find((value) => {
      const txt = norm(value);
      return txt.includes("top side only") || txt.includes("printed on both sides");
    }) || "";
  }

  function isBothSidesSelected(state) {
    const txt = norm(getSelectedPrintSidesValue(state));
    return (
      txt.includes("printed on both sides") ||
      txt.includes("both sides") ||
      txt.includes("front and reverse") ||
      txt.includes("top side and underside") ||
      txt.includes("underside")
    );
  }

  function isTopOrBothSidesArtworkField(field, state = null) {
    const key = norm(field?.key);
    const label = norm(field?.label);
    const hasPrintSidesField = !!state?.values && Object.prototype.hasOwnProperty.call(state.values, "print_sides");

    return (
      field?.type === "file" &&
      hasPrintSidesField &&
      (
        key === "artwork" ||
        key.includes("artwork") ||
        label.includes("artwork") ||
        label.includes("image / artwork")
      )
    );
  }

  function isArtworkFileField(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);

    return (
      field?.type === "file" &&
      (
        key === "artwork" ||
        key.includes("artwork") ||
        label.includes("artwork") ||
        label.includes("image / artwork") ||
        label.includes("image")
      )
    );
  }

  function getArtworkModeValue(state) {
    const values = state?.values || {};
    return Object.values(values).find((value) => {
      const txt = norm(value);
      return (
        txt.includes("different design") ||
        txt.includes("different image") ||
        txt.includes("each side") ||
        txt.includes("2 unique") ||
        txt.includes("two unique") ||
        txt.includes("same design") ||
        txt.includes("same image")
      );
    }) || "";
  }

  // Clear only the primary artwork when the customer switches between
  // same/different-side artwork modes. Additional design attachments remain.
  function clearPrimaryArtworkOnModeChange(root, schema, state, previous, next) {
    const artworkMode = value => {
      const v = norm(value);
      if (v.includes("different design") || v.includes("different image") || v.includes("each side") || v.includes("2 unique") || v.includes("two unique")) return "different";
      if (v.includes("same design") || v.includes("same image")) return "same";
      if (v.includes("text only")) return "text";
      return "";
    };
    const from = artworkMode(previous);
    const to = artworkMode(next);
    if (!from || !to || from === to) return;

    const fields = Array.isArray(schema?.fields) ? schema.fields : [];
    const primary = fields.find(field =>
      field?.type === "file" &&
      !field.showAfterFileUpload &&
      isArtworkFileField(field) &&
      !norm(field.key).includes("additional") &&
      !norm(field.label).includes("additional")
    );
    if (!primary?.key) return;
    const oldIds = getStoredFileIds(state, primary.key);
    state.values[primary.key] = "";
    setStoredFileIds(state, primary.key, []);
    clearFilePreviews(state, primary.key);
    if (oldIds.length) {
      sendFileAction(root, "pending-delete", deletableFileIds(state, oldIds));
    }
    // The proof form must not remain open against the now-empty artwork.
    const proofForm = document.querySelector('[data-intaprint-proof-form-wrap]');
    if (proofForm) proofForm.hidden = true;
  }

  function getArtworkModeMaxFiles(field, state) {
    if (!isArtworkFileField(field)) return null;

    const txt = norm(getArtworkModeValue(state));
    if (!txt) return null;
    const configuredMax = Number(field?.maxFiles);

    if (
      txt.includes("different design") ||
      txt.includes("different image") ||
      txt.includes("each side") ||
      txt.includes("2 unique") ||
      txt.includes("two unique")
    ) {
      if (
        Number.isFinite(configuredMax) &&
        configuredMax > 2 &&
        (txt.includes("different design") || txt.includes("different image"))
      ) {
        return configuredMax;
      }
      return 2;
    }

    if (txt.includes("same design") || txt.includes("same image")) {
      return 1;
    }

    return null;
  }

  function shouldShowArtworkGuideLink(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);

    return (
      field?.type === "file" &&
      (
        key === "artwork" ||
        key.includes("artwork") ||
        label.includes("artwork") ||
        label.includes("image")
      )
    );
  }

  function getDynamicMaxFilesFromRules(field, state) {
    const mf = field?.minFilesWhen;
    if (!mf || typeof mf !== "object") return null;

    const arr = Array.isArray(mf.when) ? mf.when : [];
    for (const w of arr) {
      const current = w.fieldKey ? getFieldValue(w.fieldKey, state) : "";
      if (matchesRule(current, w)) {
        const c = Number(w.count);
        if (Number.isFinite(c) && c > 0) return c;
      }
    }

    return null;
  }

  function getEffectiveMaxFiles(field, state) {
    if (isTopOrBothSidesArtworkField(field, state)) {
      return isBothSidesSelected(state) ? 2 : 1;
    }

    // Two Inserts / artwork mode option sets:
    // "Same design" = 1 file, "Different design on each side" / "2 unique" = 2 files.
    // This must be checked before field.maxFiles, because some schemas keep maxFiles at 1
    // and rely on conditional rules/text for the 2-file artwork choice.
    const artworkModeMax = getArtworkModeMaxFiles(field, state);
    if (artworkModeMax) return artworkModeMax;

    const dynamicMax = getDynamicMaxFilesFromRules(field, state);
    if (dynamicMax) return dynamicMax;

    const n = Number(field?.maxFiles);
    return Number.isFinite(n) ? n : null;
  }

  function getEffectiveButtonText(field, state, fallbackText) {
    if (isTopOrBothSidesArtworkField(field, state)) {
      const selectedPrintSides = getSelectedPrintSidesValue(state);
      if (!selectedPrintSides) return "Choose file";
      return isBothSidesSelected(state)
        ? "Upload 1 or 2 completed artwork files"
        : "Upload 1 completed artwork file";
    }

    return fallbackText;
  }

  function ensureBaseStyles() {
    if (document.getElementById("ipo-style-core")) return;
    const st = document.createElement("style");
    st.id = "ipo-style-core";
    st.textContent = `
      .ipo-field { margin-top: var(--ipo-gap, 14px); }
      .ipo-label { display:block; font-weight:600; margin-bottom:8px; }
      .ipo-required { margin-left:4px; color: var(--ipo-required-color, #d11); }
      .ipo-field-intro { margin:0 0 10px; color:#16733a; font-weight:600; }
      .ipo-select, .ipo-input, .ipo-textarea { width:100%; }

      /* Swatch dropdown */
      .ipo-sd { position: relative; }
      .ipo-sd-btn {
        width:100%; display:flex; align-items:center; justify-content:space-between; gap:12px;
        padding: 12px 14px;
        border: 1px solid var(--ipo-border, rgba(0,0,0,0.12));
        border-radius: var(--ipo-radius, 10px);
        background: var(--ipo-field-bg, #fff);
        cursor: pointer;
        text-align:left;
      }
      .ipo-sd-btn:focus { outline: none; box-shadow: 0 0 0 2px rgba(11,58,102,0.15); border-color: var(--ipo-accent, #0b3a66); }
      .ipo-sd-left { display:flex; align-items:center; gap:10px; min-width:0; }
      .ipo-sd-chip {
        width: 26px; height: 26px;
        border-radius: 999px; /* round */
        border: 1px solid rgba(0,0,0,0.18);
        background: transparent;
        flex: 0 0 auto;
      }
      .ipo-sd-label { font-size: 14px; line-height: 1.2; white-space: nowrap; overflow:hidden; text-overflow: ellipsis; }
      .ipo-sd-caret { opacity: .7; flex: 0 0 auto; }

      .ipo-sd-panel {
        position:absolute; left:0; right:0; top: calc(100% + 6px);
        background: var(--ipo-field-bg, #fff);
        border: 1px solid var(--ipo-border, rgba(0,0,0,0.12));
        border-radius: var(--ipo-radius, 10px);
        box-shadow: 0 12px 40px rgba(0,0,0,0.10);
        z-index: 50;
        overflow:hidden;
      }
      .ipo-sd-panel[hidden] { display:none; }
      .ipo-sd-search {
        width:100%; padding: 10px 12px;
        border: none; border-bottom: 1px solid rgba(0,0,0,0.08);
        outline: none;
        background: transparent;
      }
      .ipo-sd-list { max-height: 260px; overflow:auto; padding: 6px; }
      .ipo-sd-opt {
        width:100%;
        display:flex; align-items:center; gap:10px;
        padding: 10px 10px;
        border-radius: 10px;
        cursor:pointer;
      }
      .ipo-sd-opt:hover { background: rgba(0,0,0,0.05); }
      .ipo-sd-opt[aria-selected="true"] { background: rgba(11,58,102,0.10); }
      .ipo-sd-opt-name { font-size: 14px; }
      .ipo-sd-empty { padding: 10px; font-size: 13px; opacity:.7; }

      /* Ad-Loop live preview */
      .ipo-adloop-preview {
        margin: 14px 0 18px;
        border: 1px solid rgba(0,32,70,0.16);
        border-radius: var(--ipo-radius, 10px);
        background: #f7fbfd;
        overflow: hidden;
      }
      .ipo-adloop-preview-head {
        padding: 12px 14px;
        background: #E1EDF5;
        color: #002046;
        font-weight: 700;
      }
      .ipo-adloop-preview-stage { padding: 8px 12px 2px; }
      .ipo-adloop-preview-stage svg { display:block; width:100%; height:auto; }
      .ipo-adloop-preview-selection {
        display:flex;
        flex-wrap:wrap;
        gap:7px;
        padding: 0 14px 10px;
      }
      .ipo-adloop-preview-selection-item {
        display:inline-flex;
        align-items:center;
        gap:6px;
        padding:5px 8px;
        border:1px solid rgba(0,32,70,.12);
        border-radius:999px;
        background:#fff;
        font-size:11px;
        line-height:1.2;
      }
      .ipo-adloop-preview-selection-dot {
        width:12px;
        height:12px;
        flex:0 0 12px;
        border:1px solid rgba(0,0,0,.24);
        border-radius:50%;
      }
      .ipo-adloop-preview-note {
        padding: 0 14px 12px;
        color: rgba(17,17,17,.72);
        font-size: 12px;
        line-height: 1.45;
      }
      .ipo-adloop-help {
        margin: 0 0 14px;
        padding: 12px 14px;
        border-left: 4px solid #002046;
        background: rgba(225,237,245,.58);
        font-size: 13px;
        line-height: 1.5;
      }
      .ipo-adloop-help strong { color:#002046; }

      .ipo-pantone-controls {
        display:grid;
        grid-template-columns:minmax(0,1fr) auto;
        gap:12px;
        align-items:end;
        margin-top:12px;
        padding:12px;
        border:1px solid rgba(0,32,70,.14);
        border-radius:var(--ipo-radius, 10px);
        background:rgba(225,237,245,.45);
      }
      .ipo-pantone-control-label {
        display:block;
        margin-bottom:6px;
        color:#002046;
        font-size:12px;
        font-weight:700;
      }
      .ipo-pantone-reference { min-width:0; }
      .ipo-pantone-preview-row { display:flex; align-items:center; gap:8px; }
      .ipo-pantone-colour {
        width:52px;
        height:44px;
        padding:3px;
        border:1px solid var(--ipo-border, rgba(0,0,0,.16));
        border-radius:8px;
        background:#fff;
        cursor:pointer;
      }
      .ipo-pantone-hex { min-width:66px; font-size:12px; color:rgba(17,17,17,.72); }
      .ipo-pantone-note {
        grid-column:1 / -1;
        margin:0;
        font-size:12px;
        line-height:1.4;
        color:rgba(17,17,17,.72);
      }
      .ipo-pantone-note[data-match="yes"] { color:#16733a; font-weight:600; }
      .ipo-pantone-note[data-match="no"] { color:#a13b18; }
      @media (max-width:540px) {
        .ipo-pantone-controls { grid-template-columns:1fr; }
        .ipo-pantone-note { grid-column:1; }
      }

      .ipo-file-row { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
      .ipo-file-btn { padding:10px 14px; border-radius: var(--ipo-radius, 10px); border:1px solid var(--ipo-border, rgba(0,0,0,0.12));
        background: var(--ipo-btn-bg, #0b3a66); color: var(--ipo-btn-text, #fff); cursor:pointer;
      }
      .ipo-file-name { font-size: 13px; }
      .ipo-errors { margin-top:12px; padding:10px 12px; border-radius: 10px; background: rgba(220, 38, 38, 0.08); border:1px solid rgba(220,38,38,.25); }

      .ipo-saved-artwork-notice {
        padding: 12px 14px;
        margin-bottom: 14px;
        border: 1px solid #9ac6a3;
        border-radius: 8px;
        background: #eef8f0;
        font-weight: 600;
      }

      .ipo-exact-repeat-summary {
        margin-top: 14px;
        padding: 14px;
        border: 1px solid rgba(0,32,70,0.16);
        border-radius: var(--ipo-radius, 10px);
        background: #f5f8fb;
      }
      .ipo-exact-repeat-summary-title {
        margin: 0 0 8px;
        font-weight: 700;
        color: #002046;
      }
      .ipo-exact-repeat-summary-text {
        margin: 0 0 10px;
        font-size: 13px;
        line-height: 1.45;
      }
      .ipo-exact-repeat-summary-list {
        margin: 0;
        padding-left: 20px;
        font-size: 13px;
        line-height: 1.5;
      }
      .ipo-exact-repeat-summary-note {
        margin-top: 10px;
        font-size: 12px;
        line-height: 1.45;
        color: rgba(17,17,17,0.72);
      }

      .ipo-artwork-guide {
        margin: 8px 0 10px;
        font-size: 13px;
        line-height: 1.35;
      }
      .ipo-artwork-guide a {
        color: var(--ipo-accent, #0b3a66);
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      /* Tiles (e.g., fonts) */
      .ipo-tiles {
        display: grid;
        gap: 10px;
        /* Desktop: 9 columns so 26 fonts become 9 / 9 / 8 */
        grid-template-columns: repeat(9, 1fr);
      }
      @media (max-width: 990px) { .ipo-tiles { grid-template-columns: repeat(6, 1fr); } }
      @media (max-width: 720px) { .ipo-tiles { grid-template-columns: repeat(4, 1fr); } }
      @media (max-width: 540px) {
        .ipo-adloop-preview-stage { padding-left: 4px; padding-right: 4px; }
      }

      .ipo-tile {
        display:flex; align-items:center; justify-content:center;
        border: 1px solid var(--ipo-border, rgba(0,0,0,0.12));
        border-radius: var(--ipo-radius, 10px);
        background: var(--ipo-field-bg, #fff);
        padding: 10px;
        cursor: pointer;
        min-height: 62px;
        user-select:none;
      }
      .ipo-tile:hover { background: rgba(0,0,0,0.03); }
      .ipo-tile[aria-selected="true"] {
        border-color: var(--ipo-accent, #0b3a66);
        box-shadow: 0 0 0 2px rgba(11,58,102,0.12);
      }
      .ipo-tile-img { width: 100%; max-width: 140px; max-height: 54px; object-fit: contain; display:block; }
      .ipo-tile-text { font-size: 12px; font-weight:600; text-align:center; padding: 2px 4px; }

      .ipo-upload-tick--uploading {
        background: transparent;
        border: none;
      }
      .ipo-upload-spinner {
        width: 14px;
        height: 14px;
        display: inline-block;
        border: 2px solid rgba(255,255,255,0.35);
        border-top-color: #ffffff;
        border-radius: 999px;
        animation: ipo-upload-spin .75s linear infinite;
      }

      .ipo-express-date-field {
        padding: 12px;
        border: 1px solid var(--ipo-border, rgba(0,0,0,0.12));
        border-radius: var(--ipo-radius, 10px);
        background: rgba(225, 237, 245, 0.45);
      }
      .ipo-express-date-field .ipo-help {
        margin-top: 8px;
      }
      .ipo-express-status {
        margin: 0 0 12px;
        padding: 10px 12px;
        border: 1px solid rgba(0,32,70,0.14);
        border-radius: var(--ipo-radius, 10px);
        background: #E1EDF5;
        color: var(--ipo-text, #111);
        font-size: 13px;
        line-height: 1.4;
      }
      .ipo-express-status strong {
        font-weight: 700;
      }
      .ipo-express-date-note {
        margin-top: 8px;
        font-size: 12px;
        opacity: .8;
      }
      .ipo-express-date-input--earliest {
        border-color: var(--ipo-btn-bg, #002046);
        box-shadow: 0 0 0 2px rgba(0,32,70,0.10);
      }
      .ipo-edd-panel {
        margin-top: var(--ipo-gap, 14px);
        padding: 14px;
        border: 1px solid rgba(0,32,70,0.14);
        border-radius: var(--ipo-radius, 10px);
        background: #E1EDF5;
      }
      .ipo-edd-title {
        display:flex;
        align-items:center;
        gap:8px;
        margin:0 0 10px;
        font-weight:700;
        font-size:15px;
        flex-wrap:wrap;
      }
      .ipo-edd-countdown {
        display:flex;
        align-items:center;
        gap:5px;
        color:#002046;
        font-weight:800;
        margin-top:7px;
        padding-bottom:14px;
        border-bottom:1px solid rgba(0,32,70,0.18);
        font-size:13px;
        line-height:1.4;
      }
      .ipo-edd-countdown--urgent {
        color:#b42318;
      }
      .ipo-edd-row {
        display:flex;
        align-items:flex-start;
        gap:10px;
        margin-top:8px;
        font-size:13px;
        line-height:1.4;
      }
      .ipo-edd-icon { flex:0 0 auto; }
      .ipo-edd-highlight { font-weight:700; color:#002046; }
      .ipo-edd-standard-note {
        margin-top:10px;
        font-size:12px;
        line-height:1.45;
        color:rgba(17,17,17,0.72);
      }
      .ipo-edd-loading { opacity:.7; }
      @keyframes ipo-upload-spin {
        to { transform: rotate(360deg); }
      }
`;
    document.head.appendChild(st);
  }

  function getSelectedVariantId() {
    const idInput =
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector("product-form form input[name='id']") ||
      document.querySelector("input[name='id']");
    if (idInput && idInput.value) return String(idInput.value);

    const urlVar = new URLSearchParams(location.search).get("variant");
    return urlVar ? String(urlVar) : "";
  }

  function parseVariantStockMap() {
    const el = document.getElementById("IntaprintVariantStockMap");
    if (!el) return {};

    try {
      return JSON.parse((el.textContent || "{}").trim()) || {};
    } catch (e) {
      console.warn("Intaprint: unable to parse variant stock map.", e);
      return {};
    }
  }

  function getVariantStockState(variantId) {
    if (!variantId) return null;

    const map = parseVariantStockMap();
    const row = map?.[String(variantId)];
    if (!row || typeof row !== "object") return null;

    return {
      inventoryQuantity: Number(row.inventory_quantity ?? 0),
      inventoryPolicy: norm(row.inventory_policy || ""),
      available: row.available === true || String(row.available).toLowerCase() === "true"
    };
  }

  function isVariantTrulySoldOut(variantId) {
    const stock = getVariantStockState(variantId);
    if (!stock) return false;

    // Continue-selling variants are genuine pre-orders, so Estimated Dispatch
    // should remain visible even when their physical inventory is zero.
    if (stock.inventoryPolicy === "continue") return false;

    // Shopify's available flag safely handles untracked inventory too.
    return stock.available === false && stock.inventoryQuantity <= 0;
  }

  function isSampleOrderFieldDef(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);
    return (
      key === "sample_order" ||
      key === "sample" ||
      (field?.type === "select" && label.includes("sample"))
    );
  }

  function isPrintsOnlyAssemblyChoice(value) {
    const txt = norm(value).replace(/[–—]/g, "-");
    return txt === "prints only" || txt.includes("prints only") || txt.includes("no blank products supplied");
  }

  function parseSampleAvailableMap() {
    const el = document.querySelector("[data-intaprint-sample-available-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function parsePrintsOnlyAvailableMap() {
    const el = document.querySelector("[data-intaprint-prints-only-available-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function isPrintsOnlyAvailableForSelectedVariant() {
    const variantId = getSelectedVariantId();
    const map = parsePrintsOnlyAvailableMap();

    if (variantId && Object.prototype.hasOwnProperty.call(map, String(variantId))) {
      return truthyMetafieldValue(map[String(variantId)]);
    }

    return false;
  }

  function isAssemblyFieldDef(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);
    return key === "assembly" || label === "assembly";
  }

  function choiceValue(choice) {
    return typeof choice === "string"
      ? choice
      : (choice?.value ?? choice?.label ?? "").toString();
  }

  function choiceRuleMatches(rule, state, variantOptionsPayload, variantId) {
    const when = rule?.when || {};

    if (when.fieldKey) {
      const current = getFieldValue(String(when.fieldKey), state);
      if (!current) return false;

      const excluded = Array.isArray(when.notEquals)
        ? when.notEquals
        : (when.notEquals != null ? [when.notEquals] : []);
      if (excluded.length) {
        return !excluded.some((value) => norm(value) === norm(current));
      }

      return matchesRule(current, when);
    }

    const needles = Array.isArray(when.anyVariantOptionIncludes)
      ? when.anyVariantOptionIncludes
      : [];
    if (needles.length) {
      const optionValues = getVariantOptionValues(variantOptionsPayload, variantId);
      return optionValues.some((value) =>
        needles.some((needle) => norm(value).includes(norm(needle)))
      );
    }

    return false;
  }

  function getConfiguredChoices(field, state = null, schema = null) {
    let choices = Array.isArray(field?.choices) ? field.choices : null;

    if (!choices) {
      const sourceKey = String(field?.choicesFrom || "").trim();
      const sourceSchema = schema || state?._schema;
      if (!sourceKey || !sourceSchema) return [];

      const sourceField = (sourceSchema.fields || []).find((item) => item?.key === sourceKey);
      choices = Array.isArray(sourceField?.choices) ? sourceField.choices : [];
    }

    const alphabetisedColourFields = new Set([
      "underside_colour",
      "endcap_colour",
      "ribbon_colour"
    ]);

    if (field?.customPantone || alphabetisedColourFields.has(field?.key)) {
      const customPantoneChoice = field?.customPantone
        ? choices.find(
            (choice) => norm(choiceValue(choice)) === "custom pantone colour"
          ) || {
          label: "Custom Pantone colour",
          value: "Custom Pantone colour",
          swatch: "linear-gradient(135deg,#e11d48 0 25%,#facc15 25% 50%,#4ade80 50% 75%,#1d4ed8 75%)"
          }
        : null;

      const standardChoices = choices
        .filter((choice) => norm(choiceValue(choice)) !== "custom pantone colour")
        .slice()
        .sort((a, b) => {
          const labelA = String(
            typeof a === "object" && a ? (a.label ?? a.value ?? "") : (a ?? "")
          );
          const labelB = String(
            typeof b === "object" && b ? (b.label ?? b.value ?? "") : (b ?? "")
          );
          return labelA.localeCompare(labelB, "en", { sensitivity: "base" });
        });

      return customPantoneChoice
        ? [customPantoneChoice, ...standardChoices]
        : standardChoices;
    }

    return choices;
  }

  function isCustomPantoneSelection(field, state) {
    return !!field?.customPantone &&
      norm(state?.values?.[field.key]) === "custom pantone colour";
  }

  function customPantoneReferenceKey(field) {
    return `${field.key}_pantone_reference`;
  }

  function customPantonePreviewKey(field) {
    return `${field.key}_pantone_preview`;
  }

  function customPantoneSlotLabel(field) {
    return String(field?.customPantone?.slotLabel || field?.label || "Print colour").trim();
  }

  function customPantonePreviewColour(field, state) {
    const value = String(state?.values?.[customPantonePreviewKey(field)] || "").trim();
    return /^#[0-9a-f]{6}$/i.test(value) ? value : "#6b7280";
  }

  /*
   * Approximate screen equivalents from Pantoner 1.1.2 (MIT).
   * Copyright (c) 2014 James Pederson.
   * Permission is hereby granted, free of charge, to any person obtaining a copy
   * of this software and associated documentation files (the "Software"), to deal
   * in the Software without restriction, including without limitation the rights
   * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
   * copies of the Software, and to permit persons to whom the Software is
   * furnished to do so, subject to the following conditions:
   *
   * The above copyright notice and this permission notice shall be included in
   * all copies or substantial portions of the Software.
   *
   * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
   * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
   * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
   * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
   * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
   * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
   * THE SOFTWARE.
   */
  const ADLOOP_PANTONE_TO_HEX = Object.freeze({"100-c":"#f6eb61","101-c":"#f7ea48","102-c":"#fce300","103-c":"#c5a900","104-c":"#af9800","105-c":"#897a27","7401-c":"#f5e1a4","7402-c":"#ecd898","7403-c":"#eed484","7404-c":"#f4da40","7405-c":"#f2cd00","7406-c":"#f1c400","7407-c":"#cba052","106-c":"#f9e547","107-c":"#fbe122","108-c":"#fedb00","109-c":"#ffd100","110-c":"#daaa00","111-c":"#aa8a00","112-c":"#9c8412","113-c":"#fae053","114-c":"#fbdd40","115-c":"#fdda24","116-c":"#ffcd00","117-c":"#c99700","118-c":"#ac8400","119-c":"#897322","127-c":"#f3dd6d","128-c":"#f3d54e","129-c":"#f3d03e","130-c":"#f2a900","131-c":"#cc8a00","132-c":"#a07400","133-c":"#6c571b","1205-c":"#f8e08e","1215-c":"#fbd872","1225-c":"#ffc845","1235-c":"#ffb81c","1245-c":"#c69214","1255-c":"#ad841f","1265-c":"#886b25","120-c":"#fbdb65","121-c":"#fdd757","122-c":"#fed141","123-c":"#ffc72c","124-c":"#eaaa00","125-c":"#b58500","126-c":"#9a7611","7548-c":"#ffc600","7549-c":"#ffb500","7550-c":"#d19000","7551-c":"#b47e00","7552-c":"#73531d","7553-c":"#5a4522","7554-c":"#4b3d2a","7555-c":"#d29f13","7556-c":"#b78b20","7557-c":"#9f7d23","7558-c":"#967126","7559-c":"#8f6a2a","7560-c":"#7d622e","7561-c":"#6c5d34","134-c":"#fdd26e","135-c":"#ffc658","136-c":"#ffbf3f","137-c":"#ffa300","138-c":"#de7c00","139-c":"#af6d04","140-c":"#74531c","1345-c":"#fdd086","1355-c":"#ffc56e","1365-c":"#ffb549","1375-c":"#ff9e1b","1385-c":"#d57800","1395-c":"#996017","1405-c":"#6e4c1e","141-c":"#f2c75c","142-c":"#f1be48","143-c":"#f1b434","144-c":"#ed8b00","145-c":"#cf7f00","146-c":"#a76d11","147-c":"#715c2a","7408-c":"#f6be00","7409-c":"#f0b323","7410-c":"#fead77","7411-c":"#e6a65d","7412-c":"#d38235","7413-c":"#dc8633","7414-c":"#c16c18","7562-c":"#bd9b60","7563-c":"#d69a2d","7564-c":"#db8a06","7565-c":"#cd7925","7566-c":"#ad6433","7567-c":"#89532f","7568-c":"#775135","7569-c":"#d78825","7570-c":"#d3832b","7571-c":"#c67d30","7572-c":"#b67233","7573-c":"#a7662b","7574-c":"#9e6a38","7575-c":"#835d32","712-c":"#fcc89b","713-c":"#fdbe87","714-c":"#fdaa63","715-c":"#f68d2e","716-c":"#ea7600","717-c":"#d45d00","718-c":"#be4d00","148-c":"#fecb8b","149-c":"#ffc27b","150-c":"#ffb25b","151-c":"#ff8200","152-c":"#e57200","153-c":"#be6a14","154-c":"#9b5a1a","155-c":"#efd19f","156-c":"#efbe7d","157-c":"#eca154","158-c":"#e87722","159-c":"#cb6015","160-c":"#a1561c","161-c":"#603d20","1485-c":"#ffae62","1495-c":"#ff8f1c","1505-c":"#ff6900","1525-c":"#b94700","1535-c":"#94450b","1545-c":"#653819","1555-c":"#ffb990","1565-c":"#ffa06a","1575-c":"#ff7f32","1585-c":"#ff6a13","1595-c":"#d86018","1605-c":"#a65523","1615-c":"#8b4720","162-c":"#ffbe9f","163-c":"#ff9d6e","164-c":"#ff7f41","165-c":"#ff671f","166-c":"#e35205","167-c":"#be531c","168-c":"#73381d","7576-c":"#db864e","7577-c":"#e07e3c","7578-c":"#dc6b2f","7579-c":"#dc582a","7580-c":"#c05131","7581-c":"#864a33","7582-c":"#674736","1625-c":"#ffa38b","1635-c":"#ff8d6d","1645-c":"#ff6a39","1655-c":"#fc4c02","1665-c":"#dc4405","1675-c":"#a9431e","1685-c":"#833921","169-c":"#ffb3ab","170-c":"#ff8674","171-c":"#ff5c39","172-c":"#fa4616","173-c":"#cf4520","174-c":"#963821","175-c":"#6b3529","7583-c":"#c4622d","7584-c":"#ba5826","7585-c":"#af5c37","7586-c":"#9e5330","7587-c":"#924c2e","7588-c":"#7b4d35","7589-c":"#5c4738","7590-c":"#d4b59e","7591-c":"#c07d59","7592-c":"#b15533","7593-c":"#9d432c","7594-c":"#7c3a2d","7595-c":"#6b3d2e","7596-c":"#5c3d31","7597-c":"#d14124","7598-c":"#bd472a","7599-c":"#b33d26","7600-c":"#8d3f2b","7601-c":"#83412c","7602-c":"#7b4931","7603-c":"#674230","7604-c":"#e4d5d3","7605-c":"#e1bbb4","7606-c":"#d6938a","7607-c":"#c26e60","7608-c":"#a4493d","7609-c":"#823b34","7610-c":"#683431","7611-c":"#ddbcb0","7612-c":"#ca9a8e","7613-c":"#bc8a7e","7614-c":"#a37f74","7615-c":"#866761","7616-c":"#6b4c4c","7617-c":"#583d3e","7520-c":"#eabeb0","7521-c":"#c09c83","7522-c":"#b46a55","7523-c":"#ab5c57","7524-c":"#a45248","7525-c":"#9a6a4f","7526-c":"#8a391b","489-c":"#ecc3b2","488-c":"#ecbaa8","487-c":"#eaa794","486-c":"#e8927c","485-c":"#da291c","484-c":"#9a3324","483-c":"#653024","176-c":"#ffb1bb","177-c":"#ff808b","178-c":"#ff585d","179-c":"#e03c31","180-c":"#be3a34","181-c":"#81312f","1765-c":"#ffa3b5","1775-c":"#ff8da1","1785-c":"#f8485e","1788-c":"#ee2737","1795-c":"#d22630","1805-c":"#af272f","1815-c":"#7c2529","1767-c":"#fcafc0","1777-c":"#fb637e","1787-c":"#f4364c","1797-c":"#cb333b","1807-c":"#a4343a","1817-c":"#643335","7618-c":"#c66e4e","7619-c":"#c04c36","7620-c":"#b7312c","7621-c":"#ab2328","7622-c":"#93272c","7623-c":"#8a2a2b","7624-c":"#802f2d","7625-c":"#e1523d","7626-c":"#c63527","7627-c":"#a72b2a","7628-c":"#9e2a2b","7629-c":"#6d3332","7630-c":"#633231","7631-c":"#572d2d","7415-c":"#e6baa8","7416-c":"#e56a54","7417-c":"#e04e39","7418-c":"#cd545b","7419-c":"#b04a5a","7420-c":"#9b2242","7421-c":"#651d32","182-c":"#fabbcb","183-c":"#fc9bb3","184-c":"#f65275","185-c":"#e4002b","186-c":"#c8102e","187-c":"#a6192e","188-c":"#76232f","196-c":"#ecc7cd","197-c":"#e89cae","198-c":"#df4661","199-c":"#d50032","200-c":"#ba0c2f","201-c":"#9d2235","202-c":"#862633","189-c":"#f8a3bc","190-c":"#f67599","191-c":"#ef426f","192-c":"#e40046","193-c":"#bf0d3e","194-c":"#9b2743","195-c":"#782f40","1895-c":"#f5b6cd","1905-c":"#f59bbb","1915-c":"#ef4a81","1925-c":"#e0004d","1935-c":"#c5003e","1945-c":"#a6093d","1955-c":"#8a1538","705-c":"#f5dadf","706-c":"#f7ced7","707-c":"#f9b5c4","708-c":"#f890a5","709-c":"#ef6079","710-c":"#e03e52","711-c":"#cb2c30","698-c":"#f2d4d7","699-c":"#f4c3cc","700-c":"#f2acb9","701-c":"#e68699","702-c":"#d25b73","703-c":"#b83a4b","704-c":"#9e2a2f","203-c":"#ecb3cb","204-c":"#e782a9","205-c":"#e0457b","206-c":"#ce0037","207-c":"#a50034","208-c":"#861f41","209-c":"#6f263d","210-c":"#f99fc9","211-c":"#f57eb6","212-c":"#f04e98","213-c":"#e31c79","214-c":"#ce0f69","215-c":"#ac145a","216-c":"#7d2248","7422-c":"#f4cdd4","7423-c":"#e06287","7424-c":"#e24585","7425-c":"#b52555","7426-c":"#a4123f","7427-c":"#971b2f","7428-c":"#6a2c3e","7632-c":"#d6c9ca","7633-c":"#c4a4a7","7634-c":"#c16784","7635-c":"#c63663","7636-c":"#bc204b","7637-c":"#912f46","7638-c":"#7e2d40","217-c":"#eabedb","218-c":"#e56db1","219-c":"#da1884","220-c":"#a50050","221-c":"#910048","222-c":"#6c1d45","7639-c":"#936d73","7640-c":"#934054","7641-c":"#8e2c48","7642-c":"#732e4a","7643-c":"#672e45","7644-c":"#582d40","7645-c":"#502b3a","223-c":"#ef95cf","224-c":"#eb6fbd","225-c":"#df1995","226-c":"#d0006f","227-c":"#aa0061","228-c":"#890c58","229-c":"#672146","230-c":"#f4a6d7","231-c":"#f277c6","232-c":"#e93cac","233-c":"#c6007e","234-c":"#a20067","235-c":"#840b55","670-c":"#ead3e2","671-c":"#e6bcd8","672-c":"#dfa0c9","673-c":"#d986ba","674-c":"#c6579a","675-c":"#ae2573","676-c":"#960051","677-c":"#e5cedb","678-c":"#e3c8d8","679-c":"#debed2","680-c":"#c996b6","681-c":"#b06c96","682-c":"#994878","683-c":"#7c2855","684-c":"#e4c6d4","685-c":"#dcb6c9","686-c":"#d0a1ba","687-c":"#be84a3","688-c":"#a76389","689-c":"#893b67","690-c":"#612141","510-c":"#ebbecb","509-c":"#e8b3c3","508-c":"#e4a9bb","507-c":"#d592aa","506-c":"#84344e","505-c":"#6f2c3f","504-c":"#572932","7429-c":"#e2bccb","7430-c":"#dca9bf","7431-c":"#c9809e","7432-c":"#b55c80","7433-c":"#a73a64","7434-c":"#9b3259","7435-c":"#872651","691-c":"#e9cdd0","692-c":"#e4bec3","693-c":"#d7a3ab","694-c":"#c48490","695-c":"#b46b7a","696-c":"#984856","697-c":"#893c47","496-c":"#f2c6cf","495-c":"#f1bdc8","494-c":"#e9a2b2","493-c":"#dc8699","492-c":"#8f3237","491-c":"#7f3035","490-c":"#5d2a2c","503-c":"#e9c4c7","502-c":"#e5bac1","501-c":"#daa5ad","500-c":"#c6858f","499-c":"#7a3e3a","498-c":"#6a3735","497-c":"#512f2e","5035-c":"#dfc2c3","5025-c":"#dbb7bb","5015-c":"#cca1a6","5005-c":"#b07c83","4995-c":"#9c6169","4985-c":"#874b52","4975-c":"#3f2021","236-c":"#f1a7dc","237-c":"#ec86d0","238-c":"#e45dbf","239-c":"#db3eb1","240-c":"#c5299b","241-c":"#af1685","242-c":"#80225f","2365-c":"#efbae1","2375-c":"#e277cd","2385-c":"#d539b5","2395-c":"#c800a1","2405-c":"#b0008e","2415-c":"#9e007e","2425-c":"#830065","243-c":"#eab8e4","244-c":"#e59bdc","245-c":"#dd7fd3","246-c":"#c724b1","247-c":"#bb16a3","248-c":"#a51890","249-c":"#80276c","7646-c":"#a56e87","7647-c":"#a83d72","7648-c":"#991e66","7649-c":"#8a1b61","7650-c":"#722257","7651-c":"#6a2a5b","7652-c":"#5e2751","250-c":"#e7bae4","251-c":"#dd9cdf","252-c":"#c964cf","253-c":"#ad1aac","254-c":"#981d97","255-c":"#72246c","517-c":"#ebc6df","516-c":"#e6bedd","515-c":"#e2acd7","514-c":"#d48bc8","513-c":"#93328e","512-c":"#833177","511-c":"#612c51","7436-c":"#eedaea","7437-c":"#ccaed0","7438-c":"#d59ed7","7439-c":"#b288b9","7440-c":"#a277a6","7441-c":"#9f5cc0","7442-c":"#963cbd","2562-c":"#d7a9e3","2572-c":"#c98bdb","2582-c":"#ac4fc6","2592-c":"#9b26b6","2602-c":"#87189d","2612-c":"#772583","2622-c":"#653165","7653-c":"#948794","7654-c":"#a2789c","7655-c":"#a15a95","7656-c":"#8e3a80","7657-c":"#6e2b62","7658-c":"#6a3460","7659-c":"#5d3754","524-c":"#d5c2d8","523-c":"#c9b1d0","522-c":"#ba9cc5","521-c":"#a57fb2","520-c":"#642f6c","519-c":"#59315f","518-c":"#4b3048","5245-c":"#dbcdd3","5235-c":"#d0bec7","5225-c":"#c6b0bc","5215-c":"#af95a6","5205-c":"#86647a","5195-c":"#66435a","5185-c":"#4a3041","5175-c":"#d8c8d1","5165-c":"#d3c0cd","5155-c":"#bfa5b8","5145-c":"#9b7793","5135-c":"#7e5475","5125-c":"#693c5e","5115-c":"#512a44","531-c":"#dfc8e7","530-c":"#d7b9e4","529-c":"#caa2dd","528-c":"#b580d1","527-c":"#8031a7","526-c":"#702f8a","525-c":"#572c5f","256-c":"#d6bfdd","257-c":"#c6a1cf","258-c":"#8c4799","259-c":"#6d2077","260-c":"#642667","261-c":"#5d285f","262-c":"#51284f","2563-c":"#cba3d8","2573-c":"#b884cb","2583-c":"#a05eb5","2593-c":"#84329b","2603-c":"#702082","2613-c":"#671e75","2623-c":"#5f2167","7660-c":"#9991a4","7661-c":"#8d6e97","7662-c":"#7a4183","7663-c":"#6b3077","7664-c":"#653279","7665-c":"#5e366e","7666-c":"#5c4e63","2567-c":"#c1a0da","2577-c":"#a77bca","2587-c":"#8246af","2597-c":"#5c068c","2607-c":"#500778","2617-c":"#470a68","2627-c":"#3c1053","263-c":"#d7c6e6","264-c":"#c1a7e2","265-c":"#9063cd","266-c":"#753bbd","267-c":"#5f259f","268-c":"#582c83","269-c":"#512d6d","2635-c":"#c5b4e3","2645-c":"#ad96dc","2655-c":"#9678d3","2665-c":"#7d55c7","2685-c":"#330072","2695-c":"#2e1a47","270-c":"#b4b5df","271-c":"#9595d2","272-c":"#7474c1","273-c":"#24135f","274-c":"#211551","275-c":"#201747","276-c":"#221c35","2705-c":"#a7a4e0","2715-c":"#8b84d7","2725-c":"#685bc7","2735-c":"#2e008b","2745-c":"#280071","2755-c":"#250e62","2765-c":"#201547","7667-c":"#6e7ca0","7668-c":"#686e9f","7669-c":"#615e9b","7670-c":"#565294","7671-c":"#514689","7672-c":"#4c4184","7673-c":"#535486","7443-c":"#dddae8","7444-c":"#b6b8dc","7445-c":"#a7a2c3","7446-c":"#8986ca","7447-c":"#5d4777","7448-c":"#4b384c","7449-c":"#41273b","7674-c":"#878cb4","7675-c":"#7c7fab","7676-c":"#7566a0","7677-c":"#6f5091","7678-c":"#68478d","7679-c":"#563d82","7680-c":"#523178","663-c":"#e5e1e6","664-c":"#e0dbe3","665-c":"#c6bcd0","666-c":"#a192b2","667-c":"#7c6992","668-c":"#614b79","669-c":"#3f2a56","5315-c":"#d8d7df","5305-c":"#c6c4d2","5295-c":"#b3b0c4","5285-c":"#8d89a5","5275-c":"#595478","5265-c":"#403a60","5255-c":"#1e1a34","538-c":"#c5cfda","537-c":"#bbc7d6","536-c":"#a2b2c8","535-c":"#8e9fbc","534-c":"#1b365d","533-c":"#1f2a44","532-c":"#1c1f2a","7541-c":"#d9e1e2","7542-c":"#a4bcc2","7543-c":"#98a4ae","7544-c":"#768692","7545-c":"#425563","7546-c":"#253746","7547-c":"#131e29","552-c":"#b9d3dc","551-c":"#a3c7d2","550-c":"#8db9ca","549-c":"#6ba4b8","548-c":"#003d4c","547-c":"#00313c","546-c":"#072b31","5455-c":"#bfced6","5445-c":"#b7c9d3","5435-c":"#a6bbc8","5425-c":"#7a99ac","5415-c":"#5b7f95","5405-c":"#4f758b","5395-c":"#081f2c","642-c":"#d1dde6","643-c":"#c6d6e3","644-c":"#9bb8d3","645-c":"#7da1c4","646-c":"#5e8ab4","647-c":"#236192","648-c":"#002e5d","649-c":"#dbe2e9","650-c":"#ced9e5","651-c":"#a7bcd6","652-c":"#7d9bc1","653-c":"#326295","654-c":"#003a70","655-c":"#002554","656-c":"#dde5ed","657-c":"#c8d8eb","658-c":"#b1c9e8","659-c":"#7ba4db","660-c":"#407ec9","661-c":"#003594","662-c":"#001a70","7450-c":"#bdc5db","7451-c":"#89abe3","7452-c":"#8094dd","7453-c":"#7ba6de","7454-c":"#5f8fb4","7455-c":"#3a5dae","7456-c":"#606eb2","2706-c":"#cbd3eb","2716-c":"#9faee5","2726-c":"#485cc7","2736-c":"#1e22aa","2746-c":"#171c8f","2756-c":"#151f6d","2766-c":"#141b4d","2708-c":"#b8ccea","2718-c":"#5c88da","2728-c":"#0047bb","2738-c":"#06038d","2748-c":"#001871","2758-c":"#001e62","2768-c":"#071d49","2707-c":"#c3d7ee","2717-c":"#a7c6ed","2727-c":"#307fe2","2747-c":"#001a72","2757-c":"#001e60","2767-c":"#13294b","277-c":"#abcae9","278-c":"#8bb8e8","279-c":"#418fde","280-c":"#012169","281-c":"#00205b","282-c":"#041e42","283-c":"#92c1e9","284-c":"#6cace4","285-c":"#0072ce","286-c":"#0033a0","287-c":"#003087","288-c":"#002d72","289-c":"#0c2340","7681-c":"#94a9cb","7682-c":"#6787b7","7683-c":"#426da9","7684-c":"#385e9d","7685-c":"#2c5697","7686-c":"#1d4f91","7687-c":"#1d428a","545-c":"#c6dae7","544-c":"#bdd6e6","543-c":"#a4c8e1","542-c":"#7bafd4","541-c":"#003c71","540-c":"#003057","539-c":"#00263a","290-c":"#b9d9eb","291-c":"#9bcbeb","292-c":"#69b3e7","293-c":"#003da5","294-c":"#002f6c","295-c":"#002855","296-c":"#041c2c","2905-c":"#8dc8e8","2915-c":"#62b5e5","2925-c":"#009cde","2935-c":"#0057b8","2945-c":"#004c97","2955-c":"#003865","2965-c":"#00263e","297-c":"#71c5e8","298-c":"#41b6e6","299-c":"#00a3e0","300-c":"#005eb8","301-c":"#004b87","302-c":"#003b5c","303-c":"#002a3a","7688-c":"#4698cb","7689-c":"#298fc2","7690-c":"#0076a8","7691-c":"#006298","7692-c":"#005587","7693-c":"#004976","7694-c":"#01426a","2975-c":"#99d6ea","2985-c":"#5bc2e7","2995-c":"#00a9e0","3005-c":"#0077c8","3015-c":"#00629b","3025-c":"#004f71","3035-c":"#003e51","7695-c":"#7ba7bc","7696-c":"#6399ae","7697-c":"#4e87a0","7698-c":"#41748d","7699-c":"#34657f","7700-c":"#165c7d","7701-c":"#005776","7457-c":"#bbdde6","7458-c":"#71b2c9","7459-c":"#4298b5","7460-c":"#0086bf","7461-c":"#007dba","7462-c":"#00558c","7463-c":"#002b49","304-c":"#9adbe8","305-c":"#59cbe8","306-c":"#00b5e2","307-c":"#006ba6","308-c":"#00587c","309-c":"#003b49","635-c":"#a4dbe8","636-c":"#8bd3e6","637-c":"#4ec3e0","638-c":"#00afd7","639-c":"#0095c8","640-c":"#0082ba","641-c":"#0067a0","7702-c":"#48a9c5","7703-c":"#009cbd","7704-c":"#0085ad","7705-c":"#007096","7706-c":"#006a8e","7707-c":"#00617f","7708-c":"#005670","628-c":"#b8dde1","629-c":"#9bd3dd","630-c":"#77c5d5","631-c":"#3eb1c8","632-c":"#0093b2","633-c":"#007396","634-c":"#005f83","310-c":"#6ad1e3","311-c":"#05c3de","312-c":"#00a9ce","313-c":"#0092bc","314-c":"#007fa3","315-c":"#00677f","316-c":"#004851","3105-c":"#68d2df","3115-c":"#00c1d5","3125-c":"#00aec7","3135-c":"#008eaa","3145-c":"#00778b","3155-c":"#006272","3165-c":"#004f59","7709-c":"#63b1bc","7710-c":"#00a7b5","7711-c":"#0097a9","7712-c":"#00859b","7713-c":"#007d8a","7714-c":"#007680","7715-c":"#006269","317-c":"#b1e4e3","318-c":"#88dbdf","319-c":"#2dccd3","320-c":"#009ca6","321-c":"#008c95","322-c":"#007377","323-c":"#005f61","7464-c":"#a0d1ca","7465-c":"#40c1ac","7466-c":"#00b0b9","7467-c":"#00a3ad","7468-c":"#007398","7469-c":"#005f86","7470-c":"#005a70","7471-c":"#7eddd3","7472-c":"#5cb8b2","7473-c":"#279989","7474-c":"#007681","7475-c":"#487a7b","7476-c":"#0d5257","7477-c":"#244c5a","5523-c":"#b6cfd0","5513-c":"#abc7ca","5503-c":"#94b7bb","5493-c":"#7fa9ae","5483-c":"#4f868e","5473-c":"#115e67","5463-c":"#07272d","7716-c":"#00968f","7717-c":"#00857d","7718-c":"#007672","7719-c":"#006d68","7720-c":"#00635b","7721-c":"#005e5d","7722-c":"#005151","324-c":"#9cdbd9","325-c":"#64ccc9","326-c":"#00b2a9","327-c":"#008675","328-c":"#007367","329-c":"#00685e","330-c":"#00534c","3242-c":"#71dbd4","3252-c":"#2ad2c9","3262-c":"#00bfb3","3272-c":"#00a499","3282-c":"#008578","3292-c":"#00594f","3302-c":"#004c45","3245-c":"#7ce0d3","3255-c":"#2cd5c4","3265-c":"#00c7b1","3275-c":"#00b398","3285-c":"#009681","3295-c":"#007864","3305-c":"#004e42","3248-c":"#6dcdb8","3258-c":"#49c5b1","3268-c":"#00ab8e","3278-c":"#009b77","3288-c":"#008264","3298-c":"#006a52","3308-c":"#034638","566-c":"#b9dcd2","565-c":"#a1d6ca","564-c":"#86c8bc","563-c":"#6bbbae","562-c":"#006f62","561-c":"#00594c","560-c":"#1d3c34","573-c":"#b5e3d8","572-c":"#a5dfd3","571-c":"#98dbce","570-c":"#6bcaba","569-c":"#00816d","568-c":"#006c5b","567-c":"#173f35","559-c":"#adcab8","558-c":"#9abeaa","557-c":"#85b09a","556-c":"#6fa287","555-c":"#28724f","554-c":"#205c40","553-c":"#284734","5595-c":"#bfcec2","5585-c":"#a7bdb1","5575-c":"#92aca0","5565-c":"#7f9c90","5555-c":"#5c7f71","5545-c":"#43695b","5535-c":"#183028","5665-c":"#bac5b9","5655-c":"#b0bdb0","5645-c":"#a3b2a4","5635-c":"#94a596","5625-c":"#708573","5615-c":"#5e7461","5605-c":"#22372b","5527-c":"#bcc9c5","5517-c":"#b1c0bc","5507-c":"#9db0ac","5497-c":"#829995","5487-c":"#5d7975","5477-c":"#3e5d58","5467-c":"#18332f","621-c":"#d1e0d7","622-c":"#b7cdc2","623-c":"#9ab9ad","624-c":"#789f90","625-c":"#507f70","626-c":"#285c4d","627-c":"#13322b","331-c":"#a7e6d7","332-c":"#8ce2d0","333-c":"#3cdbc0","334-c":"#009775","335-c":"#007b5f","336-c":"#00664f","337-c":"#8fd6bd","338-c":"#6eceb2","339-c":"#00b388","340-c":"#00965e","341-c":"#007a53","342-c":"#006747","343-c":"#115740","7723-c":"#50a684","7724-c":"#00966c","7725-c":"#008755","7726-c":"#007b4b","7727-c":"#006f44","7728-c":"#006845","7729-c":"#005844","3375-c":"#7ae1bf","3385-c":"#47d7ac","3395-c":"#00c389","3405-c":"#00af66","3415-c":"#007749","3425-c":"#006341","3435-c":"#154734","344-c":"#a0dab3","345-c":"#91d6ac","346-c":"#71cc98","347-c":"#009a44","348-c":"#00843d","349-c":"#046a38","350-c":"#2c5234","351-c":"#a2e4b8","352-c":"#8fe2b0","353-c":"#80e0a7","354-c":"#00b140","355-c":"#009639","356-c":"#007a33","357-c":"#215732","7478-c":"#9be3bf","7479-c":"#26d07c","7480-c":"#00bf6f","7481-c":"#00b74f","7482-c":"#009f4d","7483-c":"#275d38","7484-c":"#00573f","7730-c":"#4b9560","7731-c":"#228848","7732-c":"#007a3e","7733-c":"#007041","7734-c":"#286140","7735-c":"#36573b","7736-c":"#395542","7737-c":"#6ba539","7738-c":"#48a23f","7739-c":"#319b42","7740-c":"#3a913f","7741-c":"#44883e","7742-c":"#4a773c","7743-c":"#44693d","358-c":"#addc91","359-c":"#a1d884","360-c":"#6cc24a","361-c":"#43b02a","362-c":"#509e2f","363-c":"#4c8c2b","364-c":"#4a7729","7485-c":"#d0debb","7486-c":"#bce194","7487-c":"#8edd65","7488-c":"#78d64b","7489-c":"#74aa50","7490-c":"#719949","7491-c":"#79863c","365-c":"#c2e189","366-c":"#b7dd79","367-c":"#a4d65e","368-c":"#78be20","369-c":"#64a70b","370-c":"#658d1b","371-c":"#546223","372-c":"#d4eb8e","373-c":"#cdea80","374-c":"#c5e86c","375-c":"#97d700","376-c":"#84bd00","377-c":"#7a9a01","378-c":"#59621d","580-c":"#c4d6a4","579-c":"#bcd19b","578-c":"#b7ce95","577-c":"#a9c47f","576-c":"#789d4a","575-c":"#67823a","574-c":"#4e5b31","5807-c":"#d0d1ab","5797-c":"#c6c89b","5787-c":"#babd8b","5777-c":"#a2a569","5767-c":"#8a8d4a","5757-c":"#6d712e","5747-c":"#3d441e","5875-c":"#d2ce9e","5865-c":"#cbc793","5855-c":"#c0bb87","5845-c":"#afa96e","5835-c":"#a09958","5825-c":"#89813d","5815-c":"#555025","5803-c":"#c3c6a8","5793-c":"#b3b995","5783-c":"#a3aa83","5773-c":"#899064","5763-c":"#737b4c","5753-c":"#5e6738","5743-c":"#3e4827","7492-c":"#bfcc80","7493-c":"#bbc592","7494-c":"#9caf88","7495-c":"#8f993e","7496-c":"#76881d","7497-c":"#7a7256","7498-c":"#5b6236","7744-c":"#babc16","7745-c":"#abad23","7746-c":"#999b30","7747-c":"#888d30","7748-c":"#7c8034","7749-c":"#727337","7750-c":"#656635","379-c":"#e2e868","380-c":"#dbe442","381-c":"#cedc00","382-c":"#c4d600","383-c":"#a8ad00","384-c":"#949300","385-c":"#787121","386-c":"#e9ec6b","387-c":"#e3e935","388-c":"#e0e721","389-c":"#d0df00","390-c":"#b5bd00","391-c":"#9a9500","392-c":"#827a04","587-c":"#e3e48d","586-c":"#e0e27c","585-c":"#dbde70","584-c":"#d2d755","583-c":"#b7bf10","582-c":"#8e8c13","581-c":"#625d20","393-c":"#f0ec74","394-c":"#ede939","395-c":"#ece81a","396-c":"#e1e000","397-c":"#bfb800","398-c":"#ada400","399-c":"#a09200","3935-c":"#f3ea5d","3945-c":"#f3e500","3955-c":"#efdf00","3965-c":"#eedc00","3975-c":"#bba600","3985-c":"#9a8700","3995-c":"#685c20","600-c":"#f1eb9c","601-c":"#f0e991","602-c":"#f0e87b","603-c":"#ede04b","604-c":"#eada24","605-c":"#e1cd00","606-c":"#cfb500","607-c":"#ebe49a","608-c":"#e9e186","609-c":"#e6de77","610-c":"#e1d555","611-c":"#d7c826","612-c":"#c4b000","613-c":"#b39b00","461-c":"#e9df97","460-c":"#e4d77e","459-c":"#decd63","458-c":"#d9c756","457-c":"#b89d18","456-c":"#a28e2a","455-c":"#695b24","614-c":"#dcd59a","615-c":"#d6cf8d","616-c":"#d0c883","617-c":"#c0b561","618-c":"#ac9f3c","619-c":"#9f912a","620-c":"#8a7b19","7751-c":"#cab64b","7752-c":"#cfb023","7753-c":"#c1a01e","7754-c":"#a08629","7755-c":"#897630","7756-c":"#736635","7757-c":"#675e33","7758-c":"#d4c304","7759-c":"#c4b200","7760-c":"#91852c","7761-c":"#747136","7762-c":"#5d6439","7763-c":"#585c3b","7764-c":"#535435","7765-c":"#bbb323","7766-c":"#b4a91f","7767-c":"#aa9d2e","7768-c":"#8f7e35","7769-c":"#716135","7770-c":"#635939","7771-c":"#4e4934","4545-c":"#d5cb9f","4535-c":"#cfc493","4525-c":"#c5b783","4515-c":"#b3a369","4505-c":"#998542","4495-c":"#8c7732","4485-c":"#614f25","454-c":"#cac7a7","453-c":"#bfbb98","452-c":"#b0aa7e","451-c":"#9b945f","450-c":"#594a25","449-c":"#524727","448-c":"#4a412a","7499-c":"#f1e6b2","7500-c":"#dfd1a7","7501-c":"#d9c89e","7502-c":"#ceb888","7503-c":"#a89968","7504-c":"#94795d","7505-c":"#816040","468-c":"#ddcba4","467-c":"#d3bc8d","466-c":"#c6aa76","465-c":"#b9975b","464-c":"#8b5b29","463-c":"#744f28","462-c":"#5c462b","7506-c":"#efdbb2","7507-c":"#fcd299","7508-c":"#e1b87f","7509-c":"#d6a461","7510-c":"#c6893f","7511-c":"#b77729","7512-c":"#a6631b","719-c":"#edc8a3","720-c":"#e7b78a","721-c":"#dda46f","722-c":"#c88242","723-c":"#b36924","724-c":"#934d11","725-c":"#7d3f16","475-c":"#f3cfb3","474-c":"#f1c6a7","473-c":"#f0bf9b","472-c":"#e59e6d","471-c":"#b86125","470-c":"#a45a2a","469-c":"#693f23","726-c":"#e0c09f","727-c":"#d9b48f","728-c":"#cda077","729-c":"#b58150","730-c":"#9e652e","731-c":"#774212","732-c":"#623412","4685-c":"#e0c6ad","4675-c":"#dcbfa6","4665-c":"#cda788","4655-c":"#bf9474","4645-c":"#ad7c59","4635-c":"#946037","4625-c":"#4f2c1d","7513-c":"#e1b7a7","7514-c":"#d5a286","7515-c":"#c58b68","7516-c":"#99552b","7517-c":"#85431e","7518-c":"#6d4f47","7519-c":"#5e4b3c","4755-c":"#d7c4b7","4745-c":"#cdb5a7","4735-c":"#c0a392","4725-c":"#ae8a79","4715-c":"#956c58","4705-c":"#7c4d3a","4695-c":"#5b3427","482-c":"#dbc8b6","481-c":"#d3bba8","480-c":"#c6a992","479-c":"#aa8066","478-c":"#703f2a","477-c":"#623b2a","476-c":"#4e3629","7527-c":"#d6d2c4","7528-c":"#c5b9ac","7529-c":"#b7a99a","7530-c":"#a39382","7531-c":"#7a6855","7532-c":"#63513d","7533-c":"#473729","7534-c":"#d1ccbd","7535-c":"#b7b09c","7536-c":"#a69f88","7537-c":"#a7aca2","7538-c":"#949a90","7539-c":"#8e9089","7540-c":"#4b4f54","427-c":"#d0d3d4","428-c":"#c1c6c8","429-c":"#a2aaad","430-c":"#7c878e","431-c":"#5b6770","432-c":"#333f48","433-c":"#1d252d","420-c":"#c7c9c7","421-c":"#b2b4b2","422-c":"#9ea2a2","423-c":"#898d8d","424-c":"#707372","425-c":"#54585a","426-c":"#25282a","441-c":"#bec6c4","442-c":"#a2acab","443-c":"#919d9d","444-c":"#717c7d","445-c":"#505759","446-c":"#3f4444","447-c":"#373a36","413-c":"#babbb1","414-c":"#a8a99e","415-c":"#919388","416-c":"#7e7f74","417-c":"#65665c","418-c":"#51534a","419-c":"#212322","400-c":"#c4bfb6","401-c":"#afa9a0","402-c":"#9d968d","403-c":"#8c857b","404-c":"#776e64","405-c":"#696158","406-c":"#c4bcb7","407-c":"#b2a8a2","408-c":"#978c87","409-c":"#857874","410-c":"#746661","411-c":"#5e514d","412-c":"#382f2d","434-c":"#d0c4c5","435-c":"#c1b2b6","436-c":"#ab989d","437-c":"#7b6469","438-c":"#584446","439-c":"#453536","440-c":"#382e2c","warm-gray-1-c":"#d7d2cb","warm-gray-2-c":"#cbc4bc","warm-gray-3-c":"#bfb8af","warm-gray-4-c":"#b6ada5","warm-gray-5-c":"#aca39a","warm-gray-6-c":"#a59c94","warm-gray-7-c":"#968c83","warm-gray-8-c":"#8c8279","warm-gray-9-c":"#83786f","warm-gray-10-c":"#796e65","warm-gray-11-c":"#6e6259","cool-gray-1-c":"#d9d9d6","cool-gray-2-c":"#d0d0ce","cool-gray-3-c":"#c8c9c7","cool-gray-4-c":"#bbbcbc","cool-gray-5-c":"#b1b3b3","cool-gray-6-c":"#a7a8aa","cool-gray-7-c":"#97999b","cool-gray-8-c":"#888b8d","cool-gray-9-c":"#75787b","cool-gray-10-c":"#63666a","cool-gray-11-c":"#53565a","black-2-c":"#332f21","black-3-c":"#212721","black-4-c":"#31261d","black-5-c":"#3e2b2e","black-6-c":"#101820","black-7-c":"#3d3935","yellow-c":"#fedd00","yellow-012-c":"#ffd700","orange-021-c":"#fe5000","warm-red-c":"#f9423a","red-032-c":"#ef3340","rubine-red-c":"#ce0058","rhodamine-red-c":"#e10098","purple-c":"#bb29bb","violet-c":"#440099","blue-072-c":"#10069f","reflex-blue-c":"#001489","process-blue-c":"#0085ca","green-c":"#00ab84","black-c":"#2d2926","yellow-0131-c":"#f2f0a1","red-0331-c":"#fcaebb","magenta-0521-c":"#f1b2dc","violet-0631-c":"#bf9bde","blue-0821-c":"#74d1ea","green-0921-c":"#9de7d7","black-0961-c":"#9e978e","801-c":"#009ace","802-c":"#44d62c","803-c":"#ffe900","804-c":"#ffaa4d","805-c":"#ff7276","806-c":"#ff3eb5","807-c":"#ea27c2","871-c":"#84764d","872-c":"#8b734a","873-c":"#896e4b","874-c":"#8a6f4c","875-c":"#8e6d4f","876-c":"#95694c","877-c":"#8d9092","medium-yellow-c":"#ffd900","bright-orange-c":"#ff5e00","bright-red-c":"#f93822","strong-red-c":"#ce0056","pink-c":"#d62598","medium-purple-c":"#4e008e","dark-blue-c":"#00239c","medium-blue-c":"#0084ca","bright-green-c":"#00b08b","neutral-black-c":"#222223","100-u":"#fef380","101-u":"#fff166","102-u":"#ffec2d","103-u":"#b49f29","104-u":"#9b8d39","105-u":"#7d7547","7401-u":"#fee5a5","7402-u":"#f3da9a","7403-u":"#f4cf81","7404-u":"#fdcf41","7405-u":"#e6b10e","7406-u":"#f1b828","7407-u":"#cca36e","106-u":"#ffeb68","107-u":"#ffe552","108-u":"#ffdd35","109-u":"#ffc700","110-u":"#cc9f26","111-u":"#9c873b","112-u":"#8a7a3e","113-u":"#ffe15e","114-u":"#ffd94a","115-u":"#ffce2e","116-u":"#ffb511","117-u":"#b78c30","118-u":"#9e8139","119-u":"#847748","127-u":"#fce385","128-u":"#fccf61","129-u":"#faaf3f","130-u":"#f79b2e","131-u":"#ba8032","132-u":"#977a3c","133-u":"#7c7047","1205-u":"#ffe495","1215-u":"#ffda7e","1225-u":"#ffbb4f","1235-u":"#ffae3b","1245-u":"#bb8b41","1255-u":"#a08246","1265-u":"#887549","120-u":"#ffd96a","121-u":"#ffcc52","122-u":"#ffc03f","123-u":"#ffac2a","124-u":"#d79133","125-u":"#a6813d","126-u":"#8e7742","7548-u":"#ffc915","7549-u":"#ffb838","7550-u":"#ce973d","7551-u":"#ae8643","7552-u":"#826f4b","7553-u":"#70654b","7554-u":"#6c6558","7555-u":"#d7a95a","7556-u":"#bc995d","7557-u":"#a58d5f","7558-u":"#9b835e","7559-u":"#977f5e","7560-u":"#8e7e62","7561-u":"#807964","134-u":"#ffd378","135-u":"#ffc267","136-u":"#ffad4a","137-u":"#ff9f37","138-u":"#d37c32","139-u":"#9d6f3c","140-u":"#7f6a45","1345-u":"#ffd591","1355-u":"#ffc87e","1365-u":"#ffad5b","1375-u":"#ff9940","1385-u":"#cd7e3e","1395-u":"#946e43","1405-u":"#796648","141-u":"#f9c16e","142-u":"#f8b05d","143-u":"#f6a04d","144-u":"#f38b3c","145-u":"#c97f3a","146-u":"#9d7441","147-u":"#76694a","7408-u":"#ee9d26","7409-u":"#f7ad50","7410-u":"#ffa06e","7411-u":"#e9a16f","7412-u":"#d08c60","7413-u":"#de8958","7414-u":"#c07f53","7562-u":"#bda078","7563-u":"#daa563","7564-u":"#de9858","7565-u":"#c98659","7566-u":"#ae7b5e","7567-u":"#90705c","7568-u":"#826d5e","7569-u":"#d28e56","7570-u":"#d18d5b","7571-u":"#c3895d","7572-u":"#b17f5b","7573-u":"#a87b59","7574-u":"#99775b","7575-u":"#86715a","712-u":"#ffca9d","713-u":"#ffc190","714-u":"#ffb07b","715-u":"#f99963","716-u":"#ea8651","717-u":"#d77540","718-u":"#c76c3d","148-u":"#ffc688","149-u":"#ffb575","150-u":"#ff9955","151-u":"#ff883e","152-u":"#e07a3f","153-u":"#ac6f42","154-u":"#926844","155-u":"#f7c995","156-u":"#f7b984","157-u":"#f39662","158-u":"#ee7f4b","159-u":"#c97146","160-u":"#9d6b47","161-u":"#786249","1485-u":"#ffa563","1495-u":"#ff9351","1505-u":"#ff8038","1525-u":"#bd6439","1535-u":"#976241","1545-u":"#7a5f48","1555-u":"#ffc29e","1565-u":"#ffaa81","1575-u":"#ff9665","1585-u":"#ff8450","1595-u":"#d37046","1605-u":"#a76c4c","1615-u":"#93654a","162-u":"#ffbb9b","163-u":"#ff9c76","164-u":"#ff865f","165-u":"#ff7548","166-u":"#e16740","167-u":"#b46547","168-u":"#8a614b","7576-u":"#da8e6b","7577-u":"#e18b63","7578-u":"#db7d59","7579-u":"#df7357","7580-u":"#c06d59","7581-u":"#916b5d","7582-u":"#7a685e","1625-u":"#ffa891","1635-u":"#ff967e","1645-u":"#ff7f61","1655-u":"#ff6f4c","1665-u":"#e56849","1675-u":"#ab614a","1685-u":"#95614d","169-u":"#ffb7ae","170-u":"#ff8d7e","171-u":"#ff7865","172-u":"#ff674d","173-u":"#ce614a","174-u":"#9c5b4b","175-u":"#7d594d","7583-u":"#ca7d5f","7584-u":"#c07759","7585-u":"#b67965","7586-u":"#a2715d","7587-u":"#996e5c","7588-u":"#896e61","7589-u":"#756a63","7590-u":"#dbbca9","7591-u":"#c68d78","7592-u":"#b47260","7593-u":"#a26859","7594-u":"#8b645a","7595-u":"#80665c","7596-u":"#776760","7597-u":"#d66756","7598-u":"#c36d5c","7599-u":"#bd695a","7600-u":"#9b6a5e","7601-u":"#92695c","7602-u":"#8d6e61","7603-u":"#7c675d","7604-u":"#ead8d6","7605-u":"#eac4be","7606-u":"#dea09a","7607-u":"#cb867f","7608-u":"#ab6e67","7609-u":"#926660","7610-u":"#80615e","7611-u":"#e7c8be","7612-u":"#d4aaa0","7613-u":"#c89f97","7614-u":"#ae928c","7615-u":"#978480","7616-u":"#867474","7617-u":"#756667","7520-u":"#f3c4bf","7521-u":"#c4a195","7522-u":"#af7871","7523-u":"#a97576","7524-u":"#a36d6a","7525-u":"#997970","7526-u":"#925f52","489-u":"#f5c7b8","488-u":"#f4b9a9","487-u":"#f3a293","486-u":"#f08d80","485-u":"#e45d50","484-u":"#a05b4e","483-u":"#7d5b4f","176-u":"#ffb1ba","177-u":"#ff8c91","178-u":"#ff7677","179-u":"#e15f55","180-u":"#bf6059","181-u":"#8b5952","1765-u":"#ffa7b6","1775-u":"#ff8b9a","1785-u":"#fe6672","1788-u":"#f95d62","1795-u":"#da5959","1805-u":"#b45858","1815-u":"#8f5652","1767-u":"#ffbac9","1777-u":"#ff8091","1787-u":"#fc6371","1797-u":"#cb4f53","1807-u":"#a75154","1817-u":"#795453","7618-u":"#d08a77","7619-u":"#c87164","7620-u":"#c0615b","7621-u":"#ba5d5a","7622-u":"#a55d5c","7623-u":"#9b5c57","7624-u":"#8d5a54","7625-u":"#ea776c","7626-u":"#d2625f","7627-u":"#ad5857","7628-u":"#a85959","7629-u":"#805b5a","7630-u":"#765957","7631-u":"#6e5555","7415-u":"#ebb5a5","7416-u":"#ea746a","7417-u":"#e76b61","7418-u":"#bf6366","7419-u":"#a6626b","7420-u":"#a45768","7421-u":"#7d4e5b","182-u":"#ffb8ca","183-u":"#ff8da3","184-u":"#fc6e83","185-u":"#f15060","186-u":"#d2515e","187-u":"#ae535c","188-u":"#895559","196-u":"#f4c3cb","197-u":"#f2a1b2","198-u":"#e76b7c","199-u":"#dd5061","200-u":"#bd4f5c","201-u":"#a15760","202-u":"#90585e","189-u":"#ffabc2","190-u":"#fe87a3","191-u":"#f87089","192-u":"#ee536b","193-u":"#c55265","194-u":"#a35965","195-u":"#885a61","1895-u":"#feb3cd","1905-u":"#fd9cba","1915-u":"#f66f92","1925-u":"#e9506e","1935-u":"#c94c63","1945-u":"#b05468","1955-u":"#975361","705-u":"#ffdde2","706-u":"#ffcad4","707-u":"#ffb0bf","708-u":"#fe99a8","709-u":"#f47c8a","710-u":"#e4636f","711-u":"#ce5055","698-u":"#fad7da","699-u":"#fcc2cc","700-u":"#f8aab6","701-u":"#e98f9d","702-u":"#d87987","703-u":"#b45a65","704-u":"#a24f56","203-u":"#f5b1cc","204-u":"#ee82a4","205-u":"#e56284","206-u":"#d84465","207-u":"#af4c64","208-u":"#905567","209-u":"#825864","210-u":"#ffa2cb","211-u":"#fc85b6","212-u":"#f56d9e","213-u":"#e95485","214-u":"#d1517a","215-u":"#ae5171","216-u":"#8a5566","7422-u":"#fdd4da","7423-u":"#e2758f","7424-u":"#d9577d","7425-u":"#bb5a73","7426-u":"#b1556a","7427-u":"#a5525c","7428-u":"#805c67","7632-u":"#ddcfd0","7633-u":"#c9aaae","7634-u":"#c77d92","7635-u":"#cd627d","7636-u":"#c3566d","7637-u":"#955864","7638-u":"#855660","217-u":"#f4c1e1","218-u":"#ef84b9","219-u":"#e45c96","220-u":"#b04c75","221-u":"#9e4c6e","222-u":"#82566b","7639-u":"#a0858a","7640-u":"#9b6673","7641-u":"#9a5d6d","7642-u":"#7e5867","7643-u":"#7a5a67","7644-u":"#715862","7645-u":"#6c5760","223-u":"#f9a4d8","224-u":"#f587c7","225-u":"#ea60a7","226-u":"#dc4588","227-u":"#af4879","228-u":"#8d4d6d","229-u":"#7a5466","230-u":"#feb2e0","231-u":"#f984ca","232-u":"#f16ab7","233-u":"#c84687","234-u":"#aa4c7d","235-u":"#914e72","670-u":"#f3d6e5","671-u":"#efc3dd","672-u":"#e8a7cd","673-u":"#df8cba","674-u":"#cd72a1","675-u":"#bd618e","676-u":"#a85079","677-u":"#edd2e0","678-u":"#e5c0d4","679-u":"#daacc6","680-u":"#c08ba9","681-u":"#a5708d","682-u":"#905d78","683-u":"#805268","684-u":"#eccbd9","685-u":"#e4bdd0","686-u":"#d5a6be","687-u":"#bd8ca6","688-u":"#a2738c","689-u":"#8e6178","690-u":"#7c5060","510-u":"#f2bfcb","509-u":"#eaaebe","508-u":"#df9cae","507-u":"#cf8fa4","506-u":"#9a6372","505-u":"#8a5c66","504-u":"#7f5d62","7429-u":"#eeccd9","7430-u":"#e6b5c9","7431-u":"#dca2b9","7432-u":"#c5819a","7433-u":"#b96f89","7434-u":"#a8677d","7435-u":"#8c566b","691-u":"#f1d3d5","692-u":"#e9c0c5","693-u":"#daa5ad","694-u":"#c38d96","695-u":"#ac7680","696-u":"#98656c","697-u":"#87565b","496-u":"#f9c2cc","495-u":"#f1afbc","494-u":"#e59aa8","493-u":"#c77c89","492-u":"#9d5d60","491-u":"#8e595a","490-u":"#775554","503-u":"#f2cdcf","502-u":"#e6b7bd","501-u":"#d8a5ac","500-u":"#bd898f","499-u":"#8d615f","498-u":"#805d5b","497-u":"#715956","5035-u":"#e6c6c7","5025-u":"#dcb6ba","5015-u":"#caa1a6","5005-u":"#b28a90","4995-u":"#9f787f","4985-u":"#8f6a6f","4975-u":"#745654","236-u":"#f8a6de","237-u":"#f28dd2","238-u":"#e971c0","239-u":"#db55aa","240-u":"#c15598","241-u":"#af548b","242-u":"#8d5977","2365-u":"#f8bfe7","2375-u":"#ea88d2","2385-u":"#e06fc2","2395-u":"#d557af","2405-u":"#bb569d","2415-u":"#ab5491","2425-u":"#975381","243-u":"#f1b2e6","244-u":"#eb9ade","245-u":"#e07fd0","246-u":"#ce57b3","247-u":"#bd51a2","248-u":"#a14f8c","249-u":"#885779","7646-u":"#b08799","7647-u":"#b16889","7648-u":"#9e537b","7649-u":"#935379","7650-u":"#835573","7651-u":"#7f5c78","7652-u":"#73556c","250-u":"#eebae9","251-u":"#e096df","252-u":"#ce70cc","253-u":"#b456ae","254-u":"#9e549a","255-u":"#83577d","517-u":"#f2c8e3","516-u":"#e8b6dc","515-u":"#e2a5d4","514-u":"#ce89c3","513-u":"#9e5f98","512-u":"#8a587f","511-u":"#745568","7436-u":"#f3d5ed","7437-u":"#d4b6d6","7438-u":"#d79bd7","7439-u":"#c09cc6","7440-u":"#b796ba","7441-u":"#ae7eca","7442-u":"#9e62be","2562-u":"#ddaee9","2572-u":"#cd90de","2582-u":"#bb76cf","2592-u":"#aa60bf","2602-u":"#9757a6","2612-u":"#86578d","2622-u":"#7b5b7a","7653-u":"#9e95a0","7654-u":"#aa8aa5","7655-u":"#a978a0","7656-u":"#96648d","7657-u":"#7e5978","7658-u":"#7c5e77","7659-u":"#6e5a6b","524-u":"#dac5dc","523-u":"#d0b7d5","522-u":"#bc9fc6","521-u":"#a98bb5","520-u":"#815f87","519-u":"#775d7a","518-u":"#6c5969","5245-u":"#e0cfd6","5235-u":"#d6c3cc","5225-u":"#c2acb9","5215-u":"#a28c9c","5205-u":"#907b8a","5195-u":"#816c78","5185-u":"#756069","5175-u":"#dac5d0","5165-u":"#ccb4c4","5155-u":"#b99eb2","5145-u":"#a4899f","5135-u":"#8c7287","5125-u":"#7d6378","5115-u":"#6f5564","531-u":"#e4c7eb","530-u":"#dbb9e8","529-u":"#cea5e1","528-u":"#b889d2","527-u":"#9361b0","526-u":"#805791","525-u":"#735775","256-u":"#d4b4da","257-u":"#c29bcb","258-u":"#a276ae","259-u":"#94679c","260-u":"#8a658d","261-u":"#7f6282","262-u":"#755e74","2563-u":"#cda2d9","2573-u":"#b989cb","2583-u":"#a372b5","2593-u":"#8e5da0","2603-u":"#845991","2613-u":"#7d5787","2623-u":"#75557a","7660-u":"#9c97a8","7661-u":"#947f9d","7662-u":"#84668c","7663-u":"#7a5c83","7664-u":"#765d84","7665-u":"#725e7c","7666-u":"#706977","2567-u":"#c09fdb","2577-u":"#a47ec6","2587-u":"#936db7","2597-u":"#825aa4","2607-u":"#7a5896","2617-u":"#735489","2627-u":"#6e527c","263-u":"#dcc6ea","264-u":"#bfa1e3","265-u":"#9d7ad2","266-u":"#8a65c4","267-u":"#7f5dae","268-u":"#735990","269-u":"#6e587f","2635-u":"#c9b7e8","2645-u":"#b9a3e3","2655-u":"#a288d9","2665-u":"#8c6fca","2685-u":"#765ba7","2695-u":"#6c5d80","270-u":"#adaede","271-u":"#9a9bd4","272-u":"#8586c6","273-u":"#645d9b","274-u":"#5f578b","275-u":"#5d5683","276-u":"#5c556f","2705-u":"#bab6e9","2715-u":"#9f98df","2725-u":"#8980d4","2735-u":"#6857b2","2745-u":"#61559a","2755-u":"#5d538b","2765-u":"#574f77","7667-u":"#828ca8","7668-u":"#7b81a4","7669-u":"#7878a2","7670-u":"#706e9a","7671-u":"#6e6993","7672-u":"#6b668e","7673-u":"#6c6d8d","7443-u":"#ddd8eb","7444-u":"#bcbde2","7445-u":"#aca8c7","7446-u":"#9493cf","7447-u":"#7f7392","7448-u":"#726874","7449-u":"#63515c","7674-u":"#9296b8","7675-u":"#888caf","7676-u":"#827aa3","7677-u":"#7c6d96","7678-u":"#766691","7679-u":"#6e628b","7680-u":"#695981","663-u":"#e2d9e3","664-u":"#dacfdd","665-u":"#c5b8ce","666-u":"#aea1bc","667-u":"#9083a1","668-u":"#817391","669-u":"#71637f","5315-u":"#dbd8e1","5305-u":"#c5c2d2","5295-u":"#b1afc3","5285-u":"#9694ac","5275-u":"#7b7992","5265-u":"#6d6b82","5255-u":"#5d566c","538-u":"#cdd7e1","537-u":"#bdcada","536-u":"#a4b5cb","535-u":"#8c9db8","534-u":"#5b6983","533-u":"#5e6578","532-u":"#5b5c64","7541-u":"#dde4e6","7542-u":"#b0c5cc","7543-u":"#aeb8c0","7544-u":"#9aa5af","7545-u":"#78838e","7546-u":"#67707a","7547-u":"#51545d","552-u":"#b0d0dc","551-u":"#96c0ce","550-u":"#7aaabe","549-u":"#6899ae","548-u":"#3e5e6f","547-u":"#415866","546-u":"#465058","5455-u":"#c7d7e0","5445-u":"#b4c7d3","5435-u":"#9db4c4","5425-u":"#7993a5","5415-u":"#677f91","5405-u":"#5b6f80","5395-u":"#464c59","642-u":"#d3e0ea","643-u":"#bfd2e4","644-u":"#9db9d5","645-u":"#84a3c4","646-u":"#6e8db0","647-u":"#5f7a9d","648-u":"#4e6282","649-u":"#e1e7ee","650-u":"#d7e0eb","651-u":"#afc3dc","652-u":"#89a2c4","653-u":"#6780a4","654-u":"#55688b","655-u":"#4e5d7f","656-u":"#e2eaf2","657-u":"#d6e2f2","658-u":"#b8cfee","659-u":"#96b6e5","660-u":"#658bcc","661-u":"#4967aa","662-u":"#425390","7450-u":"#c3cbe0","7451-u":"#94b3ea","7452-u":"#91a0e4","7453-u":"#88ade3","7454-u":"#7599bb","7455-u":"#6b7db9","7456-u":"#8089c0","2706-u":"#ccd4f1","2716-u":"#a2afe9","2726-u":"#717acf","2736-u":"#5e60ba","2746-u":"#595ca5","2756-u":"#585c8c","2766-u":"#575a7c","2708-u":"#b2c9ef","2718-u":"#6a87d8","2728-u":"#5268c2","2738-u":"#474da4","2748-u":"#484f8c","2758-u":"#484d7a","2768-u":"#4a4e6d","2707-u":"#c7dbf4","2717-u":"#a9c8f4","2727-u":"#6490e8","2747-u":"#424f90","2757-u":"#454f7e","2767-u":"#4a4c64","277-u":"#adcff1","278-u":"#8dbaee","279-u":"#5b8edb","280-u":"#3c4981","281-u":"#3f4a75","282-u":"#434967","283-u":"#9bc7ee","284-u":"#7ab1e8","285-u":"#4982cf","286-u":"#3255a4","287-u":"#3c5896","288-u":"#3d5588","289-u":"#434f69","7681-u":"#9db0d0","7682-u":"#8097bf","7683-u":"#6982b0","7684-u":"#6276a3","7685-u":"#5e719d","7686-u":"#556995","7687-u":"#525f8d","545-u":"#aecfe6","544-u":"#97c1df","543-u":"#79abd2","542-u":"#699cc6","541-u":"#3a557c","540-u":"#3f526f","539-u":"#465062","290-u":"#a7d4ee","291-u":"#7fbeeb","292-u":"#62a8e5","293-u":"#235ba8","294-u":"#385988","295-u":"#375172","296-u":"#435060","2905-u":"#8ac9ed","2915-u":"#64b3e8","2925-u":"#4097db","2935-u":"#1b5faa","2945-u":"#29588c","2955-u":"#395775","2965-u":"#3d5265","297-u":"#7eccee","298-u":"#4cb4e7","299-u":"#1295d8","300-u":"#006eb6","301-u":"#28628e","302-u":"#375e77","303-u":"#3f5666","7688-u":"#5e9aca","7689-u":"#5693c1","7690-u":"#4c7fa7","7691-u":"#467198","7692-u":"#4c6b8b","7693-u":"#4d6480","7694-u":"#4d6179","2975-u":"#8bd5ee","2985-u":"#48b8e7","2995-u":"#0d9ddb","3005-u":"#0078bf","3015-u":"#216a95","3025-u":"#37617a","3035-u":"#405866","7695-u":"#82a8bd","7696-u":"#719baf","7697-u":"#698fa4","7698-u":"#637f92","7699-u":"#5e7688","7700-u":"#557288","7701-u":"#496b80","7457-u":"#bfe3ed","7458-u":"#67a5bf","7459-u":"#518eaa","7460-u":"#008ec0","7461-u":"#4492c6","7462-u":"#547aa3","7463-u":"#51657d","304-u":"#8edded","305-u":"#53caec","306-u":"#00b4e4","307-u":"#0074a2","308-u":"#266782","309-u":"#3f5864","635-u":"#ace3ef","636-u":"#8ad8ed","637-u":"#5ec8e5","638-u":"#32b8df","639-u":"#009ecc","640-u":"#008aba","641-u":"#0078a7","7702-u":"#5dabc6","7703-u":"#46a0bf","7704-u":"#348bac","7705-u":"#3c7d9a","7706-u":"#437a94","7707-u":"#477388","7708-u":"#456b7d","628-u":"#b4e0e6","629-u":"#8dd0de","630-u":"#74c4d7","631-u":"#56b2cb","632-u":"#3998b5","633-u":"#27829e","634-u":"#226e8a","310-u":"#5bd0e6","311-u":"#01bedb","312-u":"#00afd0","313-u":"#008db1","314-u":"#007d99","315-u":"#206779","316-u":"#3e5a62","3105-u":"#4ccede","3115-u":"#00bfd3","3125-u":"#00abc2","3135-u":"#008ea6","3145-u":"#007788","3155-u":"#1b6875","3165-u":"#2f5c66","7709-u":"#6eb2bd","7710-u":"#40aab8","7711-u":"#179dab","7712-u":"#228b9d","7713-u":"#2d8692","7714-u":"#358189","7715-u":"#417178","317-u":"#aee9e8","318-u":"#6bd9de","319-u":"#25c9d0","320-u":"#009da5","321-u":"#00838a","322-u":"#167378","323-u":"#2f6165","7464-u":"#aadbd7","7465-u":"#55c7b5","7466-u":"#00b4bc","7467-u":"#00a8b0","7468-u":"#3f7793","7469-u":"#3c6e89","7470-u":"#437083","7471-u":"#7fe2d8","7472-u":"#6bbdb9","7473-u":"#5ea59a","7474-u":"#458a94","7475-u":"#6b8b8d","7476-u":"#5a777b","7477-u":"#5d7079","5523-u":"#bcd5d6","5513-u":"#accacd","5503-u":"#94b7bc","5493-u":"#769ca3","5483-u":"#5f8289","5473-u":"#516d75","5463-u":"#3e4e56","7716-u":"#499996","7717-u":"#3e8e8b","7718-u":"#358282","7719-u":"#417b7b","7720-u":"#477675","7721-u":"#497375","7722-u":"#4a6b6e","324-u":"#82d8d5","325-u":"#40c1bb","326-u":"#00aaa1","327-u":"#008f85","328-u":"#137d79","329-u":"#2e7572","330-u":"#446463","3242-u":"#5ddbd3","3252-u":"#00cdc2","3262-u":"#00baae","3272-u":"#00a49a","3282-u":"#008780","3292-u":"#346e6a","3302-u":"#41615f","3245-u":"#4edcca","3255-u":"#00cfbb","3265-u":"#00c0aa","3275-u":"#00aa93","3285-u":"#009081","3295-u":"#237e74","3305-u":"#466964","3248-u":"#6aceb9","3258-u":"#48c1ac","3268-u":"#28b29a","3278-u":"#009c82","3288-u":"#208776","3298-u":"#387469","3308-u":"#48615b","566-u":"#b4e1d6","565-u":"#91d1c5","564-u":"#88c6bc","563-u":"#76b9af","562-u":"#43807a","561-u":"#466d69","560-u":"#4b5e5b","573-u":"#bbe9de","572-u":"#95decf","571-u":"#85d8c8","570-u":"#62c2b1","569-u":"#2c8f82","568-u":"#458075","567-u":"#4a635d","559-u":"#b5d1c0","558-u":"#9ac0ae","557-u":"#88b29f","556-u":"#719e8b","555-u":"#567e6a","554-u":"#577565","553-u":"#5a685d","5595-u":"#c1d2c7","5585-u":"#a2b9af","5575-u":"#9db4ab","5565-u":"#8aa29a","5555-u":"#768e87","5545-u":"#60766f","5535-u":"#485956","5665-u":"#bcc8bd","5655-u":"#b3c0b6","5645-u":"#9fafa5","5635-u":"#8d9d94","5625-u":"#798981","5615-u":"#6a7970","5605-u":"#5e695e","5527-u":"#b8c7c4","5517-u":"#a9bab7","5507-u":"#96a9a7","5497-u":"#819595","5487-u":"#697c7d","5477-u":"#5f7070","5467-u":"#545f5f","621-u":"#cadfd4","622-u":"#b3cbc1","623-u":"#96b5ab","624-u":"#82a198","625-u":"#6c8a82","626-u":"#59756f","627-u":"#455d58","331-u":"#93e8d3","332-u":"#70e2cb","333-u":"#00d2b3","334-u":"#00957e","335-u":"#247e6f","336-u":"#3a7267","337-u":"#82d5ba","338-u":"#4cc1a1","339-u":"#22ae8a","340-u":"#009e77","341-u":"#347963","342-u":"#407060","343-u":"#48655b","7723-u":"#6bac92","7724-u":"#4e9b80","7725-u":"#379272","7726-u":"#348569","7727-u":"#3e7c65","7728-u":"#457865","7729-u":"#486f67","3375-u":"#5fe0b7","3385-u":"#3ad4a7","3395-u":"#00c795","3405-u":"#00b27a","3415-u":"#308167","3425-u":"#417764","3435-u":"#4b675b","344-u":"#8ed8ac","345-u":"#6ecd9c","346-u":"#4ebd88","347-u":"#169b62","348-u":"#34855b","349-u":"#477258","350-u":"#546758","351-u":"#93e6b4","352-u":"#70e0a3","353-u":"#50d691","354-u":"#00a95c","355-u":"#19975d","356-u":"#397e58","357-u":"#516e5a","7478-u":"#a0eac5","7479-u":"#29cb7e","7480-u":"#00c281","7481-u":"#00ba6e","7482-u":"#27a770","7483-u":"#587762","7484-u":"#456e60","7730-u":"#689c7a","7731-u":"#58926b","7732-u":"#498564","7733-u":"#4b7c63","7734-u":"#597765","7735-u":"#5f7163","7736-u":"#5c6b62","7737-u":"#7bac66","7738-u":"#65a969","7739-u":"#5ca369","7740-u":"#5e9865","7741-u":"#659267","7742-u":"#688564","7743-u":"#5e745d","358-u":"#97d88a","359-u":"#7dcc77","360-u":"#5db860","361-u":"#4daa50","362-u":"#56944f","363-u":"#57854e","364-u":"#5b794e","7485-u":"#d5e4c0","7486-u":"#b7e394","7487-u":"#8be076","7488-u":"#63d05a","7489-u":"#7aab6d","7490-u":"#7b9e6b","7491-u":"#818c61","365-u":"#c2e692","366-u":"#a8dc7d","367-u":"#8fd16a","368-u":"#67b346","369-u":"#62a044","370-u":"#698a48","371-u":"#68724d","372-u":"#d1f08f","373-u":"#b9e972","374-u":"#a6e35f","375-u":"#73c92d","376-u":"#70ab37","377-u":"#728f41","378-u":"#747b4d","580-u":"#c9dcac","579-u":"#bad29e","578-u":"#a7c58e","577-u":"#93b479","576-u":"#7a9b62","575-u":"#6f8456","574-u":"#6a7355","5807-u":"#d1d3a9","5797-u":"#c7ca9c","5787-u":"#babe8f","5777-u":"#a6a97b","5767-u":"#94986c","5757-u":"#81855c","5747-u":"#70744e","5875-u":"#d7d3a1","5865-u":"#ccc895","5855-u":"#bdb889","5845-u":"#a7a375","5835-u":"#938f66","5825-u":"#8b865d","5815-u":"#736f4c","5803-u":"#cfd3b8","5793-u":"#c5caaf","5783-u":"#b5bb9f","5773-u":"#9aa184","5763-u":"#7e876d","5753-u":"#747c61","5743-u":"#636a51","7492-u":"#bcca87","7493-u":"#b7c194","7494-u":"#9eb091","7495-u":"#838e59","7496-u":"#758448","7497-u":"#8c8778","7498-u":"#777d64","7744-u":"#b9be56","7745-u":"#afb35d","7746-u":"#9b9f60","7747-u":"#909661","7748-u":"#878c62","7749-u":"#808363","7750-u":"#777a61","379-u":"#e7ee7b","380-u":"#d4e458","381-u":"#bad739","382-u":"#99c221","383-u":"#8e9a36","384-u":"#868b3c","385-u":"#7e7b4b","386-u":"#eff178","387-u":"#e3ed55","388-u":"#d5e739","389-u":"#bddc04","390-u":"#97a926","391-u":"#888a3b","392-u":"#7e7d41","587-u":"#eaec97","586-u":"#e7e988","585-u":"#e3e685","584-u":"#ced665","583-u":"#a6b340","582-u":"#919145","581-u":"#78754a","393-u":"#f8f283","394-u":"#f2ee56","395-u":"#edec3f","396-u":"#dbe200","397-u":"#a8aa31","398-u":"#959539","399-u":"#8a8537","3935-u":"#fcf389","3945-u":"#fcf05f","3955-u":"#faeb37","3965-u":"#f8e815","3975-u":"#b6a72d","3985-u":"#9c903f","3995-u":"#837b4a","600-u":"#f9f29d","601-u":"#f7ef8f","602-u":"#f7ee83","603-u":"#f3e564","604-u":"#eddc4d","605-u":"#e0cc39","606-u":"#ceb829","607-u":"#f3eaa1","608-u":"#f0e691","609-u":"#ebe081","610-u":"#e0d268","611-u":"#c8b94e","612-u":"#b7a73f","613-u":"#a29438","461-u":"#eee29f","460-u":"#e8d98b","459-u":"#ddcb77","458-u":"#ceb964","457-u":"#ad9844","456-u":"#908145","455-u":"#776f48","614-u":"#e1d9a1","615-u":"#d8d096","616-u":"#cdc588","617-u":"#bbb275","618-u":"#a69d62","619-u":"#968d54","620-u":"#877f49","7751-u":"#d1bd71","7752-u":"#d5b961","7753-u":"#c6aa5d","7754-u":"#a4915e","7755-u":"#90855f","7756-u":"#837d62","7757-u":"#7a755f","7758-u":"#dac958","7759-u":"#c8b852","7760-u":"#968f5e","7761-u":"#828162","7762-u":"#747a63","7763-u":"#717463","7764-u":"#6c6e60","7765-u":"#c0ba5e","7766-u":"#bbb35f","7767-u":"#b1a763","7768-u":"#958b62","7769-u":"#81775f","7770-u":"#777161","7771-u":"#67655a","4545-u":"#d6cca6","4535-u":"#cabe96","4525-u":"#c1b58c","4515-u":"#b1a57e","4505-u":"#a39771","4495-u":"#90845f","4485-u":"#7b6f4b","454-u":"#cac8ac","453-u":"#bcb999","452-u":"#a8a687","451-u":"#929172","450-u":"#7e785a","449-u":"#716b54","448-u":"#66614e","7499-u":"#faedbc","7500-u":"#e6d5ab","7501-u":"#decba5","7502-u":"#c7af87","7503-u":"#9d9272","7504-u":"#948272","7505-u":"#887566","468-u":"#e0ceaa","467-u":"#d2ba92","466-u":"#c1a67f","465-u":"#b0946f","464-u":"#9b7c56","463-u":"#856f51","462-u":"#70624d","7506-u":"#f9e1b7","7507-u":"#ffddaa","7508-u":"#e0b384","7509-u":"#d2a273","7510-u":"#be8b60","7511-u":"#a77750","7512-u":"#966b47","719-u":"#f2c6a2","720-u":"#ecba93","721-u":"#dfa880","722-u":"#ca916c","723-u":"#b9825f","724-u":"#a57350","725-u":"#946747","475-u":"#faccad","474-u":"#f6bd9d","473-u":"#eeab88","472-u":"#db9271","471-u":"#be7b54","470-u":"#a37052","469-u":"#7b604b","726-u":"#e8c6a9","727-u":"#dcb596","728-u":"#caa182","729-u":"#bb9275","730-u":"#a57e62","731-u":"#957156","732-u":"#88674d","4685-u":"#e6cab4","4675-u":"#dab9a1","4665-u":"#caa58e","4655-u":"#b28e7b","4645-u":"#a07d6b","4635-u":"#92715e","4625-u":"#715845","7513-u":"#e7bbb4","7514-u":"#daab9c","7515-u":"#c2907f","7516-u":"#a07462","7517-u":"#936957","7518-u":"#817172","7519-u":"#756b66","4755-u":"#dcc6bb","4745-u":"#ceb4a9","4735-u":"#bda195","4725-u":"#ae9188","4715-u":"#997d73","4705-u":"#8b6f66","4695-u":"#775c50","482-u":"#dfc8b7","481-u":"#d3b7a7","480-u":"#c3a492","479-u":"#ac8c7e","478-u":"#8e6e5c","477-u":"#7d6556","476-u":"#6e5e52","7527-u":"#dcd7d2","7528-u":"#d2c7c4","7529-u":"#bbafa9","7530-u":"#a99c95","7531-u":"#8b7f79","7532-u":"#786e66","7533-u":"#6e645c","7534-u":"#d0cbc3","7535-u":"#c0bab0","7536-u":"#afa89d","7537-u":"#b4b8b6","7538-u":"#a5aaa8","7539-u":"#9c9ea0","7540-u":"#70747c","427-u":"#c2c7ca","428-u":"#acb3b8","429-u":"#91999f","430-u":"#808991","431-u":"#737b82","432-u":"#676c73","433-u":"#5b5d62","420-u":"#bdbebd","421-u":"#b3b5b5","422-u":"#9ea1a2","423-u":"#909496","424-u":"#88898a","425-u":"#7c7e7f","426-u":"#686869","441-u":"#b9c2c1","442-u":"#acb6b6","443-u":"#8f9a9b","444-u":"#7e8689","445-u":"#6b7173","446-u":"#636968","447-u":"#5f625f","413-u":"#bcbdb4","414-u":"#abaca5","415-u":"#9d9f99","416-u":"#91928c","417-u":"#82847f","418-u":"#7a7b75","419-u":"#595955","400-u":"#c7c1ba","401-u":"#b4aea7","402-u":"#a39e98","403-u":"#928d88","404-u":"#8b8681","405-u":"#77726d","406-u":"#c7beba","407-u":"#aba2a0","408-u":"#9d9594","409-u":"#948b8a","410-u":"#8a8280","411-u":"#7a726f","412-u":"#645c59","434-u":"#d6cacb","435-u":"#b7a9ad","436-u":"#a99b9f","437-u":"#8d7f83","438-u":"#817275","439-u":"#736869","440-u":"#69615f","warm-gray-1-u":"#dbd5cd","warm-gray-2-u":"#cfc7c0","warm-gray-3-u":"#beb6af","warm-gray-4-u":"#b4aca6","warm-gray-5-u":"#a9a19c","warm-gray-6-u":"#a29b96","warm-gray-7-u":"#968f8b","warm-gray-8-u":"#8f8884","warm-gray-9-u":"#867f7c","warm-gray-10-u":"#827c78","warm-gray-11-u":"#7d7773","cool-gray-1-u":"#dad9d6","cool-gray-2-u":"#cacac8","cool-gray-3-u":"#c5c5c5","cool-gray-4-u":"#b7b8b9","cool-gray-5-u":"#adaeb0","cool-gray-6-u":"#a4a6a8","cool-gray-7-u":"#9b9ea0","cool-gray-8-u":"#939598","cool-gray-9-u":"#898b8e","cool-gray-10-u":"#818387","cool-gray-11-u":"#7a7d81","black-2-u":"#646052","black-3-u":"#595d59","black-4-u":"#696056","black-5-u":"#685a5c","black-6-u":"#4c4e56","black-7-u":"#6c6864","yellow-u":"#ffe800","yellow-012-u":"#ffe000","orange-021-u":"#ff6c2f","warm-red-u":"#ff665e","red-032-u":"#f65058","rubine-red-u":"#db487e","rhodamine-red-u":"#e44c9a","purple-u":"#bf53b6","violet-u":"#7758b3","blue-072-u":"#3f43ad","reflex-blue-u":"#39499c","process-blue-u":"#0083c3","green-u":"#00ac8c","black-u":"#615d59","yellow-0131-u":"#fbf59b","red-0331-u":"#ffb1be","magenta-0521-u":"#f8aadd","violet-0631-u":"#ba93df","blue-0821-u":"#6cd1ef","green-0921-u":"#78e6d0","black-0961-u":"#9d9994","801-u":"#009ccd","802-u":"#3bd23d","803-u":"#ffe916","804-u":"#ffaa52","805-u":"#ff7477","806-u":"#ff48b0","807-u":"#e838bf","871-u":"#ad986e","872-u":"#ac936e","873-u":"#ae906f","874-u":"#ae8f6f","875-u":"#b18b74","876-u":"#b08770","877-u":"#b4b7b9","medium-yellow-u":"#ffe100","bright-orange-u":"#ff7133","bright-red-u":"#fe5442","strong-red-u":"#d23b6a","pink-u":"#d1428d","medium-purple-u":"#65428a","dark-blue-u":"#444795","medium-blue-u":"#0087c7","bright-green-u":"#00ae8f","neutral-black-u":"#4c4845","8001-c":"#8d8c8a","8002-c":"#8d867d","8003-c":"#8a7e70","8004-c":"#897966","8005-c":"#8a745a","8006-c":"#897054","8020-c":"#978176","8021-c":"#9a7a6a","8022-c":"#a0715b","8023-c":"#a1664c","8024-c":"#a75b38","8025-c":"#aa552a","8040-c":"#918887","8041-c":"#99817c","8042-c":"#9c7971","8043-c":"#a37063","8044-c":"#9f634f","8045-c":"#a35d44","8060-c":"#98828c","8061-c":"#9d7b88","8062-c":"#a2717f","8063-c":"#a76675","8064-c":"#a3646a","8065-c":"#a05d5d","8080-c":"#928294","8081-c":"#996a90","8082-c":"#9a5d8b","8083-c":"#9c4e85","8084-c":"#a24178","8085-c":"#a13769","8100-c":"#907695","8101-c":"#8f6992","8102-c":"#8f5a8f","8103-c":"#8f4c8c","8104-c":"#8e3b79","8105-c":"#8e336d","8120-c":"#8d8298","8121-c":"#8a7595","8122-c":"#886994","8123-c":"#885b94","8124-c":"#894e85","8125-c":"#87467a","8140-c":"#837698","8141-c":"#7e6a98","8142-c":"#795e99","8143-c":"#715096","8144-c":"#724f83","8145-c":"#704a79","8160-c":"#838495","8161-c":"#797997","8162-c":"#666197","8163-c":"#5b5498","8164-c":"#584990","8165-c":"#523e81","8180-c":"#7c8897","8181-c":"#5e789c","8182-c":"#4e6e9f","8183-c":"#3b62a1","8184-c":"#315995","8185-c":"#2b528c","8200-c":"#6d889a","8201-c":"#5f839c","8202-c":"#4c7e9f","8203-c":"#3177a3","8204-c":"#3f7590","8205-c":"#3d6f85","8220-c":"#6f8c9a","8221-c":"#5e8a9e","8222-c":"#4986a1","8223-c":"#2b80a1","8224-c":"#117993","8225-c":"#007287","8240-c":"#819098","8241-c":"#628e9c","8242-c":"#4c8b9b","8243-c":"#2a8a9f","8244-c":"#427f86","8245-c":"#3f7879","8260-c":"#728f98","8261-c":"#628b95","8262-c":"#4d8893","8263-c":"#338490","8264-c":"#21797c","8265-c":"#1c6d6a","8280-c":"#7c9797","8281-c":"#709795","8282-c":"#609a94","8283-c":"#499c93","8284-c":"#589382","8285-c":"#558e77","8300-c":"#80968e","8301-c":"#78998b","8302-c":"#6a9a82","8303-c":"#549372","8304-c":"#618c68","8305-c":"#5f865d","8320-c":"#8a928c","8321-c":"#80967a","8322-c":"#78986e","8323-c":"#69935e","8324-c":"#75925b","8325-c":"#728c4f","8340-c":"#8d957d","8341-c":"#8b9570","8342-c":"#899762","8343-c":"#859952","8344-c":"#83913e","8345-c":"#828d2e","8360-c":"#939478","8361-c":"#92926b","8362-c":"#98955c","8363-c":"#a19b4b","8364-c":"#9f9146","8365-c":"#a28f3d","8380-c":"#919085","8381-c":"#969077","8382-c":"#9b8e68","8383-c":"#9d8958","8384-c":"#a48244","8385-c":"#a47c39","8400-c":"#818182","8401-c":"#767575","8402-c":"#696866","8403-c":"#5c5b59","8404-c":"#56504a","8405-c":"#524942","8420-c":"#918b8d","8421-c":"#7d797a","8422-c":"#746d6e","8423-c":"#696060","8424-c":"#625550","8425-c":"#5f4f49","8440-c":"#9a8387","8441-c":"#9e7b7d","8442-c":"#a27172","8443-c":"#aa6767","8444-c":"#ac5b53","8445-c":"#ad5345","8460-c":"#89838d","8461-c":"#857784","8462-c":"#7f6a7a","8463-c":"#775e6e","8464-c":"#74535f","8465-c":"#714c56","8480-c":"#456580","8481-c":"#4c607e","8482-c":"#355772","8483-c":"#1f486b","8484-c":"#255682","8485-c":"#2c5d8a","8500-c":"#707f7c","8501-c":"#5e7d75","8502-c":"#4b646b","8503-c":"#33414c","8504-c":"#374e5f","8505-c":"#3c566b","8520-c":"#78765d","8521-c":"#555849","8522-c":"#464f45","8523-c":"#35413a","8524-c":"#3c4c4a","8525-c":"#435555","8540-c":"#834c4d","8541-c":"#6d4547","8542-c":"#633238","8543-c":"#603447","8544-c":"#643e54","8545-c":"#604355","8560-c":"#7f5649","8561-c":"#794a4a","8562-c":"#713f44","8563-c":"#70433c","8564-c":"#784f4e","8565-c":"#785556","8580-c":"#936d41","8581-c":"#845e3b","8582-c":"#7e5633","8583-c":"#6e4627","8584-c":"#6b4930","8585-c":"#76553d","8600-c":"#5d5a59","8601-c":"#504847","8602-c":"#423f44","8603-c":"#34343e","8604-c":"#393f4e","8605-c":"#3f485a","8620-c":"#78634d","8621-c":"#695644","8622-c":"#594839","8623-c":"#4e443c","8624-c":"#443d36","8625-c":"#47423d","8640-c":"#9a7c38","8641-c":"#a3842c","8642-c":"#ac8b1a","8643-c":"#ac8700","8644-c":"#a68c24","8645-c":"#a08e35","8660-c":"#8d7535","8661-c":"#89791d","8662-c":"#857d1e","8663-c":"#7e810d","8664-c":"#838e2c","8665-c":"#83913b","8680-c":"#7d7847","8681-c":"#787a38","8682-c":"#6e7f30","8683-c":"#658f31","8684-c":"#618a3b","8685-c":"#648d49","8700-c":"#72754d","8701-c":"#6a7b46","8702-c":"#5a7f44","8703-c":"#438240","8704-c":"#498853","8705-c":"#4e8a5e","8720-c":"#56735a","8721-c":"#5d7a5b","8722-c":"#46785a","8723-c":"#2e8661","8724-c":"#379277","8725-c":"#3d937e","8740-c":"#4d6c63","8741-c":"#346e61","8742-c":"#356f65","8743-c":"#007171","8744-c":"#007a82","8745-c":"#167f8b","8760-c":"#2e6d70","8761-c":"#006673","8762-c":"#355e68","8763-c":"#06576f","8764-c":"#1a6280","8765-c":"#26698a","8780-c":"#463f5f","8781-c":"#3c4363","8782-c":"#3a3c7b","8783-c":"#2a3a6e","8784-c":"#2f457c","8785-c":"#334e8a","8800-c":"#5b395f","8801-c":"#553b5f","8802-c":"#472d60","8803-c":"#4e2b60","8804-c":"#543873","8805-c":"#58417d","8820-c":"#86415c","8821-c":"#85335f","8822-c":"#6a375c","8823-c":"#622a65","8824-c":"#663775","8825-c":"#6a4182","8840-c":"#8b4153","8841-c":"#8b3354","8842-c":"#8d225b","8843-c":"#842263","8844-c":"#843072","8845-c":"#843d7d","8860-c":"#9a5051","8861-c":"#93434e","8862-c":"#95374b","8863-c":"#962742","8864-c":"#933554","8865-c":"#904061","8880-c":"#985459","8881-c":"#9d475b","8882-c":"#a33b5f","8883-c":"#aa3e50","8884-c":"#a7495e","8885-c":"#a24f65","8900-c":"#a45b43","8901-c":"#b65b49","8902-c":"#b85c5c","8903-c":"#bb4f51","8904-c":"#b75860","8905-c":"#b35f6b","8920-c":"#a0664e","8921-c":"#b35933","8922-c":"#b65317","8923-c":"#b74e23","8924-c":"#b25637","8925-c":"#ac5b43","8940-c":"#a16837","8941-c":"#ac662d","8942-c":"#ae5c1c","8943-c":"#c16014","8944-c":"#bb6428","8945-c":"#b76835","8960-c":"#966e32","8961-c":"#a26827","8962-c":"#b37300","8963-c":"#bf6d10","8964-c":"#aa6526","8965-c":"#a36934","9140-c":"#f2eeca","9284-c":"#fac9d1","9324-c":"#f3c9e4","9344-c":"#d1b6e4","9464-c":"#9fdded","9524-c":"#afeadc","9084-c":"#ada79e","9060-c":"#f1f0d8","9280-c":"#f5e2e2","9320-c":"#f2dee9","9340-c":"#e7dcea","9460-c":"#d5ebee","9520-c":"#d7efe7","9083-c":"#ccc8bf","9064-c":"#f1f1de","9285-c":"#f2ebe7","9061-c":"#f2e9ea","9345-c":"#eee9ea","9062-c":"#e6eeec","9063-c":"#e7efe7","9081-c":"#dfdcd4","9043-c":"#e8e6df","9080-c":"#e4e2db","9100-c":"#e7e7e0","9101-c":"#e3e3dd","9102-c":"#d9dbd5","9103-c":"#d5d8d2","9082-c":"#d2cdc3","9121-c":"#f1f0b0","9120-c":"#f1efb7","9141-c":"#f2edc0","9142-c":"#e9e4c5","9161-c":"#e7dbb9","9143-c":"#e3dfc3","9162-c":"#e2d9c2","9160-c":"#f4e5c0","9180-c":"#f3e4cd","9181-c":"#f4e3c9","9200-c":"#f5e1cc","9201-c":"#f5e3cc","9220-c":"#f5e0cf","9221-c":"#edd6c4","9184-c":"#efe4ce","9185-c":"#ede1cf","9202-c":"#eddecd","9182-c":"#ecdcc8","9183-c":"#e5d7c5","9203-c":"#e4d5c4","9186-c":"#e1d3c4","9224-c":"#f2e9db","9225-c":"#f0e6d8","9226-c":"#ebe3d7","9222-c":"#e6d5c8","9223-c":"#e1d3c7","9242-c":"#e9d5cd","9243-c":"#e1cec6","9244-c":"#f3e9e2","9240-c":"#f4e5de","9260-c":"#f5deda","9241-c":"#f0ddd7","9261-c":"#efd7d4","9262-c":"#ead5d2","9263-c":"#e4d1cd","9286-c":"#f6dfe0","9281-c":"#f2ddde","9300-c":"#f4dde2","9282-c":"#efdadb","9301-c":"#f0dbe1","9321-c":"#edd5e2","9302-c":"#e9ced5","9021-c":"#f1e7e4","9022-c":"#f0e7e9","9023-c":"#eae3ea","9283-c":"#e9d8d7","9303-c":"#e7d2d6","9322-c":"#e9d0dc","9323-c":"#e2ccd6","9360-c":"#ded8eb","9341-c":"#e3d7e6","9342-c":"#e0d4e2","9343-c":"#ded1e1","9361-c":"#dcd3e7","9362-c":"#dcd4e5","9363-c":"#d1c7d9","9380-c":"#dee1ec","9384-c":"#dadfec","9381-c":"#d2d9e8","9382-c":"#cdd2e6","9383-c":"#d1d5e3","9385-c":"#ced5df","9403-c":"#ced9e4","9400-c":"#d6e1ec","9401-c":"#d5e1ed","9402-c":"#d5e0ea","9420-c":"#d1e2ec","9421-c":"#cedfe9","9422-c":"#cad9e3","9423-c":"#c0d1da","9424-c":"#c3e3e7","9440-c":"#c1e0ea","9441-c":"#c0e0ec","9442-c":"#bedde7","9461-c":"#bee2e7","9462-c":"#b9dbe0","9443-c":"#b9d5de","9480-c":"#c8e9e9","9481-c":"#c5e9ea","9500-c":"#bee9e7","9482-c":"#c1e3e4","9501-c":"#b9e0dd","9483-c":"#c6dcda","9463-c":"#bddadd","9042-c":"#e3e8e3","9041-c":"#dfece4","9040-c":"#dcebec","9044-c":"#c9e2e0","9045-c":"#c8deda","9503-c":"#cbdeda","9504-c":"#c6dad7","9525-c":"#e5efe2","9540-c":"#e1f0e5","9541-c":"#dfece2","9521-c":"#d5e9e1","9522-c":"#d6e6df","9523-c":"#d6e3dc","9502-c":"#cde3e0","9544-c":"#e5efda","9560-c":"#dfefdb","9561-c":"#d7e8d2","9562-c":"#d6e2cf","9563-c":"#d5ddce","9542-c":"#cddacd","9543-c":"#c7d7c9","9020-c":"#eeeec8","9600-c":"#e7edc8","9601-c":"#e5e8cd","9122-c":"#e4e2b7","9602-c":"#d9dbbd","9603-c":"#d9d9c3","9123-c":"#dcd9b9","9580-c":"#e1efce","9581-c":"#dee7cd","9584-c":"#dce1cb","9582-c":"#d7dbc8","9583-c":"#cdd2ba","9585-c":"#d7dbc6","9163-c":"#d8cfb9","901-c":"#00aada","902-c":"#8de971","903-c":"#fff36d","904-c":"#ffcd8a","905-c":"#ff9da7","906-c":"#ff7cd6","907-c":"#f469db","908-c":"#00afdb","909-c":"#a9ee8a","910-c":"#fdf38a","911-c":"#ffd9a4","912-c":"#ffacb6","913-c":"#ff92da","914-c":"#f57fe0","915-c":"#00bce3","916-c":"#b9efa3","917-c":"#f7f4a2","918-c":"#ffdfb4","919-c":"#ffc0c8","920-c":"#ffade3","921-c":"#f8a3e6","808-c":"#00b796","809-c":"#e3e829","810-c":"#ffd635","811-c":"#ff8f6c","812-c":"#ff5fa2","813-c":"#fb48c4","814-c":"#8a69d4","922-c":"#00c0a3","923-c":"#ecf166","924-c":"#ffe671","925-c":"#ffad93","926-c":"#ff85bd","927-c":"#fc76d9","928-c":"#997adb","929-c":"#2bc9af","930-c":"#eef386","931-c":"#ffe983","932-c":"#ffbba4","933-c":"#ff9bcb","934-c":"#fc8de0","935-c":"#ab92e1","936-c":"#6dd5c3","937-c":"#eff4a4","938-c":"#fceea8","939-c":"#ffcfbe","940-c":"#ffb6d9","941-c":"#fca9e6","942-c":"#c0aee7","skin-1":"#ffdfc4","skin-2":"#f0d5be","skin-3":"#eeceb3","skin-4":"#e1b899","skin-5":"#e5c298","skin-6":"#ffdcb2","skin-7":"#e5b887","skin-8":"#e5a073","skin-9":"#e79e6d","skin-10":"#db9065","skin-11":"#ce967c","skin-12":"#c67856","skin-13":"#ba6c49","skin-14":"#a57257","skin-15":"#f0c8c9","skin-16":"#dda8a0","skin-17":"#b97c6d","skin-18":"#a8756c","skin-19":"#ad6452","skin-20":"#5c3836","skin-21":"#cb8442","skin-22":"#bd723c","skin-23":"#704139","skin-24":"#a3866a","skin-25":"#870400","skin-26":"#710101","skin-27":"#430000","skin-28":"#5b0001","skin-29":"#302e2e","skin-30":"#000000","cerulean-15-4020":"#9bb7d6","fuscia-rose-17-2031":"#c94476","true-red-19-1664":"#c02034","aqua-sky-14-4811":"#7ac5c5","tigerlily-17-1456":"#e4583e","blue-turquoise-15-5217":"#4fb0ae","sand-dollar-13-1106":"#decdbf","chili-pepper-19-1557":"#9c1b31","blue-iris-18-3943":"#595ca1","mimosa-14-0848":"#f0bf59","turquoise-15-5519":"#41b6ab","honeysuckle-18-2120":"#da4f70","tangerine-tango-17-1463":"#f05442","emerald-17-5641":"#169c78","radiant-orchid-18-3224":"#b565a7"});

  function normalisePantoneLookupKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/(?:pantone|pms)/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-+/g, "-");
  }

  function lookupPantonePreviewColour(value) {
    const key = normalisePantoneLookupKey(value);
    if (!key) return "";

    const candidates = [key];
    const compactSuffix = key.match(/^(.+?)([cu])$/);
    if (compactSuffix) candidates.push(`${compactSuffix[1]}-${compactSuffix[2]}`);
    if (!/(?:^|-)[cu]$/.test(key)) candidates.push(`${key}-c`);

    for (const candidate of candidates) {
      const colour = ADLOOP_PANTONE_TO_HEX[candidate];
      if (colour) return colour;
    }
    return "";
  }

  function getVisibleChoices(field, state = null, variantOptionsPayload = null, variantId = null) {
    let choices = getConfiguredChoices(field, state);

    const alwaysAllowed = Array.isArray(field?.allowedValues) ? field.allowedValues : [];
    if (alwaysAllowed.length) {
      choices = choices.filter((choice) =>
        alwaysAllowed.some((value) => norm(value) === norm(choiceValue(choice)))
      );
    }

    if (isAssemblyFieldDef(field) && !isPrintsOnlyAvailableForSelectedVariant()) {
      choices = choices.filter((choice) => !isPrintsOnlyAssemblyChoice(choiceValue(choice)));
    }

    const rules = Array.isArray(field?.choiceRules) ? field.choiceRules : [];
    rules.forEach((rule) => {
      if (!choiceRuleMatches(rule, state, variantOptionsPayload, variantId)) return;

      const allowed = Array.isArray(rule.allowedValues) ? rule.allowedValues : [];
      if (!allowed.length) return;
      choices = choices.filter((choice) =>
        allowed.some((value) => norm(value) === norm(choiceValue(choice)))
      );
    });

    return choices;
  }

  function clearUnavailableChoiceSelection(field, state, variantOptionsPayload, variantId) {
    const visibleChoices = getVisibleChoices(field, state, variantOptionsPayload, variantId);
    const current = (state?.values?.[field.key] ?? "").toString();
    const currentIsVisible = visibleChoices.some(
      (choice) => norm(choiceValue(choice)) === norm(current)
    );

    if (current && !currentIsVisible) {
      state.values[field.key] = "";
    }

    // Rules that leave only one valid answer should not make the customer
    // select the only possible value manually.
    if (!state.values[field.key] && visibleChoices.length === 1 && Array.isArray(field?.choiceRules)) {
      state.values[field.key] = choiceValue(visibleChoices[0]);
    }
  }

  function isPrintsOnlyHelpLine(line) {
    const txt = norm(line).replace(/[–—]/g, "-");
    return txt.includes("prints only") || txt.includes("no blank products supplied");
  }

  function clearUnavailablePrintsOnlySelection(field, state) {
    if (!isAssemblyFieldDef(field)) return;
    if (isPrintsOnlyAvailableForSelectedVariant()) return;

    const current = state?.values?.[field.key];
    if (!isPrintsOnlyAssemblyChoice(current)) return;

    const visibleChoices = getVisibleChoices(field);
    const defaultValue = field?.default != null ? String(field.default) : "";
    const defaultStillVisible = visibleChoices.some(
      (choice) => norm(choiceValue(choice)) === norm(defaultValue)
    );

    if (defaultStillVisible && !isPrintsOnlyAssemblyChoice(defaultValue)) {
      state.values[field.key] = defaultValue;
      return;
    }

    const assembledChoice = visibleChoices.find((choice) => {
      const txt = norm(choiceValue(choice));
      return (
        txt.includes("assemble") &&
        !txt.includes("unassembled") &&
        !isPrintsOnlyAssemblyChoice(txt)
      );
    });

    state.values[field.key] = assembledChoice ? choiceValue(assembledChoice) : "";
  }

  function truthyMetafieldValue(value) {
    if (value === true || value === 1) return true;
    const txt = norm(value);
    return txt === "true" || txt === "1" || txt === "yes";
  }

  function selectedVariantHasSampleRow() {
    const table = document.querySelector("discount-table");
    if (!table) return false;

    const rows = Array.from(table.querySelectorAll(".discount-table__table-row"));

    return rows.some((row) => {
      const dataQty = Number(row.getAttribute("data-qty") || "");
      if (dataQty === 1) return true;

      const firstCell = row.querySelector(".discount-table__table-cell:first-child");
      const match = (firstCell?.textContent || "").match(/\d+/);
      return match ? parseInt(match[0], 10) === 1 : false;
    });
  }

  function isSampleAvailableForSelectedVariant() {
    const variantId = getSelectedVariantId();
    const map = parseSampleAvailableMap();

    // Sample visibility must be controlled strictly by custom.sample_available.
    // Do not fall back to checking for a 1+ row, because some products use 1+
    // as a normal quantity break rather than a sample price.
    if (variantId && Object.prototype.hasOwnProperty.call(map, String(variantId))) {
      return truthyMetafieldValue(map[String(variantId)]);
    }

    return false;
  }

  function isExpressProductionFieldDef(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);
    return (
      key === "express_production" ||
      key === "production_speed" ||
      (field?.type === "select" && label.includes("production speed"))
    );
  }

  function isExpressProductionChoice(choice) {
    const value = typeof choice === "string" ? choice : (choice?.value ?? choice?.label ?? "");
    const label = typeof choice === "string" ? choice : (choice?.label ?? choice?.value ?? "");
    const txt = norm(`${value} ${label}`);
    return txt.includes("express production") || txt.includes("express");
  }

  function parseExpressProductionAvailableMap() {
    const el = document.querySelector("[data-intaprint-express-production-available-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function parseExpressProductionChargeMap() {
    const el = document.querySelector("[data-intaprint-express-production-charge-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function parseExpressProductionRulesMap() {
    const el = document.querySelector("[data-intaprint-express-production-rules-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function parseExpressProductionMaxQtyMap() {
    const el = document.querySelector("[data-intaprint-express-production-max-qty-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function parseExpressProductionMinWorkingDaysMap() {
    const el = document.querySelector("[data-intaprint-express-production-min-working-days-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function normalizeExpressProductionRules(raw) {
    let parsed = raw;

    // Shopify JSON metafields normally arrive as objects, but parse quoted JSON too.
    for (let i = 0; i < 2 && typeof parsed === "string"; i += 1) {
      const txt = parsed.trim();
      if (!txt) return null;
      try { parsed = JSON.parse(txt); }
      catch (e) { return null; }
    }

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

    const rawMinWorkingDays =
      parsed.min_working_days ??
      parsed.minimum_working_days ??
      parsed.express_min_working_days;

    const minNumber = Number(rawMinWorkingDays);
    const minWorkingDays = Number.isFinite(minNumber) && minNumber >= 0
      ? Math.floor(minNumber)
      : null;

    const rawTiers = Array.isArray(parsed.tiers)
      ? parsed.tiers
      : (Array.isArray(parsed.rules) ? parsed.rules : []);

    const tiers = rawTiers.map((tier) => {
      if (!tier || typeof tier !== "object") return null;

      const rawDays =
        tier.additional_working_days ??
        tier.working_days ??
        tier.min_working_days ??
        tier.days;
      const rawMaxQty = tier.max_qty ?? tier.maximum_qty ?? tier.qty;

      const days = Number(rawDays);
      const maxQty = Number(rawMaxQty);

      if (!Number.isFinite(days) || days < 0 || !Number.isFinite(maxQty) || maxQty <= 0) {
        return null;
      }

      return {
        workingDays: Math.floor(days),
        maxQty: Math.floor(maxQty)
      };
    }).filter(Boolean).sort((a, b) => a.workingDays - b.workingDays);

    if (minWorkingDays == null && !tiers.length) return null;
    return { minWorkingDays, tiers };
  }

  function getExpressProductionRulesFromMap(variantId) {
    if (!variantId) return null;
    const map = parseExpressProductionRulesMap();
    return normalizeExpressProductionRules(map[String(variantId)]);
  }

  function getLegacyExpressProductionMaxQtyFromMap(variantId) {
    if (!variantId) return null;
    const map = parseExpressProductionMaxQtyMap();
    const raw = map[String(variantId)];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }

  function getLegacyExpressProductionMinWorkingDaysFromMap(variantId) {
    if (!variantId) return 0;
    const map = parseExpressProductionMinWorkingDaysMap();
    const raw = map[String(variantId)];
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }

  function getExpressProductionMinWorkingDaysFromMap(variantId) {
    const rules = getExpressProductionRulesFromMap(variantId);
    if (rules && rules.minWorkingDays != null) return rules.minWorkingDays;
    return getLegacyExpressProductionMinWorkingDaysFromMap(variantId);
  }

  function getExpressProductionMinWorkingDays() {
    return getExpressProductionMinWorkingDaysFromMap(getSelectedVariantId());
  }

  function getInitialExpressProductionMaxQty(variantId) {
    const rules = getExpressProductionRulesFromMap(variantId);
    if (rules?.tiers?.length) return rules.tiers[0].maxQty;
    return getLegacyExpressProductionMaxQtyFromMap(variantId);
  }

  function getExpressProductionMaxQty(state = null) {
    const dynamic = Number(state?.expressProductionDynamicMaxQty);
    if (Number.isFinite(dynamic) && dynamic > 0) return Math.floor(dynamic);
    return getInitialExpressProductionMaxQty(getSelectedVariantId());
  }

  const expressProductionTierDateCache = new Map();

  async function getExpressProductionTierDates(variantId, earliestIso) {
    const rules = getExpressProductionRulesFromMap(variantId);
    if (!rules?.tiers?.length || !earliestIso) return [];

    const cacheKey = `${variantId}|${earliestIso}|${JSON.stringify(rules.tiers)}`;
    if (expressProductionTierDateCache.has(cacheKey)) {
      return expressProductionTierDateCache.get(cacheKey);
    }

    const request = Promise.all(rules.tiers.map(async (tier) => {
      const fallback = addWorkingDays(dateFromIsoDate(earliestIso), tier.workingDays);
      const json = await callWorkingDaysService("/add-working-days", {
        startDate: earliestIso,
        days: tier.workingDays
      });
      const thresholdDate = getDateFromWorkingDaysResponse(json) || fallback;
      return { ...tier, thresholdIso: toIsoDate(thresholdDate) };
    }));

    expressProductionTierDateCache.clear();
    expressProductionTierDateCache.set(cacheKey, request);
    return request;
  }

  async function refreshExpressProductionMaxQtyForState(state) {
    const variantId = getSelectedVariantId();
    const rules = getExpressProductionRulesFromMap(variantId);

    if (!isExpressProductionSelected(state)) {
      state.expressProductionDynamicMaxQty = null;
      return null;
    }

    if (!rules?.tiers?.length) {
      const fallback = getLegacyExpressProductionMaxQtyFromMap(variantId);
      state.expressProductionDynamicMaxQty = fallback;
      return fallback;
    }

    const earliest = await getEarliestExpressProductionDateAsync();
    const earliestIso = toIsoDate(earliest);
    const selectedIso = String(state?.values?.express_production_date || earliestIso);
    const tierDates = await getExpressProductionTierDates(variantId, earliestIso);

    let selectedTier = tierDates[0] || null;
    tierDates.forEach((tier) => {
      if (tier.thresholdIso <= selectedIso) selectedTier = tier;
    });

    const maxQty = selectedTier?.maxQty || rules.tiers[0].maxQty;
    state.expressProductionDynamicMaxQty = maxQty;
    return maxQty;
  }

  function getExpressProductionCutOffHour() {
    const el = document.querySelector("[data-intaprint-express-production-cut-off-hour]");
    if (!el) return 12;

    let raw = "";
    try {
      raw = JSON.parse((el.textContent || "12").trim());
    } catch (e) {
      raw = (el.textContent || "12").trim();
    }

    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 23 ? Math.floor(n) : 12;
  }

  function pad2(n) {
    return String(n).padStart(2, "0");
  }

  function toIsoDate(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  }

  function dateFromIsoDate(value) {
    const parts = String(value || "").split("-").map((x) => Number(x));
    if (parts.length !== 3 || parts.some((x) => !Number.isFinite(x))) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function isWorkingDay(date) {
    // Local fallback only. The shared Cloudflare working-day service is the source of truth
    // when configured, because it also excludes England & Wales bank holidays.
    const day = date.getDay();
    return day !== 0 && day !== 6;
  }

  function nextWorkingDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    do {
      d.setDate(d.getDate() + 1);
    } while (!isWorkingDay(d));
    return d;
  }

  function addWorkingDays(date, days) {
    let d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let remaining = Math.max(0, Number(days) || 0);

    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      if (isWorkingDay(d)) remaining -= 1;
    }

    if (!isWorkingDay(d)) {
      d = nextWorkingDay(d);
    }

    return d;
  }

  function getEarliestExpressProductionDate() {
    const now = new Date();
    const cutOffHour = getExpressProductionCutOffHour();
    const minWorkingDays = getExpressProductionMinWorkingDays();

    let start = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (!isWorkingDay(start) || now.getHours() >= cutOffHour) {
      start = nextWorkingDay(start);
    }

    return addWorkingDays(start, minWorkingDays);
  }

  function getWorkingDaysEndpoint() {
    const cleanEndpoint = (value) => {
      let raw = String(value || "").trim();
      if (!raw) return "";

      // The Liquid snippet stores this as application/json, so the text content
      // may be a quoted JSON string. Parse it first so the Worker URL is usable.
      try {
        const parsed = JSON.parse(raw);
        if (parsed) raw = String(parsed).trim();
      } catch (e) {}

      if (!raw) return "";
      if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;

      return raw.replace(/\/+$/, "");
    };

    const fromWindow = cleanEndpoint(window.IntaprintWorkingDaysEndpoint || "");
    if (fromWindow) return fromWindow;

    const el =
      document.querySelector("[data-intaprint-working-days-endpoint]") ||
      document.querySelector("input[name='intaprint-working-days-endpoint']");

    const raw = el?.value || el?.dataset?.intaprintWorkingDaysEndpoint || el?.textContent || "";
    return cleanEndpoint(raw);
  }

  function normalizeShopClosedDateItems(value) {
    if (value == null || value === "") return [];

    let parsed = value;

    // The Liquid script can contain either a real JSON array/object or a quoted JSON string.
    // Parse up to two times so both formats work safely.
    for (let i = 0; i < 2; i += 1) {
      if (typeof parsed !== "string") break;
      const txt = parsed.trim();
      if (!txt) return [];
      try { parsed = JSON.parse(txt); }
      catch (e) { break; }
    }

    const out = [];
    const addDate = (date) => {
      const clean = String(date || "").trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) out.push(clean);
    };

    if (Array.isArray(parsed)) {
      parsed.forEach((item) => {
        if (typeof item === "string") {
          addDate(item);
          return;
        }

        if (item && typeof item === "object") {
          const start = String(item.start || "").trim();
          const end = String(item.end || "").trim();
          const date = String(item.date || "").trim();

          if (date) addDate(date);
          else if (/^\d{4}-\d{2}-\d{2}$/.test(start) && /^\d{4}-\d{2}-\d{2}$/.test(end)) {
            out.push({ start, end, reason: String(item.reason || "Intaprint closure").trim() });
          }
        }
      });
    } else if (parsed && typeof parsed === "object") {
      if (Array.isArray(parsed.dates)) {
        parsed.dates.forEach(addDate);
      }
      if (Array.isArray(parsed.ranges)) {
        parsed.ranges.forEach((range) => {
          const start = String(range?.start || "").trim();
          const end = String(range?.end || "").trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(start) && /^\d{4}-\d{2}-\d{2}$/.test(end)) {
            out.push({ start, end, reason: String(range.reason || "Intaprint closure").trim() });
          }
        });
      }
    } else if (typeof parsed === "string") {
      parsed.split(/[\n,]+/).forEach(addDate);
    }

    const seen = new Set();
    return out.filter((item) => {
      const key = typeof item === "string" ? item : `${item.start}|${item.end}|${item.reason || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function getShopClosedDates() {
    const el = document.querySelector("[data-intaprint-shop-closed-dates]");
    if (!el) return [];

    const raw = el.value || el.textContent || "";
    return normalizeShopClosedDateItems(raw);
  }

  async function callWorkingDaysService(path, params = {}) {
    const endpoint = getWorkingDaysEndpoint();
    if (!endpoint) return null;

    try {
      const url = new URL(endpoint + path);
      const requestParams = { ...params };
      const shopClosedDates = getShopClosedDates();

      if (shopClosedDates.length && requestParams.closedDates == null) {
        requestParams.closedDates = JSON.stringify(shopClosedDates);
      }

      Object.entries(requestParams).forEach(([key, value]) => {
        if (value != null && value !== "") url.searchParams.set(key, String(value));
      });

      const resp = await fetch(url.toString(), {
        method: "GET",
        headers: { "accept": "application/json" },
        credentials: "omit"
      });

      if (!resp.ok) throw new Error(`Working-day service failed (${resp.status})`);
      return await resp.json();
    } catch (e) {
      console.warn("Intaprint working-day service unavailable; using local fallback.", e);
      return null;
    }
  }

  const productDatesRequestCache = new Map();

  function getProductDatesCacheKey(params) {
    // Include the current minute so a page left open across a cut-off refreshes naturally,
    // while simultaneous Express and EDD calculations share one Worker request.
    const minuteBucket = Math.floor(Date.now() / 60000);
    return `${minuteBucket}|${JSON.stringify(params)}`;
  }

  function getProductDatesFromService(variantId) {
    const cfg = getOrderTimingConfig(variantId);
    const now = new Date();
    const params = {
      startDate: toIsoDate(now),
      expressMinWorkingDays: getExpressProductionMinWorkingDaysFromMap(variantId),
      expressCutOffHour: getExpressProductionCutOffHour(),
      dispatchDaysOffset: cfg.dispatchDaysOffset,
      dispatchCutOffHour: cfg.cutoffHour,
      dispatchCutOffMinute: cfg.cutoffMinute,
      dispatchDays: cfg.dispatchDays.join(",")
    };

    const key = getProductDatesCacheKey(params);
    if (productDatesRequestCache.has(key)) {
      return productDatesRequestCache.get(key);
    }

    // Keep the cache tiny by dropping previous minute/variant calculations.
    productDatesRequestCache.clear();

    const request = callWorkingDaysService("/product-dates", params)
      .finally(() => {
        // Keep the resolved Promise available for other calculations during this minute.
      });

    productDatesRequestCache.set(key, request);
    return request;
  }

  function getDateFromWorkingDaysResponse(json) {
    return dateFromIsoDate(json?.date || json?.earliestDate || json?.result?.date);
  }

  async function getEarliestExpressProductionDateAsync() {
    const fallback = getEarliestExpressProductionDate();
    const variantId = getSelectedVariantId();
    const json = await getProductDatesFromService(variantId);
    return dateFromIsoDate(json?.express?.earliestDate) || fallback;
  }

  async function getCorrectedWorkingDateAsync(dateIso) {
    const fallbackDate = dateFromIsoDate(dateIso) || getEarliestExpressProductionDate();
    const fallback = isWorkingDay(fallbackDate) ? fallbackDate : nextWorkingDay(fallbackDate);

    const json = await callWorkingDaysService("/add-working-days", {
      startDate: toIsoDate(fallbackDate),
      days: 0
    });

    return getDateFromWorkingDaysResponse(json) || fallback;
  }

  async function isWorkingDayIsoAsync(dateIso) {
    const date = dateFromIsoDate(dateIso);
    const fallback = !!date && isWorkingDay(date);

    const json = await callWorkingDaysService("/is-working-day", {
      date: dateIso
    });

    return typeof json?.isWorkingDay === "boolean" ? json.isWorkingDay : fallback;
  }

  function parseStandardProductionMinWorkingDaysMap() {
    const el = document.querySelector("[data-intaprint-standard-production-min-working-days-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function parseStandardProductionMaxWorkingDaysMap() {
    const el = document.querySelector("[data-intaprint-standard-production-max-working-days-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function getStandardProductionWorkingDayRange(variantId) {
    const minMap = parseStandardProductionMinWorkingDaysMap();
    const maxMap = parseStandardProductionMaxWorkingDaysMap();

    const rawMin = minMap?.[String(variantId)];
    const rawMax = maxMap?.[String(variantId)];

    let min = Number(rawMin);
    let max = Number(rawMax);

    if (!Number.isFinite(min) || min <= 0) min = 5;
    if (!Number.isFinite(max) || max <= 0) max = 15;

    min = Math.floor(min);
    max = Math.floor(max);

    if (max < min) max = min;

    return { min, max };
  }

  function isPrintedVariant(variantOptionsPayload, variantId) {
    return norm(getSelectedVariantType(variantOptionsPayload, variantId)).includes("printed");
  }

  function parseOrderTimingMap() {
    const el = document.querySelector("[data-intaprint-order-timing-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function getSelectedVariantType(variantOptionsPayload, variantId) {
    const names = variantOptionsPayload?.option_names || [];
    const values = variantOptionsPayload?.variants?.[String(variantId)] || [];
    const index = names.findIndex((name) => norm(name) === "type");
    return index >= 0 ? String(values[index] || "") : "";
  }

  function getOrderTimingConfig(variantId) {
    const map = parseOrderTimingMap();
    const cfg = map?.[String(variantId)] || {};

    const validNumberOrFallback = (value, fallback) => {
      if (value === null || value === undefined || value === "") return fallback;
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    return {
      cutoffHour: validNumberOrFallback(cfg.cutoff_hour, 12),
      cutoffMinute: validNumberOrFallback(cfg.cutoff_minute, 0),
      dispatchDaysOffset: validNumberOrFallback(cfg.dispatch_days_offset, 0),
      dispatchDays: Array.isArray(cfg.dispatch_days) && cfg.dispatch_days.length ? cfg.dispatch_days : [1,2,3,4,5],
      titleText: String(cfg.title_text || "Estimated dispatch"),
      dispatchText: String(cfg.dispatch_text || "Estimated dispatch:")
    };
  }

  function formatTime12(hour, minute = 0) {
    const date = new Date(2000, 0, 1, Number(hour) || 0, Number(minute) || 0);
    return date.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true });
  }

  function formatRelativeDay(dateIso) {
    const date = dateFromIsoDate(dateIso);
    if (!date) return "";
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.getTime() === today.getTime()) return "today";
    if (date.getTime() === tomorrow.getTime()) return "tomorrow";
    return `on ${date.toLocaleDateString("en-GB", { weekday:"long", day:"numeric", month:"long" })}`;
  }

  function buildCutOffDateTime(dateIso, hour, minute = 0) {
    const date = dateFromIsoDate(dateIso);
    if (!date) return null;
    date.setHours(Number(hour) || 0, Number(minute) || 0, 0, 0);
    return date;
  }

  function formatCountdown(deadline) {
    if (!(deadline instanceof Date) || Number.isNaN(deadline.getTime())) return "";

    const totalMinutes = Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
    if (hours > 0 || days > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);

    return parts.join(", ");
  }

  async function getEstimatedDispatchData(state, variantId) {
    const cfg = getOrderTimingConfig(variantId);
    const json = await getProductDatesFromService(variantId);

    if (isExpressProductionSelected(state) && state.values.express_production_date) {
      return {
        date: state.values.express_production_date,
        orderByDate: json?.express?.orderByDate || toIsoDate(new Date()),
        cutOffHour: json?.express?.cutOffHour ?? getExpressProductionCutOffHour(),
        cutOffMinute: 0,
        cutOffPassed: !!json?.express?.cutOffPassed,
        isExpress: true
      };
    }

    const dispatch = json?.estimatedDispatch;
    return dispatch ? {
      date: dispatch.dispatchDate,
      orderByDate: dispatch.orderByDate,
      cutOffHour: dispatch.cutOffHour ?? cfg.cutoffHour,
      cutOffMinute: dispatch.cutOffMinute ?? cfg.cutoffMinute,
      cutOffPassed: !!dispatch.cutOffPassed,
      isExpress: false
    } : null;
  }

  function renderEstimatedDispatchPanel(root, state, variantOptionsPayload, variantId) {
    // Do not show a normal dispatch promise for a variant that cannot currently
    // be ordered. Keep EDD visible for CONTINUE-selling pre-order variants.
    if (isVariantTrulySoldOut(variantId)) {
      const placeholder = document.createElement("div");
      placeholder.hidden = true;
      placeholder.setAttribute("data-intaprint-edd-panel", "1");
      placeholder.setAttribute("data-intaprint-edd-suppressed", "sold-out");
      return placeholder;
    }

    const cfg = getOrderTimingConfig(variantId);
    const standardPrinted = isPrintedVariant(variantOptionsPayload, variantId) && !isExpressProductionSelected(state);
    const standardRange = getStandardProductionWorkingDayRange(variantId);

    const panel = document.createElement("div");
    panel.className = "ipo-edd-panel";
    panel.setAttribute("data-intaprint-edd-panel", "1");

    const title = document.createElement("div");
    title.className = "ipo-edd-title";

    const titleText = document.createElement("span");
    titleText.textContent = standardPrinted
      ? "📦 Standard Production"
      : `📦 ${cfg.titleText || "Estimated dispatch"}`;

    title.append(titleText);

    const countdown = document.createElement("div");
    countdown.className = "ipo-edd-countdown ipo-edd-loading";
    countdown.innerHTML = `<span>⏳</span><span>Calculating…</span>`;

    const orderRow = document.createElement("div");
    orderRow.className = "ipo-edd-row ipo-edd-loading";
    orderRow.innerHTML = `<span class="ipo-edd-icon">🕒</span><span>Calculating order cut-off…</span>`;

    const dispatchRow = document.createElement("div");
    dispatchRow.className = "ipo-edd-row ipo-edd-loading";
    dispatchRow.innerHTML = `<span class="ipo-edd-icon">📅</span><span>Calculating estimated dispatch…</span>`;

    const deliveryRow = document.createElement("div");
    deliveryRow.className = "ipo-edd-row";
    deliveryRow.innerHTML = `<span class="ipo-edd-icon">🚚</span><span>Delivery date depends on the shipping method selected at checkout.</span>`;

    if (standardPrinted) {
      dispatchRow.classList.remove("ipo-edd-loading");
      dispatchRow.innerHTML = `<span class="ipo-edd-icon">📅</span><span>Estimated dispatch: <span class="ipo-edd-highlight">within ${standardRange.min}–${standardRange.max} working days</span></span>`;

      const standardNote = document.createElement("div");
      standardNote.className = "ipo-edd-standard-note";
      standardNote.textContent = "We'll always aim to dispatch your order as quickly as possible. Estimated dispatch dates may vary if artwork approval is delayed or during particularly busy periods.";

      panel.append(title, dispatchRow, standardNote);
      return panel;
    }

    panel.append(title, countdown, orderRow, dispatchRow, deliveryRow);

    let countdownTimer = null;
    const stopCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    };

    const requestVariant = String(variantId);
    getEstimatedDispatchData(state, variantId).then((data) => {
      if (!panel.isConnected || getSelectedVariantId() !== requestVariant || !data?.date) {
        stopCountdown();
        return;
      }

      const dispatchDate = dateFromIsoDate(data.date);
      const deadline = buildCutOffDateTime(data.orderByDate, data.cutOffHour, data.cutOffMinute);
      if (!dispatchDate || !deadline) return;

      orderRow.classList.remove("ipo-edd-loading");
      dispatchRow.classList.remove("ipo-edd-loading");
      countdown.classList.remove("ipo-edd-loading");

      const refreshCountdown = () => {
        if (!panel.isConnected || getSelectedVariantId() !== requestVariant) {
          stopCountdown();
          return;
        }

        const remainingMs = deadline.getTime() - Date.now();
        const text = formatCountdown(deadline);
        countdown.innerHTML = `<span>⏳</span><span>${text}</span>`;
        countdown.classList.toggle("ipo-edd-countdown--urgent", remainingMs > 0 && remainingMs <= 30 * 60 * 1000);

        if (remainingMs <= 0) {
          stopCountdown();
          renderFields(root, state._schema || { fields: [] }, state, variantOptionsPayload, variantId);
        }
      };

      refreshCountdown();
      countdownTimer = setInterval(refreshCountdown, 60000);

      const orderDay = formatRelativeDay(data.orderByDate);
      const cutOff = formatTime12(data.cutOffHour, data.cutOffMinute);

      if (data.isExpress) {
        orderRow.innerHTML = `<span class="ipo-edd-icon">🚀</span><span>Express Production selected · Order by: <span class="ipo-edd-highlight">${cutOff} ${orderDay}</span></span>`;
      } else {
        orderRow.innerHTML = `<span class="ipo-edd-icon">🕒</span><span>Order by: <span class="ipo-edd-highlight">${cutOff} ${orderDay}</span></span>`;
      }

      dispatchRow.innerHTML = `<span class="ipo-edd-icon">📅</span><span>${cfg.dispatchText} <span class="ipo-edd-highlight">${formatFriendlyDate(dispatchDate)}</span></span>`;
    });

    return panel;
  }

  function formatFriendlyDate(date) {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  }

  function formatCutOffTime(hour) {
    const h = Number(hour);
    const safeHour = Number.isFinite(h) && h >= 0 && h <= 23 ? Math.floor(h) : 12;
    return `${pad2(safeHour)}:00`;
  }

  function hasExpressCutOffPassedToday() {
    const now = new Date();
    return now.getHours() >= getExpressProductionCutOffHour();
  }

  function buildExpressProductionMessage(earliestDate) {
    const friendlyDate = formatFriendlyDate(earliestDate);
    const cutOffTime = formatCutOffTime(getExpressProductionCutOffHour());

    if (!hasExpressCutOffPassedToday()) {
      return `🚀 Order before <strong>${cutOffTime} today</strong> for Express Production from <strong>${friendlyDate}</strong>.`;
    }

    return `🚀 Today's Express Production cut-off has passed. Earliest available date is <strong>${friendlyDate}</strong>.`;
  }

  function getExpressProductionChargeFromMap(variantId) {
    if (!variantId) return 0;
    const map = parseExpressProductionChargeMap();
    const raw = map[String(variantId)];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function getExpressProductionChargeExcl() {
    return getExpressProductionChargeFromMap(getSelectedVariantId());
  }

  function isExpressProductionAvailableForSelectedVariant() {
    const variantId = getSelectedVariantId();
    const map = parseExpressProductionAvailableMap();

    if (variantId && Object.prototype.hasOwnProperty.call(map, String(variantId))) {
      return truthyMetafieldValue(map[String(variantId)]);
    }

    return false;
  }

  function isExpressProductionSelected(state) {
    const value = state?.values?.express_production || "";
    return norm(value).includes("express");
  }

  function getSelectedExpressProductionChargeExcl(state) {
    return isExpressProductionSelected(state) ? getExpressProductionChargeExcl() : 0;
  }

  function applyExpressProductionChargeToDiscountTable(state) {
    const isSelected = isExpressProductionSelected(state);
    const charge = isSelected ? getExpressProductionChargeExcl() : 0;
    const maxQty = isSelected ? getExpressProductionMaxQty(state) : null;

    if (charge > 0 || maxQty) {
      document.documentElement.setAttribute("data-intaprint-express-production-selected", "true");
    } else {
      document.documentElement.removeAttribute("data-intaprint-express-production-selected");
    }

    window.IntaprintExpressProductionCharge = charge;
    window.IntaprintExpressProductionMaxQty = maxQty;

    const table = document.querySelector("discount-table");
    if (table) {
      table.dataset.expressProductionCharge = String(charge || 0);
    }

    if (typeof window.IntaprintDiscountTableRefresh === "function") {
      window.IntaprintDiscountTableRefresh({ reason: "express-production-change" });
    }

    document.dispatchEvent(new CustomEvent("intaprint:express-production-change", {
      detail: { charge, maxQty, isSelected }
    }));
  }

  function isPackagingFieldDef(field) {
    return norm(field?.key) === "packaging" || norm(field?.label).includes("packag");
  }

  function isSequentialNumberingOptionFieldDef(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);
    return (
      key === "sequential_numbering_option" ||
      key === "sequential_numbering_charge" ||
      (field?.type === "select" && label.includes("sequential") && label.includes("number"))
    );
  }

  function isSequentialNumberingChoice(choice) {
    const value = typeof choice === "string" ? choice : (choice?.value ?? choice?.label ?? "");
    const label = typeof choice === "string" ? choice : (choice?.label ?? choice?.value ?? "");
    const txt = norm(`${value} ${label}`);
    if (/^no\b|no numbering|without|none|not required/.test(txt)) return false;
    return /add|yes|required|sequential|numbering|numbered/.test(txt);
  }

  function parseSequentialNumberingChargeMap() {
    const el = document.querySelector("[data-intaprint-sequential-numbering-charge-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function getSequentialNumberingChargeFromMap(variantId) {
    if (!variantId) return 0;
    const map = parseSequentialNumberingChargeMap();
    const raw = map[String(variantId)];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function getSequentialNumberingChargeExcl() {
    return getSequentialNumberingChargeFromMap(getSelectedVariantId());
  }

  // QR-code printing is available only for variants with a positive EX-VAT charge.
  function getIndividualQrChargeExcl() {
    const el = document.querySelector('[data-intaprint-individual-qr-code-charge-map]');
    if (!el) return 0;
    try {
      const map = JSON.parse((el.textContent || '{}').trim()) || {};
      const charge = Number(map[String(getSelectedVariantId())]);
      return Number.isFinite(charge) && charge > 0 ? charge : 0;
    } catch (e) { return 0; }
  }

  function isIndividualQrOptionFieldDef(field) {
    return String(field?.key || '') === 'individual_qr_codes_option';
  }


  function isUndersidePrintFieldDef(field) {
    const key = norm(field?.key);
    const label = norm(field?.label);

    // Colour selectors describe the artwork/product and must be preserved as
    // line-item properties. They are not the paid "print the underside"
    // yes/no option, even though their keys and labels contain "underside".
    if (
      key.includes("colour") ||
      key.includes("color") ||
      label.includes("colour") ||
      label.includes("color")
    ) {
      return false;
    }

    return (
      key === "print_sides" ||
      key === "underside_print" ||
      (field?.type === "select" && (
        label.includes("underside") ||
        label.includes("under side") ||
        (label.includes("top side") && label.includes("printed"))
      ))
    );
  }

  function isUndersidePrintChoice(choice) {
    const value = typeof choice === "string" ? choice : (choice?.value ?? choice?.label ?? "");
    const label = typeof choice === "string" ? choice : (choice?.label ?? choice?.value ?? "");
    const txt = norm(`${value} ${label}`);
    if (/top side only|top only|front only/.test(txt)) return false;
    return /underside|under side|top side and underside|both sides/.test(txt);
  }

  function parseUndersidePrintChargeMap() {
    const el = document.querySelector("[data-intaprint-underside-print-charge-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function getUndersidePrintChargeFromMap(variantId) {
    if (!variantId) return 0;
    const map = parseUndersidePrintChargeMap();
    const raw = map[String(variantId)];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function getUndersidePrintChargeExcl() {
    return getUndersidePrintChargeFromMap(getSelectedVariantId());
  }

  function getActiveCurrency() {
    return window.Shopify?.currency?.active || "GBP";
  }

  function getShopCurrency() {
    return "GBP";
  }

  function getCurrencyRate() {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  function convertChargeForDisplay(chargeExcl) {
    const amount = Number(chargeExcl || 0);
    if (!Number.isFinite(amount) || amount <= 0) return 0;

    return getActiveCurrency() === getShopCurrency()
      ? amount
      : amount * getCurrencyRate();
  }

  function formatOptionCharge(chargeExcl) {
    const converted = convertChargeForDisplay(chargeExcl);
    if (!(converted > 0)) return "";

    if (getActiveCurrency() === "GBP") {
      const pence = Math.round(converted * 100);
      return pence > 0 ? `${pence}p` : "";
    }

    return new Intl.NumberFormat(
      document.documentElement.lang || "en-GB",
      {
        style: "currency",
        currency: getActiveCurrency(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(converted);
  }

  function stripExistingChargeSuffix(label) {
    return String(label || "")
      .replace(
        /\s*\(\s*\+?(?:[£€$]\s*)?\d+(?:[.,]\d+)?\s*(?:p|each)?\s*each\s*\)\s*$/i,
        ""
      )
      .replace(
        /\s*\(\s*\+?\d+(?:[.,]\d+)?p\s+each\s*\)\s*$/i,
        ""
      )
      .trim();
  }

  function getIndividualPackagingChargeExcl() {
    // Prefer the all-variant map, so this works immediately after a variant change
    // even if Horizon has not morphed the discount table yet.
    const fromMap = getPackagingChargeFromMap(getSelectedVariantId());
    if (fromMap > 0) return fromMap;

    // Fallback for older templates that only expose the selected charge on the table.
    const table = document.querySelector("discount-table");
    const raw = table?.dataset?.individualPackagingCharge ?? "";
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function formatPackagingChargePence(chargeExcl) {
    return formatOptionCharge(chargeExcl);
  }

  function parsePackagingChargeMap() {
    const el = document.querySelector("[data-intaprint-packaging-charge-map]");
    if (!el) return {};
    try { return JSON.parse((el.textContent || "{}").trim()) || {}; }
    catch (e) { return {}; }
  }

  function getPackagingChargeFromMap(variantId) {
    if (!variantId) return 0;
    const map = parsePackagingChargeMap();
    const raw = map[String(variantId)];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function isPremiumPackagingChoice(choice) {
    const value = typeof choice === "string" ? choice : (choice?.value ?? choice?.label ?? "");
    const label = typeof choice === "string" ? choice : (choice?.label ?? choice?.value ?? "");
    const txt = norm(`${value} ${label}`);
    if (/standard|no individual|without|none|no packaging|no poly/.test(txt)) return false;
    return /premium|individual|polybag|bagged|bagging/.test(txt);
  }

  function getChoiceLabel(field, choice) {
    const originalLabel = (typeof choice === "string")
      ? choice
      : (choice?.label ?? choice?.value ?? "").toString();

    const label = stripExistingChargeSuffix(originalLabel);

    const addCharge = (charge) => {
      const formatted = formatOptionCharge(charge);
      return formatted ? `${label} (+${formatted} each)` : label;
    };

    if (isPackagingFieldDef(field) && isPremiumPackagingChoice(choice)) {
      return addCharge(getIndividualPackagingChargeExcl());
    }

    if (isSequentialNumberingOptionFieldDef(field) && isSequentialNumberingChoice(choice)) {
      return addCharge(getSequentialNumberingChargeExcl());
    }

    if (isIndividualQrOptionFieldDef(field) &&
        String(typeof choice === 'string' ? choice : (choice?.value ?? '')) === 'Add individual QR codes') {
      return addCharge(getIndividualQrChargeExcl());
    }


    if (isUndersidePrintFieldDef(field) && isUndersidePrintChoice(choice)) {
      return addCharge(getUndersidePrintChargeExcl());
    }

    if (isExpressProductionFieldDef(field) && isExpressProductionChoice(choice)) {
      return addCharge(getExpressProductionChargeExcl());
    }

    return displayText(originalLabel);
  }

  function getProductForm() {
    return (
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector("product-form form") ||
      document.querySelector("form[action='/cart/add']")
    );
  }

  function setVisible(root, isVisible) {
    root.style.display = isVisible ? "" : "none";
    if (!isVisible) setError(root, "");
  }

  function setError(root, msg) {
    const el = $("[data-ipo-errors]", root);
    if (!el) return;
    el.textContent = msg || "";
    el.style.display = msg ? "block" : "none";
    el.classList.add("ipo-errors");
  }

  function parseJsonScript(root, sel, fallback) {
    const el = $(sel, root);
    if (!el) return fallback;
    try { return JSON.parse((el.textContent || "").trim()) || fallback; }
    catch (e) { console.error("Intaprint Options: bad JSON", sel, e, el.textContent); return fallback; }
  }

  function parseVariantMap(root) { return parseJsonScript(root, "[data-ipo-variant-schema-map]", {}); }
  function parseVariantOptions(root) { return parseJsonScript(root, "[data-ipo-variant-options]", null); }

  function getOptionValueFromDom(optionName) {
    // Best-effort fallback
    const radio = document.querySelector(`input[name="${CSS.escape(optionName)}"]:checked`);
    if (radio?.value) return radio.value;
    const sel = document.querySelector(`select[name="${CSS.escape(optionName)}"]`);
    if (sel?.value) return sel.value;
    return "";
  }

  function getOptionValue(optionName, variantOptionsPayload, variantId) {
    if (variantOptionsPayload && variantId) {
      const names = variantOptionsPayload.option_names || [];
      const opts = (variantOptionsPayload.variants || {})[String(variantId)];
      if (Array.isArray(opts)) {
        const idx = names.findIndex((n) => norm(n) === norm(optionName));
        if (idx >= 0) return (opts[idx] || "").toString();
      }
    }
    return getOptionValueFromDom(optionName);
  }

  function getVariantOptionValues(variantOptionsPayload, variantId) {
    if (!variantOptionsPayload || !variantId) return [];
    const opts = (variantOptionsPayload.variants || {})[String(variantId)];
    return Array.isArray(opts) ? opts.map((v) => (v ?? "").toString()) : [];
  }

  function getFieldValue(fieldKey, state) {
    return (state?.values?.[fieldKey] ?? "").toString();
  }

  function matchesRule(current, rule) {
    if (!current) return false;

    const equals = rule.equals ?? rule.is ?? rule.equalsAny ?? rule.in;

    // equals can be a string or an array of strings
    if (Array.isArray(equals)) {
      return equals.some((v) => v != null && String(v) !== "" && norm(current) === norm(v));
    }

    if (equals != null && equals !== "") return norm(current) === norm(equals);

    const includes = rule.includes;
    if (includes != null && includes !== "") return norm(current).includes(norm(includes));

    return false;
  }

  function isExactRepeatMode(state) {
    return state?.savedArtworkApplied && state?.savedArtworkReuseMode === "exact_repeat";
  }

  function isExactRepeatEditableField(field) {
    // Exact repeats preserve the approved design and all original product choices.
    // Customers may still choose the production speed/date for the new order.
    return isExpressProductionFieldDef(field);
  }

  function isFieldRequired(field, state) {
    if (isExactRepeatMode(state) && !isExactRepeatEditableField(field)) {
      return false;
    }

    if (field?.required) return true;

    const rule = field?.requiredWhen;
    if (!rule) return false;

    const fieldKey = rule.fieldKey ? String(rule.fieldKey) : null;
    const current = fieldKey ? getFieldValue(fieldKey, state) : "";

    if (!current) return false;
    return matchesRule(current, rule);
  }

  function shouldShowField(field, state, variantOptionsPayload, variantId) {
    const uploadDependencyKey = String(field?.showAfterFileUpload || "").trim();
    if (
      uploadDependencyKey &&
      countFilesFromStored(state?.values?.[uploadDependencyKey]) < 1
    ) {
      return false;
    }

    // Sample orders are controlled by selected variant availability.
    // Prefer custom.sample_available when a map is rendered; otherwise fall back to a 1+ row.
    if (isSampleOrderFieldDef(field) && !isSampleAvailableForSelectedVariant()) {
      return false;
    }

    // Packaging is controlled by the selected variant metafield:
    // custom.individual_packaging_charge. If it is blank/0, do not show the field.
    if (isPackagingFieldDef(field) && getIndividualPackagingChargeExcl() <= 0) {
      return false;
    }

    // Sequential numbering add-on is controlled by the selected variant metafield:
    // custom.sequential_numbering_charge. If it is blank/0, do not show the option.
    if (isSequentialNumberingOptionFieldDef(field) && getSequentialNumberingChargeExcl() <= 0) {
      return false;
    }

    // Never display the QR choice or its upload field on unpriced variants.
    if ((isIndividualQrOptionFieldDef(field) || field?.key === 'individual_qr_files') &&
        getIndividualQrChargeExcl() <= 0) return false;


    // Express Production is controlled by the selected variant metafield:
    // custom.express_production_available. If false/blank, do not show the field.
    if (isExpressProductionFieldDef(field) && !isExpressProductionAvailableForSelectedVariant()) {
      return false;
    }

    // Ad-Loop flood printing uses one of the selected top-print colours. Show
    // only the remaining foreground-ink selectors, and use reversed-out white
    // wording when the underside is flood printed.
    if (norm(state?._schema?.preview?.type) === "adloop") {
      const optionValues = getVariantOptionValues(variantOptionsPayload, variantId);
      const printSpec = optionValues.find((value) => /[1-4]\s*colour print to top/i.test(value)) || "";
      const topPrintCount = Number((printSpec.match(/([1-4])\s*colour print to top/i) || [])[1] || 0);
      const topFlood = norm(state?.values?.top_background_method).includes("flood print");
      const undersideFlood = norm(state?.values?.underside_background_method).includes("flood print");
      const remainingTopInks = Math.max(0, topPrintCount - (topFlood ? 1 : 0));

      if (field?.key === "text_colour_top_1") return !topFlood && topPrintCount === 1;
      if (field?.key === "text_colour_top_1_multi") return remainingTopInks >= 1 && topPrintCount >= 2;
      if (field?.key === "text_colour_top_2") return remainingTopInks >= 2;
      if (field?.key === "text_colour_top_3") return remainingTopInks >= 3;
      if (field?.key === "text_colour_top_4") return remainingTopInks >= 4;
      if (field?.key === "text_colour_underside" && undersideFlood) return false;
    }

    const rule = field?.showWhen;
    if (!rule) return true;

    // Some products use several Shopify variant options (for example
    // Type = "Printed" plus a separate print specification).  Schemas can
    // explicitly match against any selected variant option so conditional
    // fields are not accidentally hidden by an option-name mismatch.
    if (rule.anyOption === true) {
      return getVariantOptionValues(variantOptionsPayload, variantId)
        .some((value) => matchesRule(value, rule));
    }

    const optName = rule.option ? String(rule.option) : null;
    const fieldKey = rule.fieldKey ? String(rule.fieldKey) : null;

    let current = "";
    if (fieldKey) current = getFieldValue(fieldKey, state);
    else current = getOptionValue(optName || "Type", variantOptionsPayload, variantId);

    if (!current) return false;
    return matchesRule(current, rule);
  }

  function resolveDynamicText(def, state, variantOptionsPayload, variantId) {
    if (typeof def === "string") return def;
    if (!def || typeof def !== "object") return "";

    const arr = Array.isArray(def.when) ? def.when : null;
    if (arr) {
      for (const w of arr) {
        const current = w.fieldKey ? getFieldValue(w.fieldKey, state) : "";
        if (matchesRule(current, w)) return (w.text || "").toString();
      }
      return (def.default || "").toString();
    }

    const arrOpt = Array.isArray(def.whenOption) ? def.whenOption : null;
    if (arrOpt) {
      for (const w of arrOpt) {
        const current = getOptionValue(w.option || "Type", variantOptionsPayload, variantId);
        if (matchesRule(current, w)) return (w.text || "").toString();
      }
      return (def.default || "").toString();
    }

    return (def.default || "").toString();
  }

  function normalizeFileValue(val) {
    if (!val) return "";
    if (Array.isArray(val)) return val.filter(Boolean).join("\n");
    return String(val);
  }

  function countFilesFromStored(val) {
    const s = normalizeFileValue(val).trim();
    if (!s) return 0;
    return s.split("\n").map((x) => x.trim()).filter(Boolean).length;
  }

  function formatFileSize(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  function fileLooksLikeImage(file) {
    return !!file && (file.type || "").toLowerCase().startsWith("image/");
  }

  function fileLooksLikePdf(file) {
    const type = (file?.type || "").toLowerCase();
    const name = (file?.name || "").toLowerCase();
    return type === "application/pdf" || name.endsWith(".pdf");
  }

  function readImageDimensions(file) {
    return new Promise((resolve) => {
      if (!fileLooksLikeImage(file)) return resolve(null);

      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const dims = { width: img.naturalWidth || 0, height: img.naturalHeight || 0 };
        URL.revokeObjectURL(url);
        resolve(dims.width && dims.height ? dims : null);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  function clearFilePreviews(state, fieldKey) {
    const previews = state?.filePreviews?.[fieldKey] || [];
    previews.forEach((p) => {
      if (p?.previewUrl) {
        try { URL.revokeObjectURL(p.previewUrl); } catch (e) {}
      }
    });
    if (state?.filePreviews) delete state.filePreviews[fieldKey];
  }

  function renderUploadCard(card, previews, uploadedCount, isUploading) {
    if (!card) return;
    card.innerHTML = "";

    const count = Number(uploadedCount || previews.length || 0);
    if (!count && !isUploading) {
      card.hidden = true;
      return;
    }

    card.hidden = false;

    const head = document.createElement("div");
    head.className = "ipo-upload-card-head";

    const tick = document.createElement("span");
    tick.className = isUploading ? "ipo-upload-tick ipo-upload-tick--uploading" : "ipo-upload-tick";

    if (isUploading) {
      const spinner = document.createElement("span");
      spinner.className = "ipo-upload-spinner";
      spinner.setAttribute("aria-hidden", "true");
      tick.appendChild(spinner);
    } else {
      tick.textContent = "✓";
    }

    const title = document.createElement("div");
    title.className = "ipo-upload-title";
    title.textContent = isUploading
      ? "Uploading artwork…"
      : "Artwork Uploaded Successfully";

    head.appendChild(tick);
    head.appendChild(title);
    card.appendChild(head);

    if (previews.length) {
      const grid = document.createElement("div");
      grid.className = "ipo-upload-preview-grid";

      previews.forEach((p) => {
        const item = document.createElement("div");
        item.className = "ipo-upload-preview-item";

        if (p.previewUrl) {
          const img = document.createElement("img");
          img.className = "ipo-upload-preview-img";
          img.alt = p.name || "Uploaded artwork preview";
          img.loading = "lazy";
          img.referrerPolicy = "no-referrer";

          const previewCandidates = Array.isArray(p.previewCandidates) && p.previewCandidates.length
            ? p.previewCandidates
            : [p.previewUrl];
          let previewCandidateIndex = 0;

          img.addEventListener("error", () => {
            previewCandidateIndex += 1;
            if (previewCandidateIndex < previewCandidates.length) {
              img.src = previewCandidates[previewCandidateIndex];
              return;
            }

            // All preview URLs failed. For saved artwork this normally means the
            // original Shopify File has since been deleted or is no longer available.
            const icon = document.createElement("div");
            icon.className = p.isPdf ? "ipo-upload-preview-file ipo-upload-preview-pdf" : "ipo-upload-preview-file";
            icon.textContent = p.sizeLabel === "Saved artwork" ? "UNAVAILABLE" : (p.isPdf ? "PDF" : "FILE");
            img.replaceWith(icon);

            if (p.sizeLabel === "Saved artwork") {
              const info = item.querySelector(".ipo-upload-preview-info");
              if (info && !info.querySelector(".ipo-saved-artwork-unavailable")) {
                const warning = document.createElement("div");
                warning.className = "ipo-upload-preview-meta ipo-saved-artwork-unavailable";
                warning.textContent = "Saved artwork file is no longer available. Please upload a replacement.";
                warning.style.color = "#b42318";
                warning.style.fontWeight = "600";
                warning.style.marginTop = "4px";
                info.appendChild(warning);
              }
            }
          });

          img.src = previewCandidates[0];
          item.appendChild(img);
        } else {
          const icon = document.createElement("div");
          icon.className = p.isPdf ? "ipo-upload-preview-file ipo-upload-preview-pdf" : "ipo-upload-preview-file";

          if (p.isPdf) {
            icon.innerHTML = '<span class="ipo-upload-pdf-icon">PDF</span><span class="ipo-upload-pdf-label">Document</span>';
          } else {
            icon.textContent = "FILE";
          }

          item.appendChild(icon);
        }

        const info = document.createElement("div");
        info.className = "ipo-upload-preview-info";

        const filename = document.createElement("div");
        filename.className = "ipo-upload-preview-name";
        filename.textContent = p.name || "Uploaded file";
        info.appendChild(filename);

        const details = [p.isPdf ? "PDF document" : p.dimensions, p.sizeLabel].filter(Boolean).join(" • ");
        if (details) {
          const meta = document.createElement("div");
          meta.className = "ipo-upload-preview-meta";
          meta.textContent = details;
          info.appendChild(meta);
        }

        item.appendChild(info);
        grid.appendChild(item);
      });

      card.appendChild(grid);
    }
  }

  function requiredFileCount(field, state) {
    if (isTopOrBothSidesArtworkField(field, state)) {
      return 1;
    }

    let req = isFieldRequired(field, state) ? 1 : 0;
    const mf = field?.minFilesWhen;
    if (mf && typeof mf === "object") {
      const arr = Array.isArray(mf.when) ? mf.when : [];
      for (const w of arr) {
        const current = w.fieldKey ? getFieldValue(w.fieldKey, state) : "";
        if (matchesRule(current, w)) {
          const c = Number(w.count);
          if (Number.isFinite(c)) return c;
        }
      }
      const def = Number(mf.default);
      if (Number.isFinite(def)) req = def;
    }
    return req;
  }

  function ensureHiddenPropsContainer(form) {
    let c = form.querySelector("[data-ipo-hidden-props]");
    if (!c) {
      c = document.createElement("div");
      c.setAttribute("data-ipo-hidden-props", "1");
      c.style.display = "none";
      form.appendChild(c);
    }
    return c;
  }

  function clearHiddenProps(form) {
    const c = form.querySelector("[data-ipo-hidden-props]");
    if (c) c.innerHTML = "";
  }

  function getStoredFileIds(state, fieldKey) {
    const current = state?.fileIds?.[fieldKey];
    if (!current) return [];
    if (Array.isArray(current)) return current.map((x) => String(x || "").trim()).filter(Boolean);
    return String(current).split("\n").map((x) => x.trim()).filter(Boolean);
  }

  function setStoredFileIds(state, fieldKey, ids) {
    state.fileIds = state.fileIds || {};
    const cleanIds = (ids || []).map((x) => String(x || "").trim()).filter(Boolean);
    if (cleanIds.length) state.fileIds[fieldKey] = cleanIds;
    else delete state.fileIds[fieldKey];
  }

  function getAllStoredFileIds(state) {
    const out = [];
    const seen = new Set();
    Object.values(state?.fileIds || {}).forEach((ids) => {
      const arr = Array.isArray(ids) ? ids : String(ids || "").split("\n");
      arr.forEach((id) => {
        const clean = String(id || "").trim();
        if (clean && !seen.has(clean)) { seen.add(clean); out.push(clean); }
      });
    });
    return out;
  }

  function cleanupEndpointFromUploadEndpoint(uploadEndpoint, action) {
    if (!uploadEndpoint) return "";
    try {
      const url = new URL(uploadEndpoint, window.location.href);
      url.pathname = url.pathname.replace(/\/upload\/?$/, `/file/${action}`);
      return url.toString();
    } catch (e) {
      return uploadEndpoint.replace(/\/upload(\?|$)/, `/file/${action}$1`);
    }
  }

  function getFileActionEndpoint(root, action) {
    const uploadEndpoint = $("[data-ipo-upload-endpoint]", root)?.value || "";
    return cleanupEndpointFromUploadEndpoint(uploadEndpoint, action);
  }

  function sendFileAction(root, action, fileIds, useBeacon = false) {
    const ids = (fileIds || []).map((x) => String(x || "").trim()).filter(Boolean);
    if (!ids.length) return;
    const endpoint = getFileActionEndpoint(root, action);
    if (!endpoint) return;
    const payload = JSON.stringify({ fileIds: ids });
    if (useBeacon && navigator.sendBeacon) {
      try {
        const blob = new Blob([payload], { type: "application/json" });
        if (navigator.sendBeacon(endpoint, blob)) return;
      } catch (e) {}
    }
    fetch(endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: !!useBeacon
    }).catch(() => {});
  }

  function syncHiddenProps(form, schema, state, variantOptionsPayload, variantId) {
    const c = ensureHiddenPropsContainer(form);
    c.innerHTML = "";

    const appendProperty = (key, value) => {
      const cleanKey = String(key || "").trim();
      const cleanValue = String(value || "").trim();
      if (!cleanKey || !cleanValue) return;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${cleanKey}]`;
      input.value = cleanValue;
      c.appendChild(input);
    };

    const isNegativeSelection = (value) => {
      const txt = norm(value);
      return (
        !txt ||
        txt === "no" ||
        txt === "none" ||
        txt === "not required" ||
        txt === "standard" ||
        txt === "standard production" ||
        txt === "top side only" ||
        txt === "front only" ||
        txt === "no, thank you" ||
        txt.startsWith("no ")
      );
    };

    const friendlyDate = (value) => {
      const raw = String(value || "").trim();
      const date = dateFromIsoDate(raw);
      if (!date) return raw;

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    };

    const fields = (schema.fields || []).filter((field) =>
      shouldShowField(field, state, variantOptionsPayload, variantId)
    );

    const selectedType = norm(getSelectedVariantType(variantOptionsPayload, variantId));
    const isBlankVariant = selectedType.includes("blank");

    // Staff only need a simple flag showing that saved artwork was reused.
    if (state.savedArtworkApplied) {
      appendProperty("🔁 Repeat artwork order", "Yes");
    }

    if (isExpressProductionSelected(state)) {
      appendProperty("🚀 Express Production", "Selected");

      if (state.values.express_production_date) {
        appendProperty(
          "📅 Required date",
          friendlyDate(state.values.express_production_date)
        );
      }
    }

    const sampleField = fields.find((field) => isSampleOrderFieldDef(field));
    if (sampleField) {
      const sampleValue = normalizeFileValue(state.values[sampleField.key]).trim();

      if (sampleValue && !isNegativeSelection(sampleValue)) {
        appendProperty(
          isBlankVariant ? "🧪 Blank sample" : "🧪 Printed sample",
          "Selected"
        );
        appendProperty("_intaprint_sample", "true");

      }
    }

    let sequentialSelected = false;
    let sequentialDetails = "";

    fields.forEach((field) => {
      const value = normalizeFileValue(state.values[field.key]).trim();
      if (!value) return;

      const labelText = displayText(
        resolveDynamicText(
          field.labelWhen,
          state,
          variantOptionsPayload,
          variantId
        ) || field.label
      );

      const keyText = norm(field.key);
      const labelNorm = norm(labelText);

      if (isSampleOrderFieldDef(field)) return;
      if (isExpressProductionFieldDef(field)) return;

      if (field.type === "file") {
        appendProperty(labelText, "Uploaded ✓");
        appendProperty(`${labelText} URL`, value);

        const fileIds = getStoredFileIds(state, field.key).join("\n");
        if (fileIds) appendProperty(`${labelText} File ID`, fileIds);
        return;
      }

      if (isNegativeSelection(value)) return;

      if (isSequentialNumberingOptionFieldDef(field)) {
        sequentialSelected = isSequentialNumberingChoice(value);
        return;
      }

      if (
        (keyText.includes("sequential") || labelNorm.includes("sequential")) &&
        (
          keyText.includes("detail") ||
          keyText.includes("range") ||
          labelNorm.includes("detail") ||
          labelNorm.includes("range")
        )
      ) {
        sequentialDetails = value;
        return;
      }

      if (isPackagingFieldDef(field)) {
        if (isPremiumPackagingChoice(value)) {
          appendProperty("🎁 Premium packaging", "Selected");
        }
        return;
      }

      if (isUndersidePrintFieldDef(field)) {
        if (isUndersidePrintChoice(value)) {
          appendProperty("🔄 Double-sided print", "Selected");
        }
        return;
      }

      if (keyText === "assembly" || labelNorm === "assembly") {
        // Never allow a stale Prints Only value to reach cart/draft properties
        // when the selected variant has custom.prints_only_available = false.
        if (
          isPrintsOnlyAssemblyChoice(value) &&
          !isPrintsOnlyAvailableForSelectedVariant()
        ) {
          return;
        }

        appendProperty("🔧 Assembly", value);
        if (isPrintsOnlyAssemblyChoice(value)) {
          appendProperty("_intaprint_prints_only", "true");
        }
        return;
      }

      if (
        keyText === "text" ||
        keyText === "print_text" ||
        labelNorm === "text" ||
        labelNorm.startsWith("text (if any") ||
        labelNorm.includes("address, telephone") ||
        labelNorm.includes("address telephone")
      ) {
        appendProperty("✍️ Print text", value);
        return;
      }

      if (
        keyText.includes("preference") ||
        labelNorm.includes("preference") ||
        keyText.includes("special_instruction") ||
        labelNorm.includes("special instruction")
      ) {
        appendProperty("⚙️ Preferences", value);
        return;
      }

      if (
        keyText === "image_mode" ||
        keyText === "print_method" ||
        keyText === "print_option" ||
        labelNorm.includes("how would you like this product printed") ||
        labelNorm === "print method" ||
        labelNorm === "print option"
      ) {
        appendProperty("🖨️ Print", value);
        return;
      }

      if (isCustomPantoneSelection(field, state)) {
        const slotLabel = customPantoneSlotLabel(field);
        appendProperty(labelText, value);
        appendProperty(
          `${slotLabel} Pantone reference`,
          state.values[customPantoneReferenceKey(field)]
        );
        appendProperty(
          `${slotLabel} preview colour (approx.)`,
          customPantonePreviewColour(field, state).toUpperCase()
        );
        return;
      }

      appendProperty(labelText, value);
    });

    if (sequentialDetails) {
      appendProperty("🔢 Sequential numbering", sequentialDetails);
    } else if (sequentialSelected) {
      appendProperty("🔢 Sequential numbering", "Selected");
    }

    // Pass one compact private pricing value to the Discount Function. The
    // discount-table pricing hook already calculates every selected paid option
    // in the active market currency, EX VAT, per unit.
    const pricing = typeof window.IntaprintGetCurrentPricing === "function"
      ? window.IntaprintGetCurrentPricing(1)
      : null;
    const paidOptionsUnitExcl = Array.isArray(pricing?.paidOptions)
      ? pricing.paidOptions.reduce((sum, option) => {
          const value = Number(option?.unitPriceExcl);
          return sum + (Number.isFinite(value) && value > 0 ? value : 0);
        }, 0)
      : 0;

    if (Number.isFinite(paidOptionsUnitExcl) && paidOptionsUnitExcl > 0) {
      appendProperty(
        "_intaprint_paid_options_unit_excl",
        paidOptionsUnitExcl.toFixed(6)
      );
    }
  }

  async function uploadFiles(root, files) {
    const uploadEndpoint = $("[data-ipo-upload-endpoint]", root)?.value;
    if (!uploadEndpoint) throw new Error("Upload endpoint not configured.");
    const uploads = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      const resp = await fetch(uploadEndpoint, { method: "POST", body: formData });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.fileUrl || !json?.fileId) throw new Error(json?.error || `Upload failed (HTTP ${resp.status})`);
      uploads.push({ fileUrl: json.fileUrl, fileId: String(json.fileId) });
    }
    return uploads;
  }

  function closeAllSwatchDropdowns(exceptEl = null) {
    $all(".ipo-sd-panel").forEach((p) => {
      if (exceptEl && p === exceptEl) return;
      p.hidden = true;
    });
  }

  function renderExpressProductionDateField(root, state, onChange) {
    if (!isExpressProductionSelected(state)) return null;

    let earliest = getEarliestExpressProductionDate();
    let earliestIso = toIsoDate(earliest);

    if (!state.values.express_production_date || state.values.express_production_date < earliestIso) {
      state.values.express_production_date = earliestIso;
    }

    const wrap = document.createElement("div");
    wrap.className = "ipo-field ipo-express-date-field";
    wrap.setAttribute("data-ipo-field-key", "express_production_date");

    const label = document.createElement("label");
    label.className = "ipo-label";
    label.textContent = "Express Production Date";

    const star = document.createElement("span");
    star.className = "ipo-required";
    star.textContent = "*";
    label.appendChild(star);

    const status = document.createElement("div");
    status.className = "ipo-express-status";

    const input = document.createElement("input");
    input.type = "date";
    input.className = "ipo-input ipo-express-date-input--earliest";
    input.min = earliestIso;
    input.value = state.values.express_production_date || earliestIso;
    input.required = true;
    input.setAttribute("data-earliest-express-date", earliestIso);

    const note = document.createElement("div");
    note.className = "ipo-express-date-note";
    note.textContent = "Weekends, UK bank holidays and our Christmas closure period are not available.";

    const capacityNote = document.createElement("div");
    capacityNote.className = "ipo-express-date-note";

    function updateCapacityNote(maxQty) {
      capacityNote.textContent = maxQty
        ? `Maximum quantity for this Express Production date: ${Number(maxQty).toLocaleString("en-GB")} units.`
        : "";
      capacityNote.style.display = maxQty ? "" : "none";
    }

    async function refreshCapacity() {
      const maxQty = await refreshExpressProductionMaxQtyForState(state);
      if (!wrap.isConnected) return;
      updateCapacityNote(maxQty);
      applyExpressProductionChargeToDiscountTable(state);
    }

    function syncDateUi({ forceValue = false } = {}) {
      earliestIso = toIsoDate(earliest);
      input.min = earliestIso;
      input.setAttribute("data-earliest-express-date", earliestIso);

      if (forceValue || !state.values.express_production_date || state.values.express_production_date < earliestIso) {
        state.values.express_production_date = earliestIso;
        input.value = earliestIso;
        onChange();
      }

      input.classList.toggle("ipo-express-date-input--earliest", input.value === earliestIso);
      status.innerHTML = buildExpressProductionMessage(earliest);
      refreshCapacity();
    }

    async function refreshEarliestDate({ forceValue = false } = {}) {
      const serviceEarliest = await getEarliestExpressProductionDateAsync();
      if (!serviceEarliest || !wrap.isConnected) return;

      const previousEarliestIso = earliestIso;
      earliest = serviceEarliest;
      earliestIso = toIsoDate(earliest);

      syncDateUi({ forceValue: forceValue || input.value === previousEarliestIso });
    }

    syncDateUi();

    // Upgrade the fallback weekend-only date to the shared UK working-day service date.
    refreshEarliestDate();

    // If the page is left open over the cut-off time, update the message/date automatically.
    const refreshTimer = window.setInterval(() => {
      if (!wrap.isConnected) {
        window.clearInterval(refreshTimer);
        return;
      }

      refreshEarliestDate();
    }, 60 * 1000);

    input.addEventListener("change", async () => {
      let selected = input.value || "";

      if (!selected || selected < earliestIso) {
        selected = earliestIso;
      }

      let selectedDate = dateFromIsoDate(selected);
      if (!selectedDate) {
        selectedDate = earliest;
      }

      if (!isWorkingDay(selectedDate)) {
        selectedDate = nextWorkingDay(selectedDate);
        selected = toIsoDate(selectedDate);
      }

      const correctedDate = await getCorrectedWorkingDateAsync(selected);
      selected = toIsoDate(correctedDate);

      if (selected < earliestIso) {
        selected = earliestIso;
      }

      state.values.express_production_date = selected;
      input.value = selected;
      input.classList.toggle("ipo-express-date-input--earliest", selected === earliestIso);

      clearFieldErrors(root);
      setError(root, "");
      await refreshCapacity();
      onChange();
    });

    wrap.appendChild(label);
    wrap.appendChild(status);
    wrap.appendChild(input);
    wrap.appendChild(note);
    wrap.appendChild(capacityNote);

    return wrap;
  }

  function renderSwatchDropdown(field, state, onChange, variantOptionsPayload, variantId) {
    const wrap = document.createElement("div");
    wrap.className = "ipo-sd";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ipo-sd-btn";

    const left = document.createElement("div");
    left.className = "ipo-sd-left";

    const chip = document.createElement("span");
    chip.className = "ipo-sd-chip";

    const lab = document.createElement("span");
    lab.className = "ipo-sd-label";

    const caret = document.createElement("span");
    caret.className = "ipo-sd-caret";
    caret.textContent = "▾";

    left.appendChild(chip);
    left.appendChild(lab);
    btn.appendChild(left);
    btn.appendChild(caret);

    const panel = document.createElement("div");
    panel.className = "ipo-sd-panel";
    panel.hidden = true;

    const search = document.createElement("input");
    search.className = "ipo-sd-search";
    search.type = "search";
    search.placeholder = "Search";
    panel.appendChild(search);

    const list = document.createElement("div");
    list.className = "ipo-sd-list";
    panel.appendChild(list);

    clearUnavailablePrintsOnlySelection(field, state);
    clearUnavailableChoiceSelection(field, state, variantOptionsPayload, variantId);
    const choices = getVisibleChoices(field, state, variantOptionsPayload, variantId);
    const getCurrent = () => (state.values[field.key] ?? "").toString();

    function setButtonDisplay() {
      const current = getCurrent();
      const found = current ? choices.find((c) => norm((c.value ?? c.label ?? c)) === norm(current)) : null;
      const label = found ? getChoiceLabel(field, found) : (field.placeholder || "Select a value");
      const sw = found ? (found.swatch ?? found.color ?? "").toString() : "";
      lab.textContent = label;
      chip.style.background = sw || "transparent";
      chip.style.borderStyle = sw ? "solid" : "dashed";
    }

    function renderList(filterText = "") {
      list.innerHTML = "";
      const ft = norm(filterText);

      const filtered = choices.filter((c) => {
        const label = getChoiceLabel(field, c);
        return !ft || norm(label).includes(ft);
      });

      if (!filtered.length) {
        const empty = document.createElement("div");
        empty.className = "ipo-sd-empty";
        empty.textContent = "No results";
        list.appendChild(empty);
        return;
      }

      filtered.forEach((c) => {
        const value = (c.value ?? c.label ?? c).toString();
        const label = getChoiceLabel(field, c);
        const sw = (c.swatch ?? c.color ?? "").toString();

        const opt = document.createElement("div");
        opt.className = "ipo-sd-opt";
        opt.setAttribute("role", "option");
        opt.setAttribute("aria-selected", norm(getCurrent()) === norm(value) ? "true" : "false");

        const optChip = document.createElement("span");
        optChip.className = "ipo-sd-chip";
        optChip.style.background = sw || "transparent";
        optChip.style.borderStyle = sw ? "solid" : "dashed";

        const nm = document.createElement("span");
        nm.className = "ipo-sd-opt-name";
        nm.textContent = label;

        opt.appendChild(optChip);
        opt.appendChild(nm);

        opt.addEventListener("click", () => {
          state.values[field.key] = value;
          panel.hidden = true;
          setButtonDisplay();
          clearFieldErrors(document);
          setError(document, "");
          onChange();
        });

        list.appendChild(opt);
      });
    }

    setButtonDisplay();
    renderList("");

    btn.addEventListener("click", () => {
      const willOpen = panel.hidden;
      closeAllSwatchDropdowns(panel);
      panel.hidden = !willOpen;
      if (!panel.hidden) {
        search.value = "";
        renderList("");
        setTimeout(() => search.focus(), 0);
      }
    });

    search.addEventListener("input", () => renderList(search.value));

    document.addEventListener("click", (e) => {
      if (!wrap.contains(e.target)) panel.hidden = true;
    });

    wrap.appendChild(btn);
    wrap.appendChild(panel);

    return wrap;
  }

  function renderExactRepeatSummary(schema, state, variantOptionsPayload, variantId) {
    const box = document.createElement("div");
    box.className = "ipo-exact-repeat-summary";

    const title = document.createElement("div");
    title.className = "ipo-exact-repeat-summary-title";
    title.textContent = "🔁 Exact repeat specification";

    const text = document.createElement("p");
    text.className = "ipo-exact-repeat-summary-text";
    text.textContent =
      "This product will be reproduced using the approved PDF and the same product colours, text and printing options as the previous order.";

    const list = document.createElement("ul");
    list.className = "ipo-exact-repeat-summary-list";

    const hiddenKeys = new Set([
      "sample_order",
      "express_production",
      "express_production_date"
    ]);

    (schema?.fields || []).forEach((field) => {
      if (!field?.key || hiddenKeys.has(field.key) || field.type === "file") return;

      const value = normalizeFileValue(state.values[field.key]).trim();
      if (!value) return;

      const label = displayText(
        resolveDynamicText(
          field.labelWhen,
          state,
          variantOptionsPayload,
          variantId
        ) || field.label
      );

      const item = document.createElement("li");
      item.textContent = `${label}: ${value}`;
      list.appendChild(item);
    });

    const proofItem = document.createElement("li");
    proofItem.textContent = state.savedArtworkApprovedProofLabel
      ? `Approved PDF: ${state.savedArtworkApprovedProofLabel}`
      : "Approved PDF: Exact repeat artwork";
    list.appendChild(proofItem);

    const note = document.createElement("div");
    note.className = "ipo-exact-repeat-summary-note";
    note.textContent = state.savedArtworkHasOriginalArtwork
      ? "Need to change anything? Return to My Artwork and choose “Use on same product & make changes”."
      : "Need to make changes? Do not use Exact Repeat. Enter your revised text or instructions on the product page so that a new proof can be created.";

    box.append(title, text, list, note);
    return box;
  }

  function renderSavedArtworkGuide() {
    const guide = document.createElement("div");
    guide.className = "ipo-artwork-guide";

    const icon = document.createElement("span");
    icon.textContent = "🔁 Previously ordered this product? ";

    const link = document.createElement("a");
    link.href = "/pages/save-and-reuse-your-artwork";
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent =
      "Learn how to save a design for an exact repeat or reuse your original artwork";

    guide.appendChild(icon);
    guide.appendChild(link);
    guide.appendChild(document.createTextNode("."));

    return guide;
  }

  let adloopPreviewCounter = 0;

  function isAdloopPreviewEnabled(schema) {
    return norm(schema?.preview?.type) === "adloop";
  }

  function getAdloopChoiceSwatch(schema, fieldKey, value, fallback, state = null) {
    const field = (schema?.fields || []).find((item) => item?.key === fieldKey);
    if (field?.customPantone && norm(value) === "custom pantone colour") {
      return customPantonePreviewColour(field, state);
    }
    const choice = getConfiguredChoices(field, null, schema).find(
      (item) => norm(choiceValue(item)) === norm(value)
    );
    const swatch = (choice?.swatch || choice?.color || "").toString();
    return swatch && swatch !== "transparent" ? swatch : fallback;
  }

  function getAdloopColourLabel(schema, state, fieldKey, value) {
    const field = (schema?.fields || []).find((item) => item?.key === fieldKey);
    if (field?.customPantone && norm(value) === "custom pantone colour") {
      const reference = String(state?.values?.[customPantoneReferenceKey(field)] || "").trim();
      return reference || "Custom Pantone colour";
    }
    return value;
  }

  function getAdloopFontFamily(value) {
    const font = (value || "").toString().trim();
    if (!font || norm(font) === "n/a") return "Arial, sans-serif";
    return `'${font.replace(/'/g, "")}', Arial, sans-serif`;
  }

  function mixAdloopPreviewColour(base, target, amount) {
    const expandHex = (value) => {
      const match = String(value || "").trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (!match) return null;
      return match[1].length === 3
        ? match[1].split("").map((part) => part + part).join("")
        : match[1];
    };
    const from = expandHex(base);
    const to = expandHex(target);
    if (!from || !to) return base;
    const ratio = Math.max(0, Math.min(1, Number(amount) || 0));
    const channel = (offset) => Math.round(
      parseInt(from.slice(offset, offset + 2), 16) * (1 - ratio) +
      parseInt(to.slice(offset, offset + 2), 16) * ratio
    ).toString(16).padStart(2, "0");
    return `#${channel(0)}${channel(2)}${channel(4)}`;
  }

  function createSvgElement(name, attributes = {}) {
    const element = document.createElementNS("http://www.w3.org/2000/svg", name);
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, String(value));
    });
    return element;
  }

  // The supplied manufacturer-style vector trace is split into semantic parts so
  // each face can follow the customer's selections while retaining the true
  // Ad-Loop silhouette.
  const ADLOOP_VECTOR_PATHS = [
    { role: "outline", d: "M4711.73 20328.84c154.36,247.02 382.77,474.54 657.27,598.81 375.47,180.73 794.41,412.71 1176.68,602.51 61.26,30.42 91.75,54.4 163.18,83.65 210.88,86.36 142.27,118.16 397.32,59.51 85.01,-19.55 133.75,-53.23 208.59,-58.46 131.64,10.33 603.42,527.97 746.61,707.08 259.46,324.54 395.49,501.66 621.04,844.87 196.35,310.44 778.64,1088.95 1034.33,1339.35l371.82 369.15c36.13,36.3 67.53,47.63 102.02,79.75l289.93 254.07c48.34,35.31 63.84,37.12 104.35,76.25 283.89,242.41 896.04,599.93 1240.62,770.82 701.43,317.12 1255.36,529.65 2053.62,696.74 696.69,145.84 1297.07,146.17 2002.62,140.59 419.86,-3.32 857.29,-117.35 1251,-215.1 207.58,-51.53 712.59,-265.43 891.13,-394.65l314.98 -229.39c177.03,-144.68 401.52,-468.9 410.7,-693.83 9.33,-228.45 28.67,-468.87 32.04,-693.08 12.37,-823.3 219.4,-1123.07 -292.96,-1801.53 -114.8,-152.01 -436.03,-418.88 -597.55,-505.48 -242.35,-129.94 -432.34,-260.72 -716.72,-387.51l-510.55 -213.97c-267.83,-101.36 -541.47,-177.87 -815.66,-274.47l-1465.52 -362.05c-301.08,-35.87 -622.12,-151.29 -922.63,-181.16 -206.61,-20.53 -418.1,-64.25 -618.04,-104.84 -453.79,-92.13 -1137.13,-145.63 -1579.8,-251.57 -295.08,-70.62 -904.35,-140.81 -1254.78,-210.23 -190.49,-37.74 -447.33,-60.14 -617.5,-109.97 -192.97,-56.52 -420.42,-72.54 -613.6,-127.15l-856.56 -229.52c-154.11,-45.58 -148.4,-38.3 -158.39,-218.5 -18.72,-337.58 -44.64,-308.18 -306.5,-421.29 -39.43,-17.03 -95.65,-34.19 -130.8,-50.71l-122.87 -58.65c-431.11,-174.45 -828.94,-400.74 -1266.45,-561.57 -314.72,-115.7 -977.51,18.78 -1338.34,36.4 -292.9,14.3 -933.12,168.99 -1208.02,258.38 -313.73,102.02 -1507.66,501.63 -1314.19,979.62 66.22,163.63 -49.52,242.52 150.27,410.11 383.7,321.86 1343.87,272.39 1871.53,174.91 52.11,-9.63 103.15,-7.36 160.37,-20.78l455.41 -107.11z" },
    { role: "ring-shadow", d: "M2433.4 20170.88c74.08,14.91 90.49,26.44 151.26,47.59 48.41,16.84 118.24,19.02 180.6,33.58 558.29,130.36 1604.37,-91.54 2166.63,-260.1 68.39,-20.5 101.55,-38.59 160.75,-54.47 150.07,-40.26 124.89,-36.61 106.61,-93.59 -148.06,49.2 -279.88,110.41 -432.99,147.42 -293.99,71.05 -482.21,151.66 -845.13,189.12 -281.59,29.07 -865.36,87.24 -1154.3,36.66l-284.19 -62.87c-85.71,-3.25 -19.25,-2.9 -49.24,16.66z" },
    { role: "ring-mid", d: "M2142.84 19997.28c-3.66,522.49 1184.2,477.48 1482.82,455.34 300.14,-22.26 579.3,-54.63 848.95,-125.73 376.76,-99.34 390.46,-87.56 756.08,-239.78l4.4 -133.74c-30.48,3.07 -137.84,40.32 -174.14,52.41 -637.76,212.43 -2097.45,508.83 -2644.8,199.13 -62.21,-35.2 -86.92,-26.67 -148.2,-81.51l-125.11 -126.12z" },
    { role: "ring-light", d: "M2407.93 19804.24c46.81,58.85 43.37,48.99 134.94,55.07l179.16 -150.4c27.62,-15.95 32.46,-14.8 54.85,-27.02 45.95,-25.09 8.88,-9.78 50.01,-32.22 25.4,-13.85 83.65,-35.14 113.61,-52.05 328.21,-185.22 1251.32,-444.27 1558.51,-453.5 30.97,-133.74 39.11,-101.08 93.36,-193.08l-643.06 105.1c-92.36,27.25 -211.69,67.58 -294.72,86.08 -280.3,62.45 -1210.65,384.14 -1246.66,662.02z" },
    { role: "ring-body", d: "M5182.33 19783.44c-33.84,-85.3 -69.52,-161.83 -133.36,-153.15l-998.8 269.43c-431.52,57.87 -1227.06,166.77 -1608.78,-0.19 -80.88,-35.38 -212.65,-140.32 -151.48,-256.64 72.75,12.21 27.09,14.67 55.03,109.21 60.66,-23.34 63.67,-97.09 244.91,-224.95 230.65,-162.71 766.25,-381.66 1031.57,-436.25 66.11,-13.61 109.22,-34.64 182.11,-48.79l815.53 -153.01c106.45,-18.94 155.01,-99.97 324.37,-172.24 55.64,-23.75 124.24,-46.63 169.55,-81.43 -266.32,-4.31 -1016.69,122.22 -1287.07,199.74l-774.47 230.62 -590.67 282.69c-104.02,64.69 -136.39,109.5 -206.24,172.91 -68.93,62.57 -76.97,59.88 -117.44,146.6 -131.76,282.36 255.05,427.71 473.57,474.49 649.24,139 1884.53,-63.79 2482.46,-318.83 53.49,-25.02 50.85,-9.13 89.21,-40.21z" },
    { role: "top-ribbon", d: "M11013.17 25452.9c52,2.96 103.8,25.73 128.68,80.9l504.04 274.08c32.97,27.48 2.89,-15.93 17.71,30.83 86.36,3.31 193.59,71.89 266.53,112.24l543.78 229.95c301.78,126.87 908.05,316.54 1200.22,366.24 88.97,15.13 239.04,42.61 328.39,65.74 634.47,164.26 1903.82,180.57 2527.64,55.83 311.61,-62.31 640.63,-144.54 931.79,-239.33 55.86,-18.18 74.32,-31.75 128.38,-52.04 421.02,-157.98 1090.91,-574.96 1108.63,-1049.76l53.11 -1119.84c30.61,-479.6 76.87,-793.44 -216.84,-1189.51 -60.45,-81.53 -150.73,-206.44 -233.42,-258.23 53.77,164.2 377.31,338.9 357.41,850.56 -8.92,229.26 -62.43,300.9 -146.44,458.75 -141.12,265.15 -588.34,553.65 -860.45,654.14 -288.5,106.54 -279.8,146.59 -696.27,228.43 -60.8,11.95 -182.79,40.46 -255.05,57.96 -90.5,21.91 -178.71,26.76 -265.63,44.14 -418.86,83.76 -1535.89,88.6 -2009.28,-2.68l-1830.59 -459.18c-32.93,-14.8 -82.36,-31.1 -119.96,-44.25 -315.39,-110.31 -754.65,-322.84 -1047.72,-484.64 -343.19,-189.48 -679.61,-433.58 -1002.2,-663.01 -34.74,-25.11 -56.65,-50.39 -89.7,-74.63 -231.09,-169.44 -431.35,-382.18 -637.71,-581.6 -131.8,-127.37 -474.4,-522.51 -580.55,-655.4 -191.3,-239.48 -429.91,-521.22 -606.32,-761.32 -144.03,-196.03 -542.08,-632.03 -728.28,-805.04 -262.29,-243.71 -234,-222.39 -510.38,-411.64 -83.99,-57.52 -219.11,-161.41 -312.64,-173.62l102.19 1447.32c627.78,438.68 566.24,342.12 1066.36,935.01 75.78,89.83 150.21,177.24 218.15,277.93 180.93,268.18 702.15,992.7 899.39,1225.48 44.36,52.34 27.87,55.84 108.36,138.81 38.11,39.28 18.98,19.34 58.63,73.49 62.2,84.97 382.15,420.92 484.08,521.25 200.18,197.03 511.72,458.05 738.41,628.43 76.63,56.51 335.77,212.52 377.55,268.21z" },
    { role: "ring-inner", d: "M2579.7 19896.16c360.49,48.81 882.41,45.72 1230.57,-23.06 185.22,-36.59 475.35,-58.7 685.08,-129.49 -41.8,-194.13 -43.57,-330.39 -26.17,-543.11 -211.59,28.54 -849.53,170.56 -1034.13,249.23 -187.24,79.8 -772.36,277.25 -855.35,446.43z" },
    { role: "opening", d: "M17902.06 24343.68c-23.82,-18.15 -170.02,-181.03 -274.56,-252.26 -138.27,-94.22 -277.15,-212.44 -423.01,-285.78 -428.88,-215.66 -580.65,-323.37 -1102.37,-495.95 -82.89,-27.41 -171.92,-70.48 -253.19,-93.53 -199.86,-59.35 -636.74,-206.66 -822.72,-247.84l-568.43 -139.72c-82.34,-20.5 -201.58,-58.24 -275.49,-70.47l-2369.52 -464.81c-159.18,-16.11 -411.39,-79.38 -597.15,-109.78l-1792.15 -351.37c-104.46,-15.19 -205.47,-47.3 -300.05,-66.62 24.35,64.5 80.04,114.73 121.88,161.93l1052.26 1106.81 108.41 90.1c57.64,41.27 13.74,10.84 52.99,45.6 282.4,231.33 863.53,633.43 1171.78,789.26 95.82,48.44 157.96,89.31 259.63,135.22 477.86,215.78 884.19,419.66 1408.9,551.67 107.97,27.16 199.06,60.43 312.82,82.74 676.36,132.65 970.11,217.71 1758.92,218.29 328.21,0.25 1126.39,-43.52 1421.88,-143.3 98.12,-33.13 211.17,-53.67 313.09,-82.43 261.03,-73.65 584.88,-205.16 796.08,-377.76z" },
    { role: "outline", d: "M11663.6 25838.71c-14.82,-46.76 15.26,-3.35 -17.71,-30.83l-504.04 -274.08c3.38,59.95 55,62.16 105.81,94.39 40.39,25.61 93.08,52.51 139,75.93 62.1,31.68 210.54,128.27 276.94,134.59z" },
    { role: "cap-detail", d: "M5054.6 20230.84l115.29 0.69c19.68,-38.78 19.39,-34.06 34.94,-68.81 -70.93,30.72 -102.57,18.94 -150.23,68.12z" },
    { role: "cap-detail", d: "M4821.32 19638.86c59.86,-4.2 155.69,-29.21 188.76,-65.41 -87.06,-45.99 -152.2,-29.27 -188.76,65.41z" },
    { role: "cap-side", d: "M6707.73 19816.01c12.87,190.24 112.99,1727.43 138.18,1779.38 50.16,103.46 331.61,13.12 389.36,-27.19 -53.54,-53.05 -106.85,-53.82 -213.27,-131.89l-130.18 -1565.87c90.4,-37.49 592.49,-258.18 625.36,-253.71 6.61,17.21 33.21,-27.74 5.41,33.07 -15.15,33.14 -67.9,43.19 -92.97,55.53 84.53,57.69 175.75,85.16 281.02,128.46l-30.75 -405.76c-206.56,-65.87 -478.23,56.23 -661.42,162.24 -133.43,77.22 -131.43,34.62 -310.74,225.74z" },
    { role: "cap", d: "M4547.88 19721.66c305.95,-20.67 195.83,-115.26 273.86,-186.06 36.24,-32.88 87.69,-49.93 146.39,-35.57 321.71,78.69 425.1,703.32 204.9,781.15 -111.68,16.44 -132.79,2.99 -196.57,-42.32 -43.23,18.14 -162.08,53.09 -194.75,76.44 25.57,108.84 255.05,321.51 350.58,403.98 130.09,112.31 641.6,346.02 847.81,454.56l832.19 430.68c-40.01,-230.21 -47.53,-522.73 -60.99,-760.38l-68.39 -737.76c-47.89,-372.27 -6.3,-353.97 241.63,-512.08 113.33,-72.27 290.57,-146.47 419.1,-189.94 50.8,-17.18 122.14,-27.1 170.29,-42.95 -276.8,-125.39 -567.66,-250.97 -838.82,-382.68 -212.56,-103.25 -708.02,-320.16 -885.25,-353.17 -359.29,-66.91 -658.48,36.33 -954.9,213.82 -355.13,212.64 -363.07,423.55 -287.08,882.28z" },
    { role: "cap-highlight", d: "M4778.69 19046.95c-31.85,130.54 80.33,115.6 267.51,208.15 406.25,200.87 828.6,388.06 1228.41,584.43 25.39,12.97 42.37,15.62 69.19,29.35 38.94,19.94 208.5,118.55 252.91,91.59 126.47,-76.77 -195.89,-172.74 -257.2,-203.21 -185.79,-92.35 -1474.72,-706.37 -1560.82,-710.31z" },
    { role: "top-edge", d: "M11663.6 25838.71c-66.4,-6.32 -214.84,-102.91 -276.94,-134.59 -45.92,-23.42 -98.61,-50.32 -139,-75.93 -50.81,-32.23 -102.43,-34.44 -105.81,-94.39 -24.88,-55.17 -76.68,-77.94 -128.68,-80.9 -135.99,17.47 -425.57,-319.82 -543.04,-343.02 73.23,137.01 881.6,630.21 1058.64,731.47l1453.78 623.33c69.8,19.35 151.96,42.98 228.6,66.01 155.28,46.66 315.25,77.26 470.17,125.12 505.9,156.28 1677.36,212.8 2184.24,187.11 598.19,-30.32 1159.32,-169.22 1695.02,-380.65 79.19,-31.26 140.47,-71.62 220.54,-91.27 -30,-22.53 13.81,-18.28 -37.16,-19.2l-1017.1 314.8c-293.45,69.55 -1013.77,116.49 -1306.43,112.9 -444.39,-5.44 -860.59,-40.87 -1298.45,-106.37l-1134.97 -278.57c-132.84,-38.51 -1264.74,-433.22 -1323.41,-555.85z" },
    { role: "underside", d: "M7119.56 19837.22c184.59,114 412.76,235.18 573.65,380.17 76.54,68.99 187.33,140.64 258.84,218.47 38.68,42.09 74.8,72.15 117.81,112.66 209.35,197.16 387.18,409.44 570.61,632.56 60.06,73.05 382.98,500.21 439.37,515.54l779.72 177.09c264.2,59.88 535.81,91.07 800.98,156.85 127.89,31.73 276.62,32.99 407.28,70.24 241.77,68.93 980.99,160.97 1223.67,223.69 260.38,67.28 547.84,80.69 810.23,146.61l1452.62 325.97c155.45,38.72 336.57,66.13 488.56,119.92l738.51 219.01c227,63.42 655.13,228.88 857.8,328.27 108.75,53.33 225.39,90.57 326.26,151.74 52.93,32.1 99.9,53.97 161.74,84.88 374.46,187.2 542.76,347.5 830.36,612.66 120.75,-95.06 214.76,-165.81 299.18,-301.81 119.3,-192.19 106.18,-239 144.52,-459.38 32.76,9.4 35.35,-27.15 35.2,60.04 -0.67,391.24 -222.24,586.21 -488.69,781.27 -632.56,463.1 -1751.85,603.68 -2527.35,603.39 -1088.38,-1.29 -1975.85,-209.33 -2842.85,-533.7 -273.85,-102.46 -887.48,-374.55 -1135.39,-545.99 -130.66,-90.36 -263.34,-148.06 -389.01,-237.4l-253.26 -175.31c-305.04,-199.75 -750.56,-617.57 -1013.1,-882.79 -167.62,-169.34 -288.72,-319.57 -446.62,-492.96 -311.81,-332.6 -596.9,-768.55 -912.64,-1112.98l-867.36 -830.78c-46.46,-38.49 -467.21,-323.56 -521.6,-322.9 -38.5,16.3 -20,8.88 -61.77,32.4 151.43,74.77 394,231.06 523.78,333.39 253.4,199.81 444.74,412.14 672.48,630.61l584.41 732.64c38.25,44.74 62.57,66.54 98.89,116.2 111.92,152.99 547.82,698.41 696.66,836.06 166.1,153.63 316.86,337.44 488.51,483.59 185.81,159.04 355.62,320.68 549.7,454.76l520.89 353.15c91.08,70.82 202.61,124.4 298.26,179.02l402.24 207.78c612.94,314.96 1272.34,514.78 1936.95,681.05 123.44,29.81 249.77,42.55 377.48,71.4 530.37,119.8 1114.19,105.55 1655.8,105.48 362.42,-0.04 724.26,-76.07 1069.6,-135.85 323.34,-55.96 719.61,-198.38 1004.22,-360.65 37.28,-21.25 59.45,-27.73 97.26,-50.9l267.66 -194.33c65.8,-51.45 173.74,-163.57 223.75,-238.17 149.88,-223.52 207.53,-414.23 170.01,-701.18 -10.87,-83.13 -55.74,-162.8 -87.26,-225.56 -21.83,-43.47 -26.63,-65.47 -47.89,-100.88 -280.71,-467.66 -959.93,-903.54 -1474.65,-1111.59 -406.98,-164.5 -671.88,-279.01 -1120.71,-411.17l-949.51 -253.3c-160.18,-40.51 -342.19,-62.13 -504.62,-106.11 -154.1,-41.73 -337.01,-73.48 -502.4,-108.04 -45.2,-9.45 -86.46,-5.58 -130.65,-14.6l-4558.64 -779.75c-345.09,-57.32 -656.02,-149.12 -998.73,-223.75 -294.57,-64.15 -614.91,-210.99 -892.6,-328.09 -74.58,47.52 -167.01,55.87 -228.16,101.36z" }
  ];

  // Cleaned straight/top-side trace supplied by Intaprint. The metal ring
  // retains its original shading; the moulded cap and printable strip are
  // recoloured from the customer's selections.
  const ADLOOP_TOP_VECTOR_SHAPES = [
    {"shape":"path","role":"cap-main","fill":"#003399","attrs":{"d":"M1734.37 813.72c-33.43,-98.37 -40.21,-220.8 -20.32,-367.32 95.78,-263.21 276.77,-404.78 542.95,-424.72l1483.64 0c66.62,-4.17 100.51,7.42 101.66,34.8l0 1105.97c-4.16,33.15 -39.58,47.99 -106.27,44.54l-1492.49 0c-256.24,-25.35 -425.97,-156.44 -509.16,-393.27z"},"stroke":"#333333","strokeWidth":"41.71"},
    {"shape":"path","role":"ribbon","fill":"white","attrs":{"d":"M3892.82 1033.5l14883.51 0c27.97,0 50.86,-14.84 50.86,-32.98l0 -772.7c0,-18.14 -22.89,-32.98 -50.86,-32.98l-14883.51 0c-27.97,0 -50.87,14.84 -50.87,32.98l0 772.7c0,18.14 22.9,32.98 50.87,32.98z"},"stroke":"#333333","strokeWidth":"41.71"},
    {"shape":"ellipse","role":"cap-hole","fill":"black","attrs":{"cx":"2172.76","cy":"633.05","rx":"173.31","ry":"180.42"},"stroke":"black","strokeWidth":"20.86"},
    {"shape":"path","role":"ring","fill":"#5B544C","attrs":{"d":"M559.15 874.49c-90.68,-13.11 -211.49,27.58 -299.67,-5.84 13.85,-7.66 383.33,-46.88 434.58,-50.48 175.42,-12.32 1247.8,-101.64 1368.87,-92.65l5.6 20.28c-10.4,4.4 -19.07,6.89 -27.02,14.37 64.78,-11.47 46.85,-21.84 54.58,-35.06 30.2,-2.63 51.37,4.39 79.11,9.58l-5.17 -17.44c89.21,-69.86 2.43,-1.39 106.1,-60.05 -95.69,1.07 -134.31,-3.3 -252.04,-0.68 -82.03,1.84 -168.51,8.87 -250.82,4.34l7.11 -11.29 -35.89 -0.33c10.07,-17.34 9.06,-7.23 4.76,-17.13 -62.42,1.26 -331.91,-0.71 -366.59,13.79l253.19 1.41c-15.59,-9.98 -4.66,-21.15 13.96,40.86 -17.97,-5.49 -23.55,12.9 -23.64,13.01 44.57,1.29 99.47,-2.53 141.15,9.19 -14.47,15.49 -131.75,28.26 -156.21,23.4 -45.97,-7.87 -92.11,9.05 -136.09,11.03 -32.15,25.5 -312.25,33.05 -355.85,35.05 -70.4,3.22 -320.54,43.73 -345.19,-19.36 49.88,-64.08 302.32,-24.48 323.59,-72.31 -6.1,-9.52 8.7,-22.01 -54.59,-9.2 -88.26,17.87 -97.08,6.78 -185.15,8.3 18.96,-6.44 46.22,-5.32 61.32,-16.15 -37.4,-2.31 -177.61,15.64 -222.5,23.1 -23.23,3.86 -55.87,2.12 -81.85,4.31 -9.68,29.67 2.63,-12.28 8.89,13.12l-426.13 29.67c29.91,-11.98 50.42,-1.56 72.23,-23.42 20.23,-20.27 -8.7,-7.16 16.53,-18.23 62.52,-17.7 338.67,-45.66 429.81,-47.43 109.12,-2.11 547.38,-35.23 657.47,-48.48 56.85,-6.85 602.85,-49.86 657.32,-25.75 8.55,3.79 6.44,3.44 11.42,7.29 -21.12,32.66 -30.4,-1.52 -51.29,30.91 75.12,15.41 102.54,12.03 178.26,-7.52 10.29,-48.92 -13.32,-34.4 8.87,-44.11 18.13,-17.46 21.38,-10.83 35.1,-18.63 22.7,-12.91 17.79,-16.05 19.26,-42.9l-26.67 -4.67c21.31,16.92 11.87,-7.02 15.87,18.77 2.72,17.58 -0.87,19.79 -54.72,37.77 -62.09,20.71 -96.24,-9.19 -104.53,-12.87 -130.65,-58.05 -1507.74,11.68 -1746.76,63.06 -249.52,53.64 -35.41,36.65 -196.37,83.39 -29.69,8.62 -85.77,19.57 -97.45,47.15 61.12,-9.09 144.09,-42.78 202.89,-15.75 -29.52,4.78 -149.92,13.1 -159.54,37.09l48.2 3.02c-1.98,23.5 -6.28,10.2 -24.52,27.28 48.15,13.79 41.11,-10.73 105.63,8.87 -21.38,22.37 -9.42,-6.74 -96,55.72 16.03,19.84 16.91,16.13 24.22,37.87 -36.27,1.57 -76.92,6.24 -107.04,-3.18 2.98,24.1 21.27,14.47 38.43,13.25 71.26,-5.07 144.87,-15.19 204.33,-10.5 46.36,3.67 274.91,2.96 300.63,-14.84z"}},
    {"shape":"path","role":"ring","fill":"#F1EEE3","attrs":{"d":"M804 586.64c118.21,61.35 870.23,-24.39 890.83,-35.22 -141.48,-23.44 -686.09,-0.94 -890.83,35.22z"}},
    {"shape":"path","role":"ring","fill":"#7C7A75","attrs":{"d":"M1822.97 843.61c-12.53,-23.18 -4.92,-1.29 -10.08,-28.17 93,-12.05 193.9,-12.56 298.37,-29.01 39.53,-6.24 74.81,-7.35 90.79,-31.17 -6.51,-17.59 -6.74,-19.28 -26.84,-20.56 -27.74,-5.19 -48.91,-12.21 -79.11,-9.58 -7.73,13.22 10.2,23.59 -54.58,35.06 -37.39,0.66 -122.89,18.92 -172.12,24.24 -184.21,19.93 -1206.81,95.19 -1310.24,90.08 -25.72,17.8 -254.26,18.51 -300.63,14.84 -59.46,-4.69 -133.07,5.43 -204.33,10.5 -17.15,1.22 -35.44,10.85 -38.43,-13.25 30.13,9.42 70.77,4.76 107.04,3.18 -7.31,-21.74 -8.2,-18.03 -24.22,-37.87 86.58,-62.45 74.62,-33.35 96,-55.72 -64.53,-19.6 -57.49,4.91 -105.63,-8.87 18.24,-17.08 22.53,-3.77 24.52,-27.28l-48.2 -3.02c9.61,-23.99 130.02,-32.32 159.54,-37.09 -58.79,-27.03 -141.77,6.65 -202.89,15.75 11.68,-27.58 67.76,-38.53 97.45,-47.15 160.95,-46.74 -53.15,-29.75 196.37,-83.39 239.02,-51.38 1616.1,-121.11 1746.76,-63.06 8.29,3.68 42.44,33.58 104.53,12.87 53.85,-17.98 57.44,-20.19 54.72,-37.77 -4,-25.79 5.44,-1.85 -15.87,-18.77 -32.71,-1.41 -62.93,-8.59 -94.76,-11.95 -64.9,-6.86 -234.78,-4.01 -303.86,-3.69l-1.89 -12.42c-39.01,31.29 -31.13,23.06 -110.51,25.26 -465.74,12.94 -933.89,44.69 -1397,83.93 -100.98,8.55 -281.89,10.25 -296.12,101.91 -9.55,61.44 22.97,58.29 48.39,87.84 -8.94,29.17 -23.16,29.28 -35.19,56.81 -8.31,19.02 -16.02,57.16 -12.83,80.25 62.97,33.79 121.1,31.51 207.71,29.01l698.86 -34.11 686.4 -39.68c46.98,-2.45 198.71,-22.92 227.92,-17.94z"}},
    {"shape":"path","role":"ring","fill":"#2A2424","attrs":{"d":"M559.15 874.49c103.43,5.11 1126.04,-70.15 1310.24,-90.08 49.23,-5.33 134.73,-23.59 172.12,-24.24 7.95,-7.48 16.62,-9.97 27.02,-14.37l-5.6 -20.28c-121.07,-8.99 -1193.45,80.34 -1368.87,92.65 -51.25,3.6 -420.73,42.82 -434.58,50.48 88.18,33.42 208.99,-7.27 299.67,5.84z"}},
    {"shape":"path","role":"ring","fill":"#7B746C","attrs":{"d":"M919.14 671.12c-15.1,10.83 -42.36,9.71 -61.32,16.15 88.06,-1.52 96.89,9.57 185.15,-8.3 63.29,-12.82 48.49,-0.32 54.59,9.2 -21.27,47.82 -273.71,8.23 -323.59,72.31 24.65,63.1 274.8,22.59 345.19,19.36 43.6,-2 323.7,-9.55 355.85,-35.05 -145.56,3 -553.53,59.56 -660.97,14.86 44.44,-40.52 805.35,-67.08 863.87,-42.75l-66.8 16.86c24.46,4.86 141.74,-7.91 156.21,-23.4 -41.68,-11.72 -96.58,-7.89 -141.15,-9.19 0.09,-0.11 5.67,-18.5 23.64,-13.01 -18.62,-62 -29.55,-50.84 -13.96,-40.86l-253.19 -1.41c-56.58,-6.88 -168.06,7.76 -230.65,10.91 -66.85,3.35 -171.32,17.55 -232.87,14.32z"}},
    {"shape":"path","role":"ring","fill":"#F1EEE3","attrs":{"d":"M1475.02 744.79c43.98,-1.98 90.13,-18.91 136.09,-11.03l66.8 -16.86c-58.52,-24.33 -819.42,2.23 -863.87,42.75 107.44,44.7 515.41,-11.86 660.97,-14.86z"}},
    {"shape":"path","role":"ring","fill":"#5B544C","attrs":{"d":"M563.69 877.15c-90.69,-13.1 -211.5,27.59 -299.68,-5.83 13.86,-7.66 383.33,-46.88 434.58,-50.48 175.42,-12.32 1247.81,-101.64 1368.87,-92.65l5.61 20.28c-10.41,4.4 -19.08,6.89 -27.03,14.37 64.79,-11.47 46.86,-21.85 54.58,-35.07 30.2,-2.63 51.38,4.39 79.11,9.58l-5.16 -17.43c89.21,-69.86 2.42,-1.4 106.1,-60.06 -95.7,1.08 -134.32,-3.28 -252.05,-0.67 -82.03,1.82 -168.5,8.86 -250.82,4.33l7.12 -11.29 -35.89 -0.32c10.07,-17.34 9.06,-7.23 4.74,-17.14 -62.42,1.27 -331.9,-0.7 -366.59,13.79l253.19 1.42c-15.58,-9.98 -4.65,-21.16 13.96,40.86 -17.97,-5.49 -23.55,12.89 -23.64,13 44.58,1.29 99.48,-2.53 141.15,9.2 -14.46,15.47 -131.75,28.26 -156.21,23.4 -45.96,-7.87 -92.11,9.05 -136.08,11.03 -32.15,25.5 -312.26,33.05 -355.86,35.04 -70.4,3.23 -320.54,43.73 -345.19,-19.36 49.89,-64.07 302.33,-24.47 323.59,-72.3 -6.09,-9.52 8.71,-22.01 -54.58,-9.2 -88.26,17.87 -97.09,6.78 -185.16,8.3 18.96,-6.44 46.22,-5.32 61.32,-16.15 -37.39,-2.31 -177.61,15.64 -222.5,23.1 -23.22,3.86 -55.87,2.12 -81.84,4.3 -9.69,29.68 2.63,-12.27 8.88,13.13l-426.12 29.67c29.9,-11.98 50.41,-1.56 72.22,-23.43 20.24,-20.27 -8.7,-7.15 16.53,-18.22 62.53,-17.7 338.67,-45.66 429.82,-47.43 109.11,-2.12 547.38,-35.23 657.46,-48.49 56.85,-6.84 602.86,-49.85 657.32,-25.74 8.56,3.79 6.45,3.44 11.42,7.29 -21.12,32.65 -30.39,-1.53 -51.28,30.91 75.11,15.4 102.54,12.03 178.25,-7.52 10.3,-48.92 -13.31,-34.41 8.87,-44.12 18.13,-17.46 21.38,-10.82 35.1,-18.62 22.7,-12.91 17.79,-16.05 19.26,-42.91l-26.66 -4.66c21.31,16.92 11.87,-7.02 15.87,18.77 2.72,17.57 -0.88,19.79 -54.73,37.77 -62.09,20.71 -96.24,-9.19 -104.53,-12.87 -130.64,-58.05 -1507.74,11.68 -1746.75,63.06 -249.53,53.64 -35.42,36.65 -196.37,83.39 -29.7,8.62 -85.77,19.57 -97.46,47.15 61.12,-9.1 144.1,-42.78 202.89,-15.75 -29.52,4.77 -149.91,13.1 -159.54,37.09l48.21 3.01c-1.99,23.51 -6.29,10.21 -24.53,27.29 48.15,13.78 41.11,-10.73 105.65,8.87 -21.39,22.37 -9.42,-6.74 -96.01,55.72 16.04,19.84 16.92,16.13 24.23,37.87 -36.27,1.57 -76.93,6.23 -107.05,-3.18 2.99,24.1 21.27,14.47 38.43,13.25 71.26,-5.07 144.87,-15.2 204.33,-10.5 46.36,3.66 274.91,2.96 300.64,-14.85z"}},
    {"shape":"path","role":"ring","fill":"#F1EEE3","attrs":{"d":"M808.53 589.3c118.22,61.36 870.23,-24.38 890.83,-35.22 -141.48,-23.43 -686.08,-0.94 -890.83,35.22z"}},
    {"shape":"path","role":"ring","fill":"#7C7A75","attrs":{"d":"M1827.51 846.28c-12.54,-23.18 -4.93,-1.3 -10.09,-28.17 93,-12.05 193.9,-12.56 298.38,-29.02 39.52,-6.23 74.8,-7.34 90.77,-31.16 -6.5,-17.6 -6.73,-19.29 -26.84,-20.57 -27.73,-5.19 -48.91,-12.21 -79.11,-9.58 -7.72,13.22 10.21,23.6 -54.58,35.07 -37.39,0.66 -122.88,18.91 -172.12,24.23 -184.21,19.94 -1206.81,95.19 -1310.23,90.08 -25.73,17.81 -254.27,18.51 -300.64,14.85 -59.46,-4.7 -133.07,5.43 -204.33,10.5 -17.15,1.22 -35.43,10.85 -38.43,-13.25 30.13,9.41 70.78,4.76 107.05,3.18 -7.31,-21.74 -8.2,-18.03 -24.23,-37.87 86.59,-62.45 74.62,-33.35 96.01,-55.72 -64.54,-19.6 -57.5,4.9 -105.65,-8.87 18.24,-17.08 22.53,-3.77 24.53,-27.29l-48.21 -3.01c9.62,-23.99 130.02,-32.33 159.54,-37.09 -58.78,-27.03 -141.77,6.64 -202.89,15.75 11.69,-27.58 67.76,-38.53 97.46,-47.15 160.94,-46.74 -53.16,-29.75 196.37,-83.39 239.01,-51.38 1616.1,-121.11 1746.75,-63.06 8.29,3.68 42.44,33.58 104.53,12.87 53.85,-17.98 57.45,-20.2 54.73,-37.77 -4,-25.79 5.44,-1.85 -15.87,-18.77 -32.72,-1.41 -62.94,-8.59 -94.77,-11.96 -64.9,-6.86 -234.77,-4 -303.86,-3.68l-1.89 -12.42c-39.01,31.29 -31.12,23.06 -110.5,25.26 -465.75,12.94 -933.89,44.69 -1397,83.93 -100.98,8.55 -281.9,10.25 -296.13,101.91 -9.54,61.44 22.97,58.29 48.39,87.84 -8.93,29.17 -23.15,29.27 -35.19,56.81 -8.3,19.02 -16.02,57.16 -12.83,80.25 62.97,33.79 121.11,31.51 207.71,29.01l698.86 -34.12 686.41 -39.67c46.98,-2.46 198.7,-22.92 227.92,-17.94z"}},
    {"shape":"path","role":"ring","fill":"#2A2424","attrs":{"d":"M563.69 877.15c103.42,5.11 1126.03,-70.14 1310.23,-90.08 49.24,-5.33 134.73,-23.58 172.12,-24.23 7.95,-7.48 16.62,-9.97 27.03,-14.37l-5.61 -20.28c-121.06,-8.99 -1193.45,80.34 -1368.87,92.65 -51.25,3.6 -420.72,42.82 -434.58,50.48 88.18,33.42 208.99,-7.27 299.68,5.83z"}},
    {"shape":"path","role":"ring","fill":"#7B746C","attrs":{"d":"M923.66 673.79c-15.1,10.83 -42.36,9.71 -61.32,16.15 88.06,-1.52 96.9,9.57 185.16,-8.3 63.29,-12.82 48.49,-0.32 54.58,9.2 -21.26,47.82 -273.7,8.23 -323.59,72.3 24.65,63.1 274.8,22.6 345.19,19.36 43.6,-1.99 323.71,-9.54 355.86,-35.04 -145.57,3 -553.53,59.56 -660.98,14.86 44.44,-40.52 805.35,-67.09 863.88,-42.75l-66.81 16.86c24.46,4.86 141.75,-7.93 156.21,-23.4 -41.67,-11.73 -96.57,-7.9 -141.15,-9.2 0.09,-0.11 5.67,-18.49 23.64,-13 -18.61,-62.01 -29.54,-50.84 -13.96,-40.86l-253.19 -1.42c-56.58,-6.87 -168.06,7.77 -230.64,10.91 -66.86,3.36 -171.32,17.56 -232.88,14.33z"}},
    {"shape":"path","role":"ring","fill":"#F1EEE3","attrs":{"d":"M1479.55 747.46c43.97,-1.98 90.13,-18.91 136.08,-11.03l66.81 -16.86c-58.53,-24.34 -819.43,2.23 -863.88,42.75 107.45,44.7 515.41,-11.86 660.98,-14.86z"}}
  ];

  function renderAdloopPreview(schema, state, variantOptionsPayload, variantId) {
    const values = state?.values || {};
    const ribbonValue = values.ribbon_colour || "White";
    const endcapValue = values.endcap_colour || "Black";
    const undersideValue = values.underside_colour || "White";
    const ribbonColour = getAdloopChoiceSwatch(schema, "ribbon_colour", ribbonValue, "#ffffff", state);
    const endcapColour = getAdloopChoiceSwatch(schema, "endcap_colour", endcapValue, "#111111", state);
    const undersideColour = getAdloopChoiceSwatch(schema, "underside_colour", undersideValue, "#ffffff", state);
    const topFlood = norm(values.top_background_method).includes("flood print");
    const undersideFlood = norm(values.underside_background_method).includes("flood print");
    const topFloodValue = values.top_flood_colour || "Black";
    const undersideFloodValue = values.underside_flood_colour || "Black";
    const topSurfaceColour = topFlood
      ? getAdloopChoiceSwatch(schema, "top_flood_colour", topFloodValue, "#111111", state)
      : ribbonColour;
    const undersideSurfaceColour = undersideFlood
      ? getAdloopChoiceSwatch(schema, "underside_flood_colour", undersideFloodValue, "#111111", state)
      : ribbonColour;

    const optionValues = getVariantOptionValues(variantOptionsPayload, variantId);
    const plainUnderside = optionValues.some((value) => norm(value).includes("plain underside"));
    const topKeys = [
      "text_colour_top_1",
      "text_colour_top_1_multi",
      "text_colour_top_2",
      "text_colour_top_3",
      "text_colour_top_4"
    ];
    const visibleTopKeys = topKeys.filter((key) => {
      const field = (schema?.fields || []).find((item) => item?.key === key);
      return field && shouldShowField(field, state, variantOptionsPayload, variantId);
    });
    const selectedTopKeys = visibleTopKeys.filter((key) => values[key]);
    const firstTopKey = selectedTopKeys[0] || visibleTopKeys[0] || "text_colour_top_1";
    const topPrintValue = topFlood && !selectedTopKeys.length
      ? "White (reversed out)"
      : (values[firstTopKey] || "Black");
    const topPrintColour = topFlood && !selectedTopKeys.length
      ? "#ffffff"
      : getAdloopChoiceSwatch(schema, firstTopKey, topPrintValue, "#111111", state);
    const undersidePrintValue = undersideFlood
      ? "White (reversed out)"
      : (values.text_colour_underside || "Black");
    const undersidePrintColour = undersideFlood
      ? "#ffffff"
      : getAdloopChoiceSwatch(
        schema,
        "text_colour_underside",
        undersidePrintValue,
        "#111111",
        state
      );
    const enteredText = (values.text_info || "").toString().trim();
    const previewText = enteredText ? enteredText.slice(0, 58) : "YOUR TEXT / LOGO";
    const loopPreviewText = previewText.slice(
      0,
      Math.min(24, Math.max(1, Math.ceil(previewText.length / 2)))
    );
    const enteredUndersideText = (values.text_info_underside || "").toString().trim();
    const undersidePreviewText = enteredUndersideText
      ? enteredUndersideText.slice(0, 58)
      : "UNDERSIDE TEXT / LOGO";
    const loopUndersidePreviewText = undersidePreviewText.slice(
      0,
      Math.min(14, Math.max(1, Math.ceil(undersidePreviewText.length / 3)))
    );
    const fontFamily = getAdloopFontFamily(values.font);
    const previewId = ++adloopPreviewCounter;
    const loopTopTextPathId = `ipo-adloop-loop-top-path-${previewId}`;
    const loopUndersideTextPathId = `ipo-adloop-loop-underside-path-${previewId}`;

    const box = document.createElement("section");
    box.className = "ipo-adloop-preview";
    box.setAttribute("data-ipo-adloop-preview", "");

    const heading = document.createElement("div");
    heading.className = "ipo-adloop-preview-head";
    heading.textContent = "Your Ad-Loop preview";

    const stage = document.createElement("div");
    stage.className = "ipo-adloop-preview-stage";
    const svg = createSvgElement("svg", {
      viewBox: plainUnderside ? "0 0 720 390" : "0 0 720 465",
      role: "img",
      "aria-label": "Approximate preview of the selected Ad-Loop colours and text"
    });

    const defs = createSvgElement("defs");
    const loopTopTextPath = createSvgElement("path", {
      id: loopTopTextPathId,
      d: "M8150 21700 C10000 25300 13700 27350 18500 25550"
    });
    const loopUndersideTextPath = createSvgElement("path", {
      id: loopUndersideTextPathId,
      d: "M9400 21450 C12800 21750 15500 22450 17700 23550"
    });
    defs.append(loopTopTextPath, loopUndersideTextPath);
    svg.appendChild(defs);

    const loopLabel = createSvgElement("text", {
      x: 18, y: 24, fill: "#002046", "font-size": 15, "font-weight": 700
    });
    loopLabel.textContent = "LOOP VIEW";
    svg.appendChild(loopLabel);

    const loopGuide = createSvgElement("text", {
      x: 112, y: 24, fill: "#5b6571", "font-size": 11
    });
    loopGuide.textContent = "top side at front • underside at rear";
    svg.appendChild(loopGuide);

    // This is the cleaned vector supplied by Intaprint, cropped and scaled to
    // roughly two-thirds of the full straight-strip view.
    const tracedLoop = createSvgElement("g", {
      transform: "translate(132 38) scale(.026) translate(-2140 -18840)"
    });
    const undersideLoopColour = plainUnderside ? undersideColour : undersideSurfaceColour;
    const vectorFill = (role) => {
      if (role === "top-ribbon" || role === "top-edge") return topSurfaceColour;
      if (role === "underside") return undersideLoopColour;
      if (role === "cap" || role === "cap-side" || role === "cap-detail") return endcapColour;
      if (role === "cap-highlight") return "#ffffff";
      if (role === "opening") return "#ffffff";
      if (role === "ring-shadow") return "#8d9297";
      if (role === "ring-mid") return "#c8ccd0";
      if (role === "ring-light") return "#f4f5f6";
      if (role === "ring-inner") return "#ffffff";
      if (role === "ring-body") return "#c6cbd0";
      return "#111111";
    };
    ADLOOP_VECTOR_PATHS.forEach(({ role, d }) => {
      const attributes = { d, fill: vectorFill(role) };
      if (role === "cap-highlight") attributes["fill-opacity"] = ".62";
      tracedLoop.appendChild(createSvgElement("path", attributes));
    });

    const loopTextSize = Math.max(720, 1020 - loopPreviewText.length * 6.5);
    const topLoopText = createSvgElement("text", {
      fill: topPrintColour,
      "font-size": loopTextSize,
      "font-family": fontFamily,
      "font-weight": 600
    });
    const topLoopTextPath = createSvgElement("textPath", {
      href: `#${loopTopTextPathId}`,
      startOffset: "100%",
      "text-anchor": "end"
    });
    topLoopTextPath.textContent = loopPreviewText;
    topLoopText.appendChild(topLoopTextPath);
    tracedLoop.appendChild(topLoopText);

    if (!plainUnderside) {
      const undersideLoopTextSize = Math.max(
        720,
        1020 - loopUndersidePreviewText.length * 6.5
      );
      const undersideText = createSvgElement("text", {
        fill: undersidePrintColour,
        "font-size": undersideLoopTextSize,
        "font-family": fontFamily,
        "font-weight": 600
      });
      const undersideTextPath = createSvgElement("textPath", {
        href: `#${loopUndersideTextPathId}`,
        startOffset: "100%",
        "text-anchor": "end"
      });
      undersideTextPath.textContent = loopUndersidePreviewText;
      undersideText.appendChild(undersideTextPath);
      tracedLoop.appendChild(undersideText);
    }
    svg.appendChild(tracedLoop);

    const topLabel = createSvgElement("text", {
      x: 18, y: 288, fill: "#002046", "font-size": 15, "font-weight": 700
    });
    topLabel.textContent = "TOP SIDE";
    svg.appendChild(topLabel);

    // Flat views rebuilt from the supplied top-side vector. The same physical
    // ring, cap and ribbon are used for each printed face.
    const topVectorFill = (role, originalFill, surfaceColour) => {
      if (role === "ribbon") return surfaceColour;
      if (role === "cap-main") return endcapColour;
      return originalFill;
    };
    const createFlatView = (textValue, printColour, surfaceColour, translateY) => {
      const group = createSvgElement("g", {
        transform: `translate(8 ${translateY}) scale(.0364)`
      });

      ADLOOP_TOP_VECTOR_SHAPES.forEach((item) => {
        const attributes = {
          ...item.attrs,
          fill: topVectorFill(item.role, item.fill, surfaceColour)
        };
        if (item.stroke) {
          attributes.stroke = item.stroke;
          attributes["stroke-width"] = item.strokeWidth;
          attributes["stroke-miterlimit"] = "22.9256";
        }
        group.appendChild(createSvgElement(item.shape, attributes));
      });

      const fontSize = Math.max(
        300,
        Math.min(500, 13600 / Math.max(18, textValue.length * .58))
      );
      const text = createSvgElement("text", {
        x: 11335,
        y: 790,
        fill: printColour,
        "font-size": fontSize,
        "font-family": fontFamily,
        "font-weight": 600,
        "text-anchor": "middle"
      });
      text.textContent = textValue;
      group.appendChild(text);
      return group;
    };

    svg.appendChild(createFlatView(previewText, topPrintColour, topSurfaceColour, 300));

    if (!plainUnderside) {
      const undersideLabel = createSvgElement("text", {
        x: 18, y: 373, fill: "#002046", "font-size": 15, "font-weight": 700
      });
      undersideLabel.textContent = "UNDERSIDE";
      svg.appendChild(undersideLabel);
      svg.appendChild(createFlatView(
        undersidePreviewText,
        undersidePrintColour,
        undersideSurfaceColour,
        385
      ));
    }

    stage.appendChild(svg);

    const selection = document.createElement("div");
    selection.className = "ipo-adloop-preview-selection";
    const addSelection = (label, value, colour) => {
      const item = document.createElement("span");
      item.className = "ipo-adloop-preview-selection-item";
      const dot = document.createElement("span");
      dot.className = "ipo-adloop-preview-selection-dot";
      dot.style.background = colour || "transparent";
      const text = document.createElement("span");
      text.textContent = `${label}: ${value || "Not selected"}`;
      item.append(dot, text);
      selection.appendChild(item);
    };

    addSelection("Ribbon", ribbonValue, ribbonColour);
    addSelection("End cap", endcapValue, endcapColour);
    if (topFlood) {
      addSelection(
        "Top flood",
        getAdloopColourLabel(schema, state, "top_flood_colour", topFloodValue),
        topSurfaceColour
      );
      if (!selectedTopKeys.length) {
        addSelection("Top wording", "White (reversed out)", "#ffffff");
      }
    }
    selectedTopKeys.forEach((key, index) => {
      addSelection(
        topFlood
          ? `Additional top print ${index + 1}`
          : (selectedTopKeys.length > 1 ? `Top print ${index + 1}` : "Top print"),
        getAdloopColourLabel(schema, state, key, values[key]),
        getAdloopChoiceSwatch(schema, key, values[key], "#111111", state)
      );
    });
    if (plainUnderside) {
      addSelection("Plain underside", undersideValue, undersideColour);
    } else if (undersideFlood) {
      addSelection(
        "Underside flood",
        getAdloopColourLabel(schema, state, "underside_flood_colour", undersideFloodValue),
        undersideSurfaceColour
      );
      addSelection("Underside wording", "White (reversed out)", "#ffffff");
    } else if (values.text_colour_underside) {
      addSelection(
        "Underside print",
        getAdloopColourLabel(schema, state, "text_colour_underside", undersidePrintValue),
        undersidePrintColour
      );
    }

    const note = document.createElement("div");
    note.className = "ipo-adloop-preview-note";
    const previewColourKeys = [
      ...(topFlood ? ["top_flood_colour"] : []),
      ...selectedTopKeys,
      ...(plainUnderside ? [] : [undersideFlood ? "underside_flood_colour" : "text_colour_underside"])
    ];
    const customPantoneUsed = previewColourKeys.some((key) =>
      norm(values[key]) === "custom pantone colour"
    );
    note.textContent = customPantoneUsed
      ? "Pantone colours are approximate on screen. Production will use the supplied Pantone reference, and exact colour placement will be confirmed on your proof."
      : (topFlood && selectedTopKeys.length
        ? "Approximate preview only. The first additional print colour is applied to the wording; exact colour placement will be confirmed on your proof."
        : "Approximate colour and text preview only. We will optimise the artwork and send a proof before production.");

    box.append(heading, stage, selection, note);
    return box;
  }

  function renderAdloopHelp() {
    const help = document.createElement("div");
    help.className = "ipo-adloop-help";

    const strong = document.createElement("strong");
    strong.textContent = "How the printing works: ";
    help.appendChild(strong);
    help.appendChild(document.createTextNode(
      "White ribbon can use any standard or custom Pantone print colour. Yellow ribbon can use Black, Fresh Green, Sky Blue or Bright Red. Orange, Kiwi and fluorescent ribbons use Black only. Flood printing uses White ribbon and counts as one print colour; white wording is reversed out. Either printed side can be flood printed. A plain underside is solid-colour plastic, while a printed underside is limited to one print colour. When printing on both sides, the same ribbon colour is used."
    ));
    return help;
  }

  function refreshAdloopPreview(root, schema, state, variantOptionsPayload, variantId) {
    if (!isAdloopPreviewEnabled(schema)) return;
    const existing = root.querySelector("[data-ipo-adloop-preview]");
    if (!existing) return;
    existing.replaceWith(renderAdloopPreview(schema, state, variantOptionsPayload, variantId));
  }

  function renderCustomPantoneControls(
    field,
    root,
    schema,
    state,
    variantOptionsPayload,
    variantId,
    updateHidden
  ) {
    const referenceKey = customPantoneReferenceKey(field);
    const previewKey = customPantonePreviewKey(field);
    const automaticKey = `${previewKey}_automatic`;
    const initialMatch = lookupPantonePreviewColour(state.values[referenceKey]);
    if (!state.values[previewKey]) {
      state.values[previewKey] = initialMatch || "#6b7280";
      state.values[automaticKey] = !!initialMatch;
    }

    const panel = document.createElement("div");
    panel.className = "ipo-pantone-controls";

    const referenceWrap = document.createElement("div");
    referenceWrap.className = "ipo-pantone-reference";
    const referenceLabel = document.createElement("label");
    referenceLabel.className = "ipo-pantone-control-label";
    referenceLabel.textContent = "Pantone reference *";
    const referenceInput = document.createElement("input");
    referenceInput.type = "text";
    referenceInput.className = "ipo-input";
    referenceInput.placeholder = "e.g. Pantone 186 C";
    referenceInput.value = state.values[referenceKey] || "";
    referenceInput.setAttribute("aria-label", `${customPantoneSlotLabel(field)} Pantone reference`);
    referenceWrap.append(referenceLabel, referenceInput);

    const previewWrap = document.createElement("div");
    const previewLabel = document.createElement("label");
    previewLabel.className = "ipo-pantone-control-label";
    previewLabel.textContent = "Approximate preview colour";
    const previewRow = document.createElement("div");
    previewRow.className = "ipo-pantone-preview-row";
    const previewInput = document.createElement("input");
    previewInput.type = "color";
    previewInput.className = "ipo-pantone-colour";
    previewInput.value = customPantonePreviewColour(field, state);
    previewInput.setAttribute("aria-label", `${customPantoneSlotLabel(field)} approximate preview colour`);
    const previewHex = document.createElement("span");
    previewHex.className = "ipo-pantone-hex";
    previewHex.textContent = previewInput.value.toUpperCase();
    previewRow.append(previewInput, previewHex);
    previewWrap.append(previewLabel, previewRow);

    const note = document.createElement("p");
    note.className = "ipo-pantone-note";
    const updateMatchNote = () => {
      const reference = referenceInput.value.trim();
      const match = lookupPantonePreviewColour(reference);
      if (match) {
        note.dataset.match = "yes";
        note.textContent = `✓ ${reference} matched automatically. The on-screen colour is approximate; production will use the entered Pantone reference.`;
      } else if (reference) {
        note.dataset.match = "no";
        note.textContent = "No automatic screen match was found. Please choose the closest approximate preview colour; production will still use the Pantone reference entered above.";
      } else {
        note.dataset.match = "";
        note.textContent = "Enter a Pantone reference to match its approximate screen colour automatically.";
      }
    };

    referenceInput.addEventListener("input", () => {
      state.values[referenceKey] = referenceInput.value;
      const match = lookupPantonePreviewColour(referenceInput.value);
      if (match) {
        state.values[previewKey] = match;
        state.values[automaticKey] = true;
        previewInput.value = match;
        previewHex.textContent = match.toUpperCase();
      } else if (state.values[automaticKey]) {
        state.values[previewKey] = "#6b7280";
        state.values[automaticKey] = false;
        previewInput.value = "#6b7280";
        previewHex.textContent = "#6B7280";
      }
      updateMatchNote();
      clearFieldErrors(root);
      setError(root, "");
      updateHidden();
      refreshAdloopPreview(root, schema, state, variantOptionsPayload, variantId);
    });

    previewInput.addEventListener("input", () => {
      state.values[previewKey] = previewInput.value;
      state.values[automaticKey] = false;
      previewHex.textContent = previewInput.value.toUpperCase();
      clearFieldErrors(root);
      setError(root, "");
      updateHidden();
      refreshAdloopPreview(root, schema, state, variantOptionsPayload, variantId);
    });

    updateMatchNote();

    panel.append(referenceWrap, previewWrap, note);
    return panel;
  }

  function renderFields(root, schema, state, variantOptionsPayload, variantId) {
    state._schema = schema;
    const host = $("[data-ipo-fields]", root);
    if (!host) return;
    host.innerHTML = "";

    if (isExactRepeatMode(state)) {
      host.appendChild(
        renderExactRepeatSummary(
          schema,
          state,
          variantOptionsPayload,
          variantId
        )
      );
    }
    let eddInserted = false;
    const selectedType = getSelectedVariantType(variantOptionsPayload, variantId);
    const isBlankVariant = norm(selectedType).includes("blank");
    const isPrintedProduct = norm(selectedType).includes("printed");

    if (isPrintedProduct && !isExactRepeatMode(state)) {
      host.appendChild(renderSavedArtworkGuide());
    }

    if (isAdloopPreviewEnabled(schema) && !isExactRepeatMode(state)) {
      host.appendChild(renderAdloopHelp());
      host.appendChild(renderAdloopPreview(schema, state, variantOptionsPayload, variantId));
    }

    (schema.fields || []).forEach((f) => {
      if (!shouldShowField(f, state, variantOptionsPayload, variantId)) return;
      if (isExactRepeatMode(state) && !isExactRepeatEditableField(f)) return;

      const wrap = document.createElement("div");
      wrap.className = "ipo-field";
      wrap.setAttribute("data-ipo-field-key", f.key);

      const labelText = displayText(resolveDynamicText(f.labelWhen, state, variantOptionsPayload, variantId) || f.label);

      const label = document.createElement("label");
      label.className = "ipo-label";
      label.textContent = labelText;
      if (isFieldRequired(f, state)) {
        const star = document.createElement("span");
        star.className = "ipo-required";
        star.textContent = "*";
        label.appendChild(star);
      }
      wrap.appendChild(label);

      if (f.intro) {
        const intro = document.createElement("div");
        intro.className = "ipo-field-intro";
        intro.textContent = displayText(f.intro);
        wrap.appendChild(intro);
      }

      const form = getProductForm();
      const updateHidden = () => { if (form) syncHiddenProps(form, schema, state, variantOptionsPayload, variantId); };

      const rerender = () => {
        renderFields(root, schema, state, variantOptionsPayload, variantId);
        if (typeof window.IntaprintDiscountTableRefresh === 'function') {
          window.IntaprintDiscountTableRefresh({ reason: 'ipo-option-change' });
        }
        updateHidden();
      };

      if (f.type === "select" && (f.display === "swatches" || f.render === "swatches")) {
        wrap.appendChild(renderSwatchDropdown(f, state, rerender, variantOptionsPayload, variantId));
        if (isCustomPantoneSelection(f, state)) {
          wrap.appendChild(renderCustomPantoneControls(
            f,
            root,
            schema,
            state,
            variantOptionsPayload,
            variantId,
            updateHidden
          ));
        }
      } else if (f.type === "select") {
        clearUnavailablePrintsOnlySelection(f, state);
        clearUnavailableChoiceSelection(f, state, variantOptionsPayload, variantId);

        const sel = document.createElement("select");
        sel.className = "ipo-select";
        sel.required = !!isFieldRequired(f, state);

        const ph = document.createElement("option");
        ph.value = "";
        ph.textContent = f.placeholder || "Select a value";
        sel.appendChild(ph);

        getVisibleChoices(f, state, variantOptionsPayload, variantId).forEach((c) => {
          const o = document.createElement("option");
          o.value = c.value ?? c;
          o.textContent = getChoiceLabel(f, c);
          sel.appendChild(o);
        });

        if ((state.values[f.key] == null || state.values[f.key] === "") && f.default != null && f.default !== "") {
          const defaultValue = String(f.default);
          const defaultVisible = getVisibleChoices(f, state, variantOptionsPayload, variantId).some(
            (choice) => norm(choiceValue(choice)) === norm(defaultValue)
          );
          if (defaultVisible) state.values[f.key] = defaultValue;
        }

        sel.value = (state.values[f.key] ?? "");
        sel.addEventListener("change", () => {
          clearPrimaryArtworkOnModeChange(root, schema, state, state.values[f.key], sel.value);
          state.values[f.key] = sel.value;
          clearFieldErrors(root);
          setError(root, "");

          if (isExpressProductionFieldDef(f)) {
            if (!isExpressProductionSelected(state)) {
              delete state.values.express_production_date;
            }
            applyExpressProductionChargeToDiscountTable(state);
          }

          rerender();
        });
        wrap.appendChild(sel);

      } else if (f.type === "tiles") {
        const grid = document.createElement("div");
        grid.className = "ipo-tiles";
        clearUnavailablePrintsOnlySelection(f, state);
        clearUnavailableChoiceSelection(f, state, variantOptionsPayload, variantId);
        const choices = getVisibleChoices(f, state, variantOptionsPayload, variantId);
        const current = (state.values[f.key] ?? "").toString();

        choices.forEach((c) => {
          // Allow either a string or an object: { label, value, image }
          const label = (typeof c === "string") ? c : (c.label ?? c.value ?? "").toString();
          const value = (typeof c === "string") ? c : (c.value ?? c.label ?? "").toString();
          const imgUrl = (typeof c === "object" && c) ? (c.image || c.img || c.icon || "") : "";

          const tile = document.createElement("button");
          tile.type = "button";
          tile.className = "ipo-tile";
          tile.setAttribute("aria-selected", (norm(current) === norm(value)) ? "true" : "false");
          tile.title = label;

          if (imgUrl) {
            const img = document.createElement("img");
            img.className = "ipo-tile-img";
            img.alt = label;
            img.loading = "lazy";
            img.src = imgUrl;
            tile.appendChild(img);
          } else {
            const t = document.createElement("div");
            t.className = "ipo-tile-text";
            t.textContent = label || value;
            tile.appendChild(t);
          }

          tile.addEventListener("click", () => {
            clearPrimaryArtworkOnModeChange(root, schema, state, state.values[f.key], value);
            state.values[f.key] = value;
            rerender();
          });

          grid.appendChild(tile);
        });

        wrap.appendChild(grid);
      } else if (f.type === "textarea") {
        const ta = document.createElement("textarea");
        ta.className = "ipo-textarea";
        ta.rows = f.rows || 4;
        ta.placeholder = f.placeholder || "";
        ta.value = (state.values[f.key] ?? "");
        ta.addEventListener("input", () => {
          state.values[f.key] = ta.value;
          clearFieldErrors(root);
          setError(root, "");
          updateHidden();
        });
        wrap.appendChild(ta);
      } else if (f.type === "text") {
        const inp = document.createElement("input");
        inp.type = "text";
        inp.className = "ipo-input";
        inp.placeholder = f.placeholder || "";
        inp.value = (state.values[f.key] ?? "");
        inp.addEventListener("input", () => {
          state.values[f.key] = inp.value;
          clearFieldErrors(root);
          setError(root, "");
          updateHidden();
          refreshAdloopPreview(root, schema, state, variantOptionsPayload, variantId);
        });
        wrap.appendChild(inp);
      } else if (f.type === "file") {
        const row = document.createElement("div");
        row.className = "ipo-file-row";

        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = f.accept || "";
        fileInput.style.display = "none";

        const maxFiles = getEffectiveMaxFiles(f, state);
        const allowMultiple = maxFiles ? maxFiles > 1 : !!f.multiple;

        if (allowMultiple) {
          fileInput.multiple = true;
        } else {
          fileInput.removeAttribute("multiple");
        }

        const chooseButtonText = getEffectiveButtonText(
          f,
          state,
          displayText(
            resolveDynamicText(f.buttonTextWhen, state, variantOptionsPayload, variantId) ||
            (f.buttonText ? String(f.buttonText) : "Choose file")
          )
        );

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ipo-file-btn";

        const removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.className = "ipo-file-btn ipo-file-remove-btn";
        removeBtn.textContent = allowMultiple ? "Remove Files" : "Remove Artwork";
        removeBtn.style.display = "none";

        const meta = document.createElement("div");
        meta.className = "ipo-file-meta";

        const name = document.createElement("div");
        name.className = "ipo-file-name";
        name.textContent = "No file selected";

        const status = document.createElement("div");
        status.className = "ipo-file-status";
        status.textContent = "";

        meta.appendChild(name);
        meta.appendChild(status);

        const card = document.createElement("div");
        card.className = "ipo-upload-card";
        card.hidden = true;

        state.filePreviews = state.filePreviews || {};

        const getStoredUrls = () => {
          const current = state.values[f.key];
          if (!current) return [];
          if (Array.isArray(current)) return current.filter(Boolean);
          return String(current).split("\n").map((x) => x.trim()).filter(Boolean);
        };

        const setStoredUrls = (urls) => {
          const cleanUrls = (urls || []).filter(Boolean);
          state.values[f.key] = cleanUrls.length > 1 ? cleanUrls : (cleanUrls[0] || "");
        };

        const getStoredIds = () => getStoredFileIds(state, f.key);
        const setStoredIds = (ids) => setStoredFileIds(state, f.key, ids);

        const getUploadCount = () => getStoredUrls().length;

        const updateUploadUi = () => {
          const count = getUploadCount();
          const previews = state.filePreviews[f.key] || [];

          if (count) {
            name.textContent = "";
            status.textContent = "";
            btn.textContent = allowMultiple && (!maxFiles || count < maxFiles)
              ? "Add More Files"
              : (allowMultiple ? "Replace Files" : "Replace Artwork");
            removeBtn.style.display = "";
            renderUploadCard(card, previews, count, false);
          } else {
            name.textContent = "No file selected";
            status.textContent = "";
            btn.textContent = chooseButtonText;
            removeBtn.style.display = "none";
            renderUploadCard(card, [], 0, false);
          }
        };

        updateUploadUi();

        btn.addEventListener("click", () => fileInput.click());

        removeBtn.addEventListener("click", () => {
          setError(root, "");
          const idsToDelete = getStoredIds();
          fileInput.value = "";
          setStoredUrls([]);
          setStoredIds([]);
          clearFilePreviews(state, f.key);
          state.filePreviews = state.filePreviews || {};
          updateUploadUi();
          updateHidden();
          sendFileAction(root, "pending-delete", deletableFileIds(state, idsToDelete));

          const hasDependentFields = (schema.fields || []).some(
            (dependentField) => dependentField?.showAfterFileUpload === f.key
          );
          if (hasDependentFields) rerender();
        });

        fileInput.addEventListener("change", async () => {
          setError(root, "");
          const files = Array.from(fileInput.files || []);
          if (!files.length) return;

          const existingUrls = getStoredUrls();
          const existingIds = getStoredIds();
          const existingPreviews = state.filePreviews[f.key] || [];

          let remainingSlots = maxFiles ? Math.max(maxFiles - existingUrls.length, 0) : files.length;

          // If the customer has already reached the file limit and chooses files again,
          // treat this as a replace action rather than silently ignoring the selection.
          const shouldReplace = maxFiles && existingUrls.length >= maxFiles;
          const baseUrls = shouldReplace ? [] : existingUrls;
          const baseIds = shouldReplace ? [] : existingIds;
          const basePreviews = shouldReplace ? [] : existingPreviews;

          if (shouldReplace) {
            remainingSlots = maxFiles;
          }

          const useFiles = maxFiles ? files.slice(0, remainingSlots) : files;
          if (!useFiles.length) {
            fileInput.value = "";
            return;
          }

          // Shopify Files limits
          const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
          const MAX_MEGAPIXELS = 20;

          for (const file of useFiles) {

            // File size check
            if (file.size > MAX_FILE_SIZE) {
              setError(
                root,
                `"${file.name}" is too large. Maximum file size is 20MB.`
              );
              fileInput.value = "";
              return;
            }

            // Image resolution check
            if (fileLooksLikeImage(file)) {
              const dims = await readImageDimensions(file);

              if (dims) {
                const megapixels = (dims.width * dims.height) / 1000000;

                if (megapixels > MAX_MEGAPIXELS) {
                  setError(
                    root,
                    `"${file.name}" is too large (${megapixels.toFixed(1)}MP). Maximum image size is 20 megapixels.`
                  );
                  fileInput.value = "";
                  return;
                }
              }
            }
          }

          state.filePreviews = state.filePreviews || {};

          const newPreviews = await Promise.all(useFiles.map(async (file) => {
            const dims = await readImageDimensions(file);
            return {
              name: file.name,
              sizeLabel: formatFileSize(file.size),
              dimensions: dims ? `${dims.width} × ${dims.height} px` : "",
              previewUrl: fileLooksLikeImage(file) ? URL.createObjectURL(file) : "",
              isPdf: fileLooksLikePdf(file)
            };
          }));

          const uploadingPreviews = basePreviews.concat(newPreviews);
          state.filePreviews[f.key] = uploadingPreviews;

          name.textContent = "";
          status.textContent = "";
          removeBtn.style.display = "";
          renderUploadCard(card, uploadingPreviews, baseUrls.length + useFiles.length, true);

          try {
            const uploads = await uploadFiles(root, useFiles);
            const combinedUrls = baseUrls.concat(uploads.map((upload) => upload.fileUrl));
            const combinedIds = baseIds.concat(uploads.map((upload) => upload.fileId));
            setStoredUrls(combinedUrls);
            setStoredIds(combinedIds);
            if (shouldReplace && existingIds.length) {
              sendFileAction(root, "pending-delete", deletableFileIds(state, existingIds));
            }
            state.filePreviews[f.key] = uploadingPreviews;

            name.textContent = "";
            status.textContent = "";
            updateUploadUi();
            fileInput.value = "";
            updateHidden();

            const hasDependentFields = (schema.fields || []).some(
              (dependentField) => dependentField?.showAfterFileUpload === f.key
            );
            if (hasDependentFields && baseUrls.length === 0) rerender();
          } catch (e) {
            console.error(e);

            if (shouldReplace || !baseUrls.length) {
              setStoredUrls([]);
              setStoredIds([]);
              renderUploadCard(card, [], 0, false);
              clearFilePreviews(state, f.key);
            } else {
              setStoredUrls(baseUrls);
              setStoredIds(baseIds);
              state.filePreviews[f.key] = basePreviews;
              renderUploadCard(card, basePreviews, baseUrls.length, false);
            }

            name.textContent = baseUrls.length ? "" : "No file selected";
            status.textContent = "";
            updateUploadUi();
            fileInput.value = "";
            setError(root, e.message || "Upload failed");
            updateHidden();
          }
        });

        row.appendChild(btn);
        row.appendChild(removeBtn);
        row.appendChild(fileInput);
        wrap.appendChild(card);
        wrap.appendChild(row);

        if (shouldShowArtworkGuideLink(f)) {
          const guide = document.createElement("div");
          guide.className = "ipo-artwork-guide";

          const icon = document.createElement("span");
          icon.textContent = "📖 Not sure about bleed or safe zones? ";

          const link = document.createElement("a");
          link.href = "/pages/artwork-setup-guide";
          link.target = "_blank";
          link.rel = "noopener";
          link.textContent = "View our Artwork Setup Guide";

          guide.appendChild(icon);
          guide.appendChild(link);
          guide.appendChild(document.createTextNode(" before uploading."));

          wrap.appendChild(guide);

        }
      }

      if (f.help) {
        const helpLines = String(f.help)
          .split("\n")
          .filter((line) => {
            if (
              isAssemblyFieldDef(f) &&
              !isPrintsOnlyAvailableForSelectedVariant() &&
              isPrintsOnlyHelpLine(line)
            ) {
              return false;
            }
            return true;
          });

        if (helpLines.length) {
          const help = document.createElement("div");
          help.className = "ipo-help";
          // Keep option-set JSON as plain text.
          // Lines starting with ⚠ are highlighted in red automatically.
          helpLines.forEach((line, index) => {
            if (index > 0) {
              help.appendChild(document.createElement("br"));
            }

            const span = document.createElement("span");
            span.textContent = line;

            if (line.trim().startsWith("⚠")) {
              span.className = "ipo-help-warning";
            }

            help.appendChild(span);
          });

          wrap.appendChild(help);
        }
      }

      host.appendChild(wrap);

      if (isExpressProductionFieldDef(f) && isExpressProductionSelected(state)) {
        const dateField = renderExpressProductionDateField(root, state, () => {
          updateHidden();
          const existing = host.querySelector("[data-intaprint-edd-panel]");
          if (existing) existing.replaceWith(renderEstimatedDispatchPanel(root, state, variantOptionsPayload, variantId));
        });
        if (dateField) host.appendChild(dateField);
      }

      const shouldInsertAfterProduction = isExpressProductionFieldDef(f);
      const shouldInsertAfterBlankSample = isBlankVariant && isSampleOrderFieldDef(f);

      if (!eddInserted && (shouldInsertAfterProduction || shouldInsertAfterBlankSample)) {
        host.appendChild(renderEstimatedDispatchPanel(root, state, variantOptionsPayload, variantId));
        eddInserted = true;
      }
    });

    if (!eddInserted) {
      host.appendChild(renderEstimatedDispatchPanel(root, state, variantOptionsPayload, variantId));
    }

    const form = getProductForm();
    if (form) syncHiddenProps(form, schema, state, variantOptionsPayload, variantId);

    applyExpressProductionChargeToDiscountTable(state);
  }

  function clearFieldErrors(root) {
    $all(".ipo-field-error", root).forEach((el) => el.remove());
  }

  function showFieldError(root, fieldKey, message) {
    clearFieldErrors(root);
    setError(root, "");

    const fieldEl = root.querySelector(`[data-ipo-field-key="${CSS.escape(fieldKey)}"]`);
    if (!fieldEl) {
      setError(root, message);
      root.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const error = document.createElement("div");
    error.className = "ipo-field-error";
    error.textContent = `⚠ ${message}`;

    fieldEl.appendChild(error);
    fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function validateExpressProductionDateBeforeSubmit(root, state) {
    const earliest = await getEarliestExpressProductionDateAsync();
    const earliestIso = toIsoDate(earliest);
    let selectedDate = (state.values.express_production_date || "").toString().trim();

    if (!selectedDate || selectedDate < earliestIso) {
      state.values.express_production_date = earliestIso;
      const dateInput = root.querySelector('[data-ipo-field-key="express_production_date"] input[type="date"]');
      if (dateInput) {
        dateInput.min = earliestIso;
        dateInput.value = earliestIso;
      }
      showFieldError(
        root,
        "express_production_date",
        `Please choose an Express Production date from ${formatFriendlyDate(earliest)} onwards.`
      );
      return false;
    }

    const ok = await isWorkingDayIsoAsync(selectedDate);
    if (!ok) {
      const corrected = await getCorrectedWorkingDateAsync(selectedDate);
      state.values.express_production_date = toIsoDate(corrected);

      const dateInput = root.querySelector('[data-ipo-field-key="express_production_date"] input[type="date"]');
      if (dateInput) dateInput.value = state.values.express_production_date;

      showFieldError(
        root,
        "express_production_date",
        `That date is not available. I have moved it to ${formatFriendlyDate(corrected)}.`
      );
      return false;
    }

    return true;
  }

  function bindValidation(root, schema, state, variantOptionsPayload, variantId) {
    const form = getProductForm();
    if (!form) return;
    if (form.dataset.ipoValidateBound === "1") return;
    form.dataset.ipoValidateBound = "1";

    // Bind at document capture level so Express async validation runs before
    // Horizon/product-form submit handlers. Binding directly to the form can
    // allow an earlier theme listener to add once, followed by requestSubmit()
    // adding the same quantity a second time.
    document.addEventListener("submit", (e) => {
      if (e.target !== form) return;
      if (root.style.display === "none") return;

      clearFieldErrors(root);

      // Prevent a validated Express submission from being processed twice.
      // The first submit is paused for async date validation. The resubmitted
      // event is allowed once; any immediate duplicate submit event is blocked.
      if (
        isExpressProductionSelected(state) &&
        state.expressProductionSubmitValidated &&
        form.dataset.ipoExpressSubmitInFlight === "1"
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }

      if (
        isExpressProductionSelected(state) &&
        state.expressProductionSubmitValidated
      ) {
        form.dataset.ipoExpressSubmitInFlight = "1";

        setTimeout(() => {
          delete form.dataset.ipoExpressSubmitInFlight;
        }, 3000);
      }

      if (isExpressProductionSelected(state) && getWorkingDaysEndpoint() && !state.expressProductionSubmitValidated) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        validateExpressProductionDateBeforeSubmit(root, state).then((ok) => {
          if (!ok) return;

          state.expressProductionSubmitValidated = true;
          syncHiddenProps(form, schema, state, variantOptionsPayload, variantId);

          if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
          } else {
            form.submit();
          }

          setTimeout(() => {
            state.expressProductionSubmitValidated = false;
          }, 0);
        });

        return;
      }

      for (const f of schema.fields || []) {
        if (!shouldShowField(f, state, variantOptionsPayload, variantId)) continue;
        if (isExactRepeatMode(state) && !isExactRepeatEditableField(f)) continue;

        if (f.type === "file") {
          const reqCount = requiredFileCount(f, state);
          if (reqCount <= 0) continue;

          const actual = countFilesFromStored(state.values[f.key]);
          if (actual < reqCount) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            showFieldError(
              root,
              f.key,
              `Please upload ${reqCount} file${reqCount === 1 ? "" : "s"} for: ${displayText(f.label)}`
            );
            return;
          }
          continue;
        }

        if (!isFieldRequired(f, state)) continue;

        let v = (state.values[f.key] ?? "").toString().trim();

        if (!v) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const message = f.type === "select"
            ? `Please select a value for: ${displayText(f.label)}`
            : `${displayText(f.label)} is required.`;

          showFieldError(root, f.key, message);
          return;
        }

        if (isCustomPantoneSelection(f, state)) {
          const pantoneReference = String(
            state.values[customPantoneReferenceKey(f)] || ""
          ).trim();
          if (!pantoneReference) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            showFieldError(
              root,
              f.key,
              `Please enter the Pantone reference for: ${customPantoneSlotLabel(f)}`
            );
            return;
          }
        }
      }

      if (isExpressProductionSelected(state)) {
        const earliest = getEarliestExpressProductionDate();
        const earliestIso = toIsoDate(earliest);
        const selectedDate = (state.values.express_production_date || "").toString().trim();

        if (!selectedDate || selectedDate < earliestIso) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          showFieldError(
            root,
            "express_production_date",
            `Please choose an Express Production date from ${formatFriendlyDate(earliest)} onwards.`
          );
          return;
        }

        const selected = dateFromIsoDate(selectedDate);
        if (selected && !isWorkingDay(selected)) {
          const corrected = nextWorkingDay(selected);
          state.values.express_production_date = toIsoDate(corrected);
          const dateInput = root.querySelector('[data-ipo-field-key="express_production_date"] input[type="date"]');
          if (dateInput) dateInput.value = state.values.express_production_date;
        }
      }

      clearFieldErrors(root);
      setError(root, "");
      sendFileAction(root, "submitted", getAllStoredFileIds(state), true);
    }, true);
  }

  const SAVED_ARTWORK_WORKER_URL = "https://order-history.intaprint.workers.dev";

  async function loadSavedArtworkSelection() {
    const token = new URLSearchParams(window.location.search).get("saved_artwork");
    if (!token) return null;

    try {
      const response = await fetch(
        `${SAVED_ARTWORK_WORKER_URL}/saved-artwork/resolve?token=${encodeURIComponent(token)}`,
        { method: "GET", headers: { accept: "application/json" } }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Saved artwork could not be loaded.");
      }

      return payload.artwork || null;
    } catch (error) {
      console.error("Intaprint Options: saved artwork load failed", error);
      return { error: error?.message || "Saved artwork could not be loaded." };
    }
  }

  function savedArtworkPreview(url, name) {
    const cleanUrl = String(url || "").trim();
    let derivedName = "";

    try {
      const parsed = new URL(cleanUrl, window.location.origin);
      derivedName = decodeURIComponent(parsed.pathname.split("/").pop() || "");
    } catch (error) {
      derivedName = decodeURIComponent((cleanUrl.split("/").pop() || "").split("?")[0]);
    }

    const cleanName = String(name || "").trim() || derivedName || "Saved artwork";
    const extensionSource = `${cleanName} ${cleanUrl.split("?")[0]}`;
    const isPdf = /\.pdf(?:$|\s)/i.test(extensionSource);
    const isImage = /\.(?:png|jpe?g|gif|webp|svg)(?:$|\s)/i.test(extensionSource);

    const previewCandidates = [];
    if (isImage && cleanUrl) {
      previewCandidates.push(cleanUrl);

      // Some older Shopify Files URLs have stale version query strings. The same
      // public CDN path without that query often remains renderable.
      const withoutQuery = cleanUrl.split("?")[0];
      if (withoutQuery && withoutQuery !== cleanUrl) previewCandidates.push(withoutQuery);

      // Requesting a modest image width gives Shopify's image CDN another safe
      // rendering route while keeping the original file reference unchanged.
      try {
        const resized = new URL(cleanUrl, window.location.origin);
        resized.searchParams.set("width", "320");
        previewCandidates.push(resized.toString());
      } catch (error) {}
    }

    return {
      name: cleanName,
      sizeLabel: "Saved artwork",
      dimensions: "",
      previewUrl: previewCandidates[0] || "",
      previewCandidates: [...new Set(previewCandidates)],
      isPdf
    };
  }

  function normaliseSavedArtworkLabel(value) {
    const text = norm(value)
      .replace(/^[^a-z0-9]+/i, "")
      .replace(/[^a-z0-9]+$/i, "")
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ")
      .trim();

    if (
      text === "print" ||
      text === "printing" ||
      text.includes("how would you like this product printed") ||
      text.includes("how would you like your product printed") ||
      text.includes("print method")
    ) {
      return "print";
    }

    if (text === "artwork" || text.includes("will you be uploading artwork")) {
      return "artwork mode";
    }

    return text;
  }

  function savedArtworkFieldAliases(field) {
    const key = norm(field?.key);
    const aliases = new Set([
      normaliseSavedArtworkLabel(field?.label || ""),
      normaliseSavedArtworkLabel(key)
    ]);

    const add = (...values) => values.forEach((value) => aliases.add(normaliseSavedArtworkLabel(value)));

    if (["image_mode", "same_image"].includes(key)) {
      add("print", "printing", "print method", "how would you like this product printed");
    }
    if (key === "print_sides") {
      add("print", "printing", "print sides", "printed sides", "sides printed");
    }
    if (key === "artwork_mode") {
      add("artwork mode", "artwork", "upload artwork", "will you be uploading artwork");
    }
    if (key === "artwork" || key === "logo_upload") {
      add("artwork upload", "artwork upload file", "upload artwork file", "logo design upload", "logo / design upload");
    }
    if (key === "assembly") add("assembly", "assembled", "product assembly");
    if (key === "packaging") add("packaging", "individual packaging");
    if (key === "text_optional" || key === "text_info") {
      add("text", "text if any", "print text", "text info", "additional text");
    }
    if (key === "text_info_underside") {
      add("underside text", "text underside", "text info underside", "print text underside");
    }
    if (key === "preferences" || key === "instructions") {
      add("preferences", "instructions", "special instructions", "artwork instructions");
    }
    if (key === "font") add("font", "choose font", "font choice");
    if (key === "underside_colour") add("underside colour", "underside color");
    if (key === "endcap_colour") add("end-cap colour", "end cap colour", "endcap colour");
    if (key === "ribbon_colour") add("ribbon colour", "ribbon color");
    if (key === "top_background_method") add("top-side background", "top background", "top print background");
    if (key === "top_flood_colour") add("top flood", "top flood colour", "top background colour");
    if (key === "underside_background_method") add("underside background", "underside print background");
    if (key === "underside_flood_colour") add("underside flood", "underside flood colour", "underside background colour");
    if (key.startsWith("text_colour_top")) add(field?.label || "", "text colour top", "top text colour");
    if (key === "text_colour_underside") add("text colour underside", "underside text colour");
    if (key === "sequential_numbering_option") add("sequential numbering", "numbering");
    if (key === "sequential_numbering") add("sequential numbering details", "numbering details");

    aliases.delete("");
    return aliases;
  }

  function normaliseSavedArtworkChoice(value) {
    const text = norm(value)
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ")
      .trim();

    const aliases = new Map([
      ["same image both sides", "same design on both sides"],
      ["same image on both sides", "same design on both sides"],
      ["same artwork both sides", "same design on both sides"],
      ["same artwork on both sides", "same design on both sides"],
      ["different image each side", "different design on each side"],
      ["different images each side", "different design on each side"],
      ["different image on each side", "different design on each side"],
      ["different images on each side", "different design on each side"],
      ["different artwork each side", "different design on each side"],
      ["different artwork on each side", "different design on each side"],
      ["same image on all sides", "same design on all sides"],
      ["2 unique images (one repeated)", "2 unique designs (one design repeated)"],
      ["different image on each side", "different designs on each side"],
      ["top side and underside", "printed on both sides"],
      ["top side only", "top side only (most popular)"],
      ["assembled", "assemble and insert prints"],
      ["unassembled", "supply unassembled with printed inserts"],
      ["prints only", "prints only - no blank products supplied"],
      ["premium", "individually bagged"],
      ["standard", "standard - no individual packaging (scratching may occur)"]
    ]);

    return aliases.get(text) || text;
  }

  function savedArtworkHasAddedText(savedOptions) {
    return Array.from(savedOptions.entries()).some(([label, value]) => {
      const cleanLabel = normaliseSavedArtworkLabel(label);
      const cleanValue = norm(value);
      return (
        (cleanLabel.includes("text") || cleanLabel === "additional text") &&
        !cleanLabel.includes("text only") &&
        cleanValue &&
        !["no", "none", "not required", "n/a", "uploaded ✓"].includes(cleanValue)
      );
    });
  }

  function findSavedArtworkChoice(field, savedValue, savedOptions, schema = null) {
    const choices = getConfiguredChoices(field, null, schema);
    const savedChoice = normaliseSavedArtworkChoice(savedValue);

    const exact = choices.find((item) => {
      const value = String(item?.value ?? item?.label ?? item ?? "");
      const label = getChoiceLabel(field, item);
      return (
        normaliseSavedArtworkChoice(value) === savedChoice ||
        normaliseSavedArtworkChoice(label) === savedChoice
      );
    });
    if (exact != null) return exact;

    // One Insert / Top-or-Both-Sides option sets store the stable value
    // "Upload artwork", while the visible choices distinguish artwork-only from
    // artwork plus text. Select the correct live choice from the saved text fields.
    if (savedChoice === "upload artwork") {
      const preferred = savedArtworkHasAddedText(savedOptions)
        ? "upload artwork and add text"
        : "upload artwork only";

      return choices.find((item) => {
        const value = normaliseSavedArtworkChoice(String(item?.value ?? item?.label ?? item ?? ""));
        const label = normaliseSavedArtworkChoice(getChoiceLabel(field, item));
        return value === preferred || label === preferred;
      }) || null;
    }

    // A saved value may be the schema value while the current customer-facing
    // label is more descriptive. Compare significant words as a final safe fallback.
    const savedWords = new Set(savedChoice.split(/[^a-z0-9]+/).filter((word) => word.length > 2));
    const candidates = choices.filter((item) => {
      const texts = [
        String(item?.value ?? item?.label ?? item ?? ""),
        getChoiceLabel(field, item)
      ].map(normaliseSavedArtworkChoice);

      return texts.some((text) => {
        const words = text.split(/[^a-z0-9]+/).filter((word) => word.length > 2);
        const shared = words.filter((word) => savedWords.has(word));
        return shared.length >= Math.min(2, savedWords.size, words.length);
      });
    });

    return candidates.length === 1 ? candidates[0] : null;
  }

  function parseSavedArtworkOptions(summary) {
    const savedOptions = new Map();

    String(summary || "").split(/\s+·\s+/).forEach((entry) => {
      const separator = entry.indexOf(":");
      if (separator < 0) return;

      const rawLabel = entry.slice(0, separator);
      const label = normaliseSavedArtworkLabel(rawLabel);
      const value = entry.slice(separator + 1).trim();

      if (!label || !value || /^uploaded\s*✓?$/i.test(value)) return;
      savedOptions.set(label, value);
    });

    return savedOptions;
  }

  function findSavedValueForField(field, savedOptions) {
    const aliases = savedArtworkFieldAliases(field);

    for (const alias of aliases) {
      if (savedOptions.has(alias)) return savedOptions.get(alias);
    }

    // Never use fuzzy label matching for free-text fields. For example, a field
    // alias such as "print text" must not match the saved option label "Print"
    // and incorrectly insert its value ("Upload artwork") into the text box.
    if (["text", "textarea"].includes(field?.type)) {
      return "";
    }

    // Where a select/tile field label has been shortened on the order, match a
    // distinctive overlap only when it identifies one saved entry unambiguously.
    const fieldWords = new Set(
      Array.from(aliases)
        .flatMap((alias) => alias.split(/[^a-z0-9]+/))
        .filter((word) => word.length > 3)
    );

    const matches = Array.from(savedOptions.entries()).filter(([label]) => {
      const labelWords = label.split(/[^a-z0-9]+/).filter((word) => word.length > 3);
      return labelWords.some((word) => fieldWords.has(word));
    });

    return matches.length === 1 ? matches[0][1] : "";
  }

  function applySavedArtworkOptionSummary(savedArtwork, schema, state) {
    const summary = String(savedArtwork?.optionSummary || "").trim();
    if (!summary) return;

    const savedOptions = parseSavedArtworkOptions(summary);
    const excludedKeys = new Set([
      "sample_order",
      "express_production",
      "express_production_date"
    ]);

    (schema?.fields || []).forEach((field) => {
      if (!field?.key || excludedKeys.has(field.key) || field.type === "file") return;
      if (state.values[field.key]) return;

      const savedValue = findSavedValueForField(field, savedOptions);
      if (!savedValue) return;

      if (field.type === "select" || field.type === "tiles") {
        const choice = findSavedArtworkChoice(field, savedValue, savedOptions, schema);
        if (choice != null) {
          state.values[field.key] = String(choice?.value ?? choice?.label ?? choice);

          if (isCustomPantoneSelection(field, state)) {
            const slotLabel = customPantoneSlotLabel(field);
            const reference = savedOptions.get(normaliseSavedArtworkLabel(
              `${slotLabel} Pantone reference`
            ));
            const preview = savedOptions.get(normaliseSavedArtworkLabel(
              `${slotLabel} preview colour (approx.)`
            ));
            if (reference) {
              state.values[customPantoneReferenceKey(field)] = reference;
            }
            if (/^#[0-9a-f]{6}$/i.test(String(preview || "").trim())) {
              state.values[customPantonePreviewKey(field)] = String(preview).trim();
            }
          }
        }
        return;
      }

      if (["text", "textarea"].includes(field.type)) {
        state.values[field.key] = String(savedValue);
      }
    });
  }
  function applySavedArtworkToState(savedArtwork, schema, state) {
    if (!savedArtwork || savedArtwork.error || state.savedArtworkApplied) return false;

    // Restore the original printed configuration first. This is important because
    // the artwork field's file limit can depend on choices such as same/different sides.
    applySavedArtworkOptionSummary(savedArtwork, schema, state);

    const urls = (Array.isArray(savedArtwork.fileUrls) ? savedArtwork.fileUrls : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    const ids = (Array.isArray(savedArtwork.fileIds) ? savedArtwork.fileIds : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    const names = Array.isArray(savedArtwork.fileNames) ? savedArtwork.fileNames : [];

    if (!urls.length) return false;

    const fields = Array.isArray(schema?.fields) ? schema.fields : [];
    const artworkFields = fields.filter((field) => isArtworkFileField(field));
    const targetField = artworkFields[0] || fields.find((field) => field?.type === "file");
    if (!targetField?.key) return false;

    const maxFiles = getEffectiveMaxFiles(targetField, state);
    const selectedUrls = maxFiles ? urls.slice(0, maxFiles) : urls;
    const selectedIds = ids.slice(0, selectedUrls.length);

    state.values[targetField.key] = selectedUrls.length > 1 ? selectedUrls : selectedUrls[0];
    setStoredFileIds(state, targetField.key, selectedIds);
    state.filePreviews = state.filePreviews || {};
    state.filePreviews[targetField.key] = selectedUrls.map((url, index) =>
      savedArtworkPreview(url, names[index])
    );

    // Files from a completed previous order must never be sent to the cleanup endpoint.
    state.savedArtworkFileIds = new Set(selectedIds);
    state.savedArtworkApplied = true;
    state.savedArtworkName = String(savedArtwork.name || "Saved artwork").trim();
    state.savedArtworkReuseMode = savedArtwork.reuseMode === "exact_repeat"
      ? "exact_repeat"
      : "make_changes";
    state.savedArtworkHasOriginalArtwork = savedArtwork.hasOriginalArtwork === true;
    state.savedArtworkApprovedProofLabel = String(
      savedArtwork?.approvedProof?.label || ""
    ).trim();
    state.savedArtworkOriginalOrder = String(
      savedArtwork.orderName ||
      savedArtwork.originalOrderName ||
      savedArtwork?.approvedProof?.orderName ||
      ""
    ).trim();
    return true;
  }

  function deletableFileIds(state, ids) {
    const protectedIds = state?.savedArtworkFileIds instanceof Set
      ? state.savedArtworkFileIds
      : new Set();
    return (ids || []).filter((id) => !protectedIds.has(String(id || "").trim()));
  }

  function showSavedArtworkNotice(root, state) {
    if (!state?.savedArtworkApplied) return;
    let notice = root.querySelector("[data-ipo-saved-artwork-notice]");
    if (!notice) {
      notice = document.createElement("div");
      notice.className = "ipo-saved-artwork-notice";
      notice.setAttribute("data-ipo-saved-artwork-notice", "1");
      const fieldsHost = root.querySelector("[data-ipo-fields]");
      if (fieldsHost) root.insertBefore(notice, fieldsHost);
      else root.prepend(notice);
    }
    notice.textContent = state.savedArtworkReuseMode === "exact_repeat"
      ? `✓ Exact repeat selected: ${state.savedArtworkName || "Saved artwork"}. The approved PDF and previous product specification are locked.`
      : `✓ Original artwork selected for changes: ${state.savedArtworkName || "Saved artwork"}`;
  }

  function init(root) {
    ensureBaseStyles();

    const schemaMap = parseVariantMap(root);
    const variantOptionsPayload = parseVariantOptions(root);
    const state = { values: {}, fileIds: {} };
    const savedArtworkPromise = loadSavedArtworkSelection();
    let lastVid = "";

    async function apply(force = false) {
      const form = getProductForm();
      const vid = getSelectedVariantId();
      if (!vid) return;

      const schemaStr = schemaMap[vid] ? String(schemaMap[vid]) : "";
      if (!schemaStr.trim()) {
        const variantChanged = vid !== lastVid;
        if (variantChanged) {
          state.values = {};
          state.filePreviews = {};
          state.fileIds = {};
          state.savedArtworkApplied = false;
          state.savedArtworkName = "";
          state.savedArtworkReuseMode = "";
          state.savedArtworkApprovedProofLabel = "";
          state.savedArtworkOriginalOrder = "";
          state.savedArtworkFileIds = new Set();
          state.expressProductionDynamicMaxQty = null;
        }

        const host = $("[data-ipo-fields]", root);
        if (host) {
          host.innerHTML = "";
          host.appendChild(renderEstimatedDispatchPanel(root, state, variantOptionsPayload, vid));
        }

        const savedNotice = root.querySelector("[data-ipo-saved-artwork-notice]");
        if (savedNotice) savedNotice.remove();
        setVisible(root, true);
        applyExpressProductionChargeToDiscountTable(state);
        if (form) clearHiddenProps(form);
        lastVid = vid;
        return;
      }

      let schema;
      try { schema = JSON.parse(schemaStr.trim()); }
      catch (e) {
        console.error("Intaprint Options: invalid schema JSON for variant", vid, e, schemaStr);
        setError(root, "Options configuration error (invalid JSON).");
        setVisible(root, true);
        lastVid = vid;
        return;
      }

      const variantChanged = vid !== lastVid;

      if (variantChanged) {
        Object.keys(state.filePreviews || {}).forEach((key) => clearFilePreviews(state, key));
        state.values = {};
        state.filePreviews = {};
        state.fileIds = {};
        state.savedArtworkApplied = false;
        state.savedArtworkName = "";
        state.savedArtworkOriginalOrder = "";
        state.savedArtworkFileIds = new Set();
        state.expressProductionDynamicMaxQty = null;
      }

      const savedArtwork = await savedArtworkPromise;
      if (savedArtwork?.error) {
        setError(root, savedArtwork.error);
      } else {
        applySavedArtworkToState(savedArtwork, schema, state);
      }

      renderFields(root, schema, state, variantOptionsPayload, vid);
      showSavedArtworkNotice(root, state);
      bindValidation(root, schema, state, variantOptionsPayload, vid);

      setVisible(root, true);
      const form2 = getProductForm();
      if (form2) syncHiddenProps(form2, schema, state, variantOptionsPayload, vid);

      // Variant changed: never carry a Sample selection across variants.
      // Fire this AFTER the fields have re-rendered with the blank sample value,
      // so the quantity/MOQ script reads normal order mode and restores min/step text.
      if (variantChanged) {
        document.dispatchEvent(new CustomEvent("intaprint:sample-order-reset", {
          detail: { variantId: vid, isSample: false }
        }));

        if (window.IntaprintDiscountTableRefresh) {
          setTimeout(() => window.IntaprintDiscountTableRefresh({ reason: "ipo-variant-reset" }), 0);
        }
      }

      lastVid = vid;
    }

    setInterval(() => {
      const vid = getSelectedVariantId();
      if (vid && vid !== lastVid) apply();
    }, 200);

    apply();

    // Horizon morphs variant-dependent content after variant selection.
    // Re-apply once that content has updated so Packaging can read the latest
    // custom.individual_packaging_charge from the discount table data attribute.
    document.addEventListener("intaprint:variant-content-updated", () => {
      setTimeout(() => apply(true), 0);
    });

    // Backwards-compatible fallback for existing Intaprint variant events.
    document.addEventListener("variant:changed", () => {
      setTimeout(() => apply(true), 0);
    });

    window.addEventListener("popstate", () => setTimeout(() => apply(true), 0));
  }

  document.addEventListener("DOMContentLoaded", () => {
    $all("[data-intaprint-options]").forEach(init);
  });
})();